import React, { useState, useEffect, useRef, useMemo } from 'react';
import { KioskHeader, StepDots, formatPhone, formatPrice, KioskKeypad } from './KioskFlow';
import useIdleTimeout, { IdleWarningModal } from './useIdleTimeout';
import { trackAddToCart, trackBeginCheckout, trackPurchase, trackRebook } from './analytics';
import { setKioskContext, kev } from './kioskAnalytics';
import GuidedAddOnsFlow from './booking/GuidedAddOnsFlow';
import KioskShop from './KioskShop';
import { resolveRebookService, isWithinUpkeepWindow } from './lib/rebook-variants';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const FIRST_VISIT_DISCOUNT = 0.25;
const UPKEEP_DISCOUNT = 0.10;
// Campaign metadata used to label discounted line items + the dopamine banner.
// Zenoti's invoice response doesn't surface campaign names — we know them client-side
// because we control which code gets sent to /api/invoice/apply-coupon.
const CAMPAIGN_META = {
  WAXLOVE: { code: 'WAXLOVE', percent: 25 },
  UPKEEP:  { code: 'UPKEEP',  percent: 10 },
};
const TAX_RATE = 0.12;
const TIPS = [
  { label: '15%', value: 0.15 },
  { label: '18%', value: 0.18 },
  { label: '20%', value: 0.20 },
  { label: '25%', value: 0.25 },
];
const UPKEEP_MAX_WEEKS = 6; // Upkeep discount only within this many weeks
const DEFAULT_REBOOK_WEEKS = 5; // Default recommended rebook timeline

// Parse rebook timeline from service code (e.g., "2w" = 2 weeks, "10d" = 10 days, "8w" = 8 weeks)
function parseRebookTimeline(code) {
  if (!code) return null;
  const match = code.match(/(\d+)(w|d)/i);
  if (!match) return null;
  const num = parseInt(match[1]);
  const unit = match[2].toLowerCase();
  if (unit === 'w') return { weeks: num, label: num === 1 ? '1 Week' : `${num} Weeks` };
  if (unit === 'd') return { weeks: Math.round(num / 7 * 10) / 10, days: num, label: num === 1 ? '1 Day' : `${num} Days` };
  return null;
}

// Check if a service is eligible for upkeep discount
// Eligible: waxing, face waxing categories + brow shape & brow shape + tint from brow & lash
// Also checks service name as fallback since Zenoti appointment API may not include category
function isUpkeepEligible(svc) {
  const cat = (svc.categoryName || svc.category || '').toLowerCase();
  if (cat.includes('wax')) return true;
  const name = (svc.name || '').toLowerCase();
  if (name.includes('brow shape')) return true; // covers "brow shape" and "brow shape + tint"
  // Fallback: check service name for common wax services (categoryName may be empty from Zenoti API)
  const waxNames = ['brazilian', 'bikini', 'french', 'underarm', 'arm wax', 'leg wax', 'half leg',
    'full leg', 'back wax', 'chest wax', 'lip', 'chin', 'nose wax', 'face wax', 'brow wax',
    'stomach', 'hollywood', 'neck wax', 'sideburn'];
  if (waxNames.some(w => name.includes(w))) return true;
  return false;
}

// Pick the rebook-screen banner content from today's services + first-visit signal.
// Returns { state, category, subhead, discountText, badge }:
//   - state/category — stable strings used for analytics
//   - subhead         — short results line shown above the card
//   - discountText    — line under "{N} weeks" inside the card (null when no discount)
//   - badge           — the green deal pill label (null when no discount)
// UPKEEP_MAX_WEEKS is interpolated so the window in copy can't drift from logic.
// Brow detection: if ALL eligible services include "brow" in the name, use brow
// copy (sharper than the wax variant). Mixed brow+wax falls through to the wax
// variant since "smooth" reads broadly enough.
function pickRebookBanner({ services, isFirstVisit }) {
  const list = services || [];
  if (list.length === 0) {
    return {
      state: 'other',
      category: 'other',
      subhead: 'Consistency keeps your results looking their best.',
      discountText: null,
      badge: null,
    };
  }
  const eligible = list.filter(isUpkeepEligible);
  if (eligible.length > 0) {
    const isBrowOnly = eligible.every(s => (s.name || '').toLowerCase().includes('brow'));
    if (isFirstVisit) {
      return {
        state: isBrowOnly ? 'waxlove-brow' : 'waxlove-wax',
        category: isBrowOnly ? 'brow' : 'wax',
        subhead: isBrowOnly
          ? 'A tidy on schedule keeps your brows sharp and even.'
          : "The magic's in the routine — your second visit is even better.",
        discountText: `Rebook within ${UPKEEP_MAX_WEEKS} weeks and your next is 25% off.`,
        badge: 'WAXLOVE · 25% OFF',
      };
    }
    return {
      state: isBrowOnly ? 'upkeep-brow' : 'upkeep-wax',
      category: isBrowOnly ? 'brow' : 'wax',
      subhead: isBrowOnly
        ? 'A tidy on schedule keeps your brows sharp and even.'
        : 'Consistency is the secret to long-lasting, smooth skin.',
      discountText: `Rebook within ${UPKEEP_MAX_WEEKS} weeks and save 10%.`,
      badge: 'UPKEEP · 10% OFF',
    };
  }
  // Non-eligible: laser, lash lift, or other
  const anyLaser = list.some(s =>
    ((s.categoryName || s.category || '').toLowerCase().includes('laser')) ||
    ((s.name || '').toLowerCase().includes('laser'))
  );
  if (anyLaser) {
    return {
      state: 'laser',
      category: 'laser',
      subhead: 'Catching each growth cycle on time is what makes laser work.',
      discountText: null,
      badge: null,
    };
  }
  const anyLash = list.some(s => (s.name || '').toLowerCase().includes('lash'));
  if (anyLash) {
    return {
      state: 'lash',
      category: 'lash',
      subhead: 'Right on schedule keeps your lift looking its best.',
      discountText: null,
      badge: null,
    };
  }
  return {
    state: 'other',
    category: 'other',
    subhead: 'Consistency keeps your results looking their best.',
    discountText: null,
    badge: null,
  };
}

const INITIAL_DAYS_SHOWN = 4;
const INITIAL_TIMES_PER_DAY = 4;
const CHECKOUT_STEPS = ['invoice', 'rebook', 'tip', 'payment', 'rating'];
const TRAINING_CHECKOUT_STEPS = ['invoice', 'rating']; // Training: no rebook, tip, or payment
const MISSED_REBOOK_STEPS = ['rebook']; // Missed appointment: rebook only (no invoice/tip/payment)
const TRAINING_CATEGORY_NAME = 'free training'; // Matches EXCLUDED_CATEGORIES in server.js

// Idle timeout per step (seconds)
const CHECKOUT_TIMEOUTS = {
  phone: 10,
  name: 30,
  invoice: 90,
  recommendations: 25,
  rebook: 120,
  rebook_services: 120,
  rebook_catalog: 120,
  rebook_addons: 120,
  rebook_mandatory: 120,
  rebook_mandatory_select: 120,
  rebook_therapist: 90,
  rebook_time: 120,
  tip: 60,
  checkout_complete: 60,
  payment: 180,
  family_prompt: 60,
  rating: 30,
  receipt: 10,
  done: 15, // auto-reset (no warning)
  cr_hold: 0, // change-request hold — runs its own waitSeconds countdown, no idle timer
  cr_desk: 60, // "see the front desk" terminal screen — auto-reset like done
};

export default function KioskCheckOut({ onBack, centerId, initialRebook, employee, terminal }) {
  const isMissedRebook = !!initialRebook;
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [guest, setGuest] = useState(null);
  const [appointment, setAppointment] = useState(null);

  // Multi-account disambiguation
  const [multipleGuests, setMultipleGuests] = useState(false);
  const [nameInput, setNameInput] = useState('');

  // Full-screen loading overlay for page transitions
  const [pageLoading, setPageLoading] = useState(false);

  // First-visit detection
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  // Count of completed past visits (from history endpoint). Used by WAXLOVE auto-apply:
  // totalVisits === 1 means this is the customer's SECOND visit ever.
  const [totalVisits, setTotalVisits] = useState(null);

  // Training appointment detection (services priced at $50 in Zenoti but free for trainees)
  const [isTrainingAppointment, setIsTrainingAppointment] = useState(false);

  // Rebook — service catalog
  const [rebookWeek, setRebookWeek] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryServices, setCategoryServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]); // [{id, name, price, duration, provider, preSelected}]
  const [loadingServices, setLoadingServices] = useState(false);

  // Add-ons per service
  const [serviceAddOns, setServiceAddOns] = useState({}); // {serviceId: {optional: [], mandatory: []}}
  const [selectedOptionalAddOns, setSelectedOptionalAddOns] = useState([]); // [{id, name, price, duration, parentServiceId}]
  const [loadingAddOns, setLoadingAddOns] = useState(false);

  // Mandatory add-ons (removal question)
  const [mandatoryServices, setMandatoryServices] = useState([]); // services that have mandatory add-ons
  const [currentMandatoryIdx, setCurrentMandatoryIdx] = useState(0);
  const [needsRemoval, setNeedsRemoval] = useState({}); // {serviceId: true/false}
  const [selectedMandatoryAddOns, setSelectedMandatoryAddOns] = useState([]); // [{id, name, price, duration, parentServiceId, isMandatory}]

  // Therapist + time
  const [rebookTherapists, setRebookTherapists] = useState([]);
  const [rebookSelectedTherapist, setRebookSelectedTherapist] = useState(null);
  const [rebookDate, setRebookDate] = useState(null);
  const [rebookTime, setRebookTime] = useState(null);
  const [rebookConfirmed, setRebookConfirmed] = useState(false);
  const [rebookDiscountApplied, setRebookDiscountApplied] = useState(false);
  const [rebookShowMoreDays, setRebookShowMoreDays] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [rebookSlots, setRebookSlots] = useState([]);
  const [sameTimeSlot, setSameTimeSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingTherapists, setLoadingTherapists] = useState(false);
  const [centerTimeMap, setCenterTimeMap] = useState({}); // maps "date|time" → Zenoti centerTime

  // Invoice from Zenoti (real prices with discounts/packages applied)
  const [invoiceId, setInvoiceId] = useState(null);
  const [invoiceBalance, setInvoiceBalance] = useState(null); // null = not fetched, 0 = fully covered by benefits
  const [invoiceData, setInvoiceData] = useState(null); // full Zenoti invoice {items, subtotal, discount, tax, total}

  // Manual discount code entry on invoice screen
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponMessage, setCouponMessage] = useState(null); // { type: 'success'|'error', text }
  const [showCouponInput, setShowCouponInput] = useState(false);
  // Auto-apply campaign tracking. Resets per-invoice via useEffect keyed on invoiceId.
  // appliedCampaign holds whatever code WE applied this session (so we can label discounted
  // lines client-side — Zenoti's invoice response doesn't include campaign names).
  const [autoApplyAttemptedFor, setAutoApplyAttemptedFor] = useState(null); // stores the invoiceId we tried
  const [appliedCampaign, setAppliedCampaign] = useState(null); // { code, percent }
  const [justApplied, setJustApplied] = useState(null);          // { code, savings } — drives dopamine banner
  // Services this guest has had within the UPKEEP window (6 weeks), from history endpoint.
  // Used to gate the UPKEEP pill: a service today only qualifies if the same service is in this list.
  const [recentServices, setRecentServices] = useState([]);

  // Featured-on-kiosk products carousel (replaces the old Shop Products card on the invoice screen)
  // null = not yet loaded (render skeleton); [] = loaded but empty (hide row); array = render cards
  const [featuredProducts, setFeaturedProducts] = useState(null);
  const [addingProductId, setAddingProductId] = useState(null);
  const [productError, setProductError] = useState(null); // { productId, text }
  // Optimistic quantity overlay: { [productId]: quantity }. Bumped immediately on tap before the
  // API roundtrip lands; cleared on success (invoice refetch picks up the real line items) or on
  // failure (rollback to whatever invoiceData.items says).
  const [optimisticQty, setOptimisticQty] = useState({});

  // Tip + Payment + Rating
  const [tipSelected, setTipSelected] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [showCustomTip, setShowCustomTip] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  // Family checkout — post-payment "pay for her too?" (spec 2026-08-07).
  // familyGuest = the linked guest currently being paid (null = primary payer);
  // familyCandidatesRef: null = not fetched, false = in flight, [] / [cands] = result.
  const [familyGuest, setFamilyGuest] = useState(null);
  const familyCandidatesRef = useRef(null);
  // hostedPaymentUri not used — terminal is triggered by API call directly
  const [guestGiftCards, setGuestGiftCards] = useState([]);
  const [giftCardApplied, setGiftCardApplied] = useState(null); // { number, amount }
  const [giftCardError, setGiftCardError] = useState(null);
  const [remainingAfterGiftCard, setRemainingAfterGiftCard] = useState(null);

  // Manual gift card entry (physical cards / codes not on the guest's account).
  // Reuses applyGiftCard once we've looked up the balance via /api/kiosk/giftcard-balance.
  const [manualGcOpen, setManualGcOpen] = useState(false);
  const [manualGcCode, setManualGcCode] = useState('');
  const [manualGcLookup, setManualGcLookup] = useState(false); // true while balance lookup is in flight
  const [manualGcError, setManualGcError] = useState(null);

  // Payment method picker.
  //   null       → "How would you like to pay?" choice screen
  //   'giftcard' → gift card sub-screen (on-file cards + manual entry)
  // Note: 'card' is no longer a value — tapping "Debit / Credit" on the picker calls
  // startPayment() directly (kicks off the Helcim terminal transaction), bypassing the
  // intermediate confirm screen that used to live here. Resetting to null returns the
  // guest to the picker (e.g. on Back from the gift card sub-screen).
  const [paymentMethodChoice, setPaymentMethodChoice] = useState(null);

  // Comment field on the combined paid+rating screen. The textarea is always visible
  // alongside the stars (per the redesign brief), but the in-app alpha keypad only
  // appears once the guest taps the note — tapping a star must NOT pop the keyboard.
  const [commentFocused, setCommentFocused] = useState(false);

  // Web Audio chime that plays when the combined paid+rating screen appears. Lazy
  // AudioContext creation; if the browser blocks (no prior user gesture), play()
  // silently fails. By the time the kiosk reaches checkout the guest has tapped many
  // times — gesture priming is implicit.
  const audioCtxRef = useRef(null);
  const chimePlayedRef = useRef(false);

  // Card on file
  const [savedCards, setSavedCards] = useState([]);
  const [cardOnFileInput, setCardOnFileInput] = useState('');
  const [cardOnFileError, setCardOnFileError] = useState('');
  const [cardOnFileProcessing, setCardOnFileProcessing] = useState(false);

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [hasLeftGoogleReview, setHasLeftGoogleReview] = useState(false);

  // Receipt
  const [receiptSent, setReceiptSent] = useState(false);
  const [receiptMethod, setReceiptMethod] = useState(null); // 'email' or 'sms'

  // Shop overlay (products/packages browsing from invoice screen)
  const [shopMode, setShopMode] = useState(null); // null | 'products' | 'packages'
  const [shopProducts, setShopProducts] = useState([]);
  const [shopProductsLoading, setShopProductsLoading] = useState(false);

  // Shop screen (between invoice and rebook) — tabs: For You, Packages, Services
  const [recommendations, setRecommendations] = useState(null); // { techName, techId, packages }
  const [shopTab, setShopTab] = useState('foryou'); // foryou, packages, services
  const [shopCart, setShopCart] = useState([]); // [{ id, name, price, type:'package'|'bridge-package' }] — 'bridge-package' = sold via the manual-bridge charge (max one)
  const [shopPackages, setShopPackages] = useState([]);
  const [shopServiceCat, setShopServiceCat] = useState(null); // selected category in services tab
  const [shopServices, setShopServices] = useState([]); // services in selected category
  const [shopRebookServices, setShopRebookServices] = useState([]); // services added for rebook
  const [shopPurchasing, setShopPurchasing] = useState(false);
  const [shopPurchased, setShopPurchased] = useState(false);
  const [shopNextStep, setShopNextStep] = useState('rebook');
  // The kiosk CANNOT sell series packages directly (Zenoti reserves that for
  // guest-authenticated webstore purchases), so instead we text the guest a link
  // to the real webstore package page and they buy it on their own phone.
  // Keyed by package id → 'sending' | 'sent' | 'error' for per-card inline feedback.
  const [packageLinkStatus, setPackageLinkStatus] = useState({});
  // Kiosk package bridge (manual Zenoti entry, MH pilot) — center-gated flag.
  // Any fetch error leaves it false, which silently keeps the legacy
  // text-me-the-link behavior. bridgeCharge holds the SERVER's charge response
  // (authoritative total + coverage) once startPayment initiates a bridge sale —
  // post-charge display math must use it over local estimates, because on
  // covered days the server charges package+tip only (service invoice stays
  // open for the manual entry).
  const [bridgeEnabled, setBridgeEnabled] = useState(false);
  const [bridgeCharge, setBridgeCharge] = useState(null); // { totalAmount, coveredToday, packageName, packagePrice }
  useEffect(() => {
    if (!centerId) { setBridgeEnabled(false); return; }
    let stale = false;
    fetch(`${API_URL}/api/kiosk/package-bridge-config?centerId=${encodeURIComponent(centerId)}`)
      .then(r => r.json())
      .then(d => { if (!stale) setBridgeEnabled(d?.enabled === true); })
      .catch(() => { if (!stale) setBridgeEnabled(false); });
    return () => { stale = true; };
  }, [centerId]);

  // Load shop data + check recommendations after invoice
  async function checkRecommendations(nextStepIfNone) {
    if (!centerId) { setStep(nextStepIfNone); return; }
    setShopNextStep(nextStepIfNone);
    setPageLoading(true);
    try {
      const fetches = [fetch(`${API_URL}/api/packages/${centerId}`), loadFullCatalog()];
      if (guest?.guestId) {
        fetches.push(fetch(`${API_URL}/api/staff/recommendations/${guest.guestId}?centerId=${centerId}`));
      }
      const [pkgRes, , recRes] = await Promise.all(fetches);
      const pkgData = await pkgRes.json();
      setShopPackages(pkgData.packages || []);
      let hasRecs = false;
      if (recRes) {
        const recData = await recRes.json();
        if (recData.found && recData.packages?.length > 0) {
          setRecommendations(recData);
          setShopTab('foryou');
          hasRecs = true;
        }
      }
      if (hasRecs) {
        setStep('recommendations');
      } else {
        setStep(nextStepIfNone);
      }
    } catch (e) {
      console.error('Shop load error:', e);
      setStep(nextStepIfNone);
    } finally {
      setPageLoading(false);
    }
  }

  function addToCart(item) {
    // Products from the Browse All overlay route through addProductToVisit (PUT to service invoice).
    // Packages keep the legacy shopCart + shopCheckout flow (separate invoice per package).
    if (item.type === 'product') {
      addProductToVisit({
        id: item.id, name: item.name, price: item.price, image: item.image || null,
      });
      return;
    }
    // Bridge-enabled centers sell series packages through the standalone charge
    // endpoint (one package per checkout, v1) — mark it 'bridge-package' so
    // startPayment routes it to the bridge and the legacy create-invoice flow
    // never sees it. Adding another REPLACES the existing one (no stacking).
    if (item.type === 'package' && bridgeEnabled) {
      setShopCart(prev => [...prev.filter(c => c.type !== 'bridge-package'), { ...item, type: 'bridge-package' }]);
      kev('shop', 'product_added', { name: item.name, price: item.price, type: 'bridge-package' });
      return;
    }
    setShopCart(prev => prev.some(c => c.id === item.id) ? prev : [...prev, item]);
    kev('shop', 'product_added', { name: item.name, price: item.price, type: item.type || 'product' });
  }
  function removeFromCart(itemId) {
    setShopCart(prev => prev.filter(c => c.id !== itemId));
  }
  // Receptionless package sale: text the guest a link to the real Zenoti webstore
  // package page so they complete the purchase on their own phone (the kiosk can't
  // sell series packages directly). Per-card status drives the inline confirmation.
  async function textPackageLink(pkg) {
    const guestPhone = phone || guest?.phone;
    if (!guestPhone) { setPackageLinkStatus(prev => ({ ...prev, [pkg.id]: 'error' })); return; }
    if (packageLinkStatus[pkg.id] === 'sending' || packageLinkStatus[pkg.id] === 'sent') return;
    setPackageLinkStatus(prev => ({ ...prev, [pkg.id]: 'sending' }));
    try {
      const res = await fetch(`${API_URL}/api/kiosk/text-package-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: guestPhone, centerId, packageName: pkg.name }),
      });
      const data = await res.json();
      if (data.sent) {
        setPackageLinkStatus(prev => ({ ...prev, [pkg.id]: 'sent' }));
        kev('shop', 'package_link_texted', { name: pkg.name });
      } else {
        setPackageLinkStatus(prev => ({ ...prev, [pkg.id]: 'error' }));
      }
    } catch (e) {
      console.error('Text package link failed:', e);
      setPackageLinkStatus(prev => ({ ...prev, [pkg.id]: 'error' }));
    }
  }
  function addServiceToRebook(svc) {
    if (shopRebookServices.some(s => s.id === svc.id)) return;
    setShopRebookServices(prev => [...prev, svc]);
  }
  function removeServiceFromRebook(svcId) {
    setShopRebookServices(prev => prev.filter(s => s.id !== svcId));
  }

  // Open shop overlay from invoice screen
  async function openShop(mode) {
    setShopMode(mode);
    if (mode === 'products' && shopProducts.length === 0) {
      setShopProductsLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/products/${centerId}`);
        const data = await res.json();
        setShopProducts(data.products || []);
      } catch (e) {
        console.error('Failed to load products:', e);
      } finally {
        setShopProductsLoading(false);
      }
    }
    if (mode === 'packages' && shopPackages.length === 0) {
      try {
        const res = await fetch(`${API_URL}/api/packages/${centerId}`);
        const data = await res.json();
        setShopPackages(data.packages || []);
      } catch (e) {
        console.error('Failed to load packages:', e);
      }
    }
  }

  // Load services for a category in the shop tab
  function loadShopServices(categoryId) {
    setShopServiceCat(categoryId);
    const catalog = catalogRef.current;
    if (!catalog) return;
    const raw = catalog.services[categoryId] || [];
    const svcs = raw
      .filter(s => !s.add_ons_info?.is_add_on && s.duration > 0 && s.catalog_info?.can_book !== false)
      .map(s => ({ id: s.id, name: s.display_name || s.name, price: s.price_info?.sale_price || 0, duration: s.duration || 0 }));
    setShopServices(svcs);
  }

  // Checkout cart — create invoice(s) + terminal payment
  async function shopCheckout() {
    if (shopCart.length === 0) return;
    setShopPurchasing(true);
    try {
      // Create package invoice (supports multiple packages)
      // Skip entirely when the packages were bundled into the main terminal
      // tap (paid + recorded by the webhook already).
      const packageIds = bundledPackagesRef.current ? [] : shopCart.filter(c => c.type === 'package').map(c => c.id);
      if (packageIds.length > 0) {
        // Create one invoice per package (Zenoti limitation)
        for (const pkgId of packageIds) {
          const invRes = await fetch(`${API_URL}/api/packages/create-invoice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ guestId: guest.guestId, packageId: pkgId, centerId })
          });
          const invData = await invRes.json();
          if (!invData.invoiceId) throw new Error('Could not create invoice');

          // Trigger terminal payment
          const payRes = await fetch(`${API_URL}/api/kiosk/trigger-terminal-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invoiceId: invData.invoiceId, centerId, tipAmount: 0,
              guestId: guest.guestId, employeeToken: employee?.token,
              employeeId: employee?.id, terminalId: terminal?.id
            })
          });
          const payData = await payRes.json();
          if (payData.error) throw new Error(payData.error);

          // Poll for payment
          await new Promise((resolve, reject) => {
            let attempts = 0;
            const poll = setInterval(async () => {
              attempts++;
              try {
                const balRes = await fetch(`${API_URL}/api/kiosk/invoice/${invData.invoiceId}?centerId=${centerId}`);
                const balData = await balRes.json();
                if (balData.balanceDue <= 0) { clearInterval(poll); resolve(); }
                else if (attempts >= 40) { clearInterval(poll); reject(new Error('Payment timeout')); }
              } catch { if (attempts >= 40) { clearInterval(poll); reject(new Error('Poll error')); } }
            }, 3000);
          });
        }
      }

      // Create product invoices
      const productItems = shopCart.filter(c => c.type === 'product');
      if (productItems.length > 0) {
        for (const prod of productItems) {
          const invRes = await fetch(`${API_URL}/api/products/create-invoice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ guestId: guest.guestId, productId: prod.id, quantity: 1, centerId })
          });
          const invData = await invRes.json();
          if (!invData.invoiceId) throw new Error('Could not create product invoice');

          // Trigger terminal payment
          const payRes = await fetch(`${API_URL}/api/kiosk/trigger-terminal-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invoiceId: invData.invoiceId, centerId, tipAmount: 0,
              guestId: guest.guestId, employeeToken: employee?.token,
              employeeId: employee?.id, terminalId: terminal?.id
            })
          });
          const payData = await payRes.json();
          if (payData.error) throw new Error(payData.error);

          // Poll for payment
          await new Promise((resolve, reject) => {
            let attempts = 0;
            const poll = setInterval(async () => {
              attempts++;
              try {
                const balRes = await fetch(`${API_URL}/api/kiosk/invoice/${invData.invoiceId}?centerId=${centerId}`);
                const balData = await balRes.json();
                if (balData.balanceDue <= 0) { clearInterval(poll); resolve(); }
                else if (attempts >= 40) { clearInterval(poll); reject(new Error('Payment timeout')); }
              } catch { if (attempts >= 40) { clearInterval(poll); reject(new Error('Poll error')); } }
            }, 3000);
          });
        }
      }

      setShopPurchased(true);
    } catch (err) {
      console.error('Cart checkout error:', err);
      setShopPurchasing(false);
    }
  }

  // Proceed from shop — apply rebook services and move on
  function proceedFromShop() {
    // Pre-select services for rebook
    if (shopRebookServices.length > 0) {
      setSelectedServices(prev => {
        const existing = prev.map(s => s.id);
        const newSvcs = shopRebookServices.filter(s => !existing.includes(s.id))
          .map(s => ({ ...s, selected: true, preSelected: false, provider: null }));
        return [...prev, ...newSvcs];
      });
    }
    setStep(shopNextStep);
  }

  // Pleasant two-note chime synthesized via Web Audio. Fires when the combined
  // paid+rating screen appears. Wrapped in try/catch — audio is enhancement, never
  // a flow blocker; if anything throws (no AudioContext, autoplay policy, suspended
  // context) we swallow silently.
  function playChime() {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});

      const now = ctx.currentTime;
      // Ascending fourth: G5 → C6. Soft sine, short envelope.
      const notes = [
        { freq: 783.99, start: 0,    duration: 0.18 },
        { freq: 1046.5, start: 0.16, duration: 0.50 },
      ];
      for (const { freq, start, duration } of notes) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = 'sine';
        osc.connect(gain).connect(ctx.destination);
        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(0.22, now + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
        osc.start(now + start);
        osc.stop(now + start + duration + 0.05);
      }
    } catch (_) { /* audio blocked or unsupported — silent */ }
  }

  // Trigger the chime once when entering the combined paid+rating screen.
  // ref-guarded so it never replays on re-renders or comment edits.
  useEffect(() => {
    if (step === 'rating' && !chimePlayedRef.current) {
      chimePlayedRef.current = true;
      playChime();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Fire `rebook_viewed` exactly once per kiosk session so we can compute
  // saved / skipped / back conversion rates against the impressions count. Only
  // for the normal flow (missed-rebook is its own separate funnel). Ref-guarded
  // against re-renders that happen mid-rebook (week toggles etc.).
  const rebookViewedRef = useRef(false);
  useEffect(() => {
    if (step === 'rebook' && !isMissedRebook && !rebookViewedRef.current && appointment) {
      rebookViewedRef.current = true;
      const banner = pickRebookBanner({
        services: appointment?.services || [],
        isFirstVisit,
      });
      kev('rebook', 'rebook_viewed', {
        bannerState: banner.state,
        category: banner.category,
        recommendedWeeks: getRecommendedRebook().weeks,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isMissedRebook, appointment, isFirstVisit]);

  // Idle timeout — resets to home after inactivity
  // Phone + done screens auto-reset silently (no warning needed)
  // Pause idle timer while pageLoading (API in progress — user is waiting, not idle)
  const { showWarning, countdown, dismissWarning } = useIdleTimeout({
    timeoutSeconds: (pageLoading || paymentProcessing) ? 0 : (CHECKOUT_TIMEOUTS[step] || 0),
    onReset: () => { kev(step, 'idle_timeout', { screen: step }); onBack(); },
    autoReset: step === 'phone' || step === 'name' || step === 'done' || step === 'cr_desk',
  });

  const subtotal = isTrainingAppointment ? 0 : (appointment ? appointment.services.reduce((s, x) => s + x.price, 0) : 0);
  const tax = isTrainingAppointment ? 0 : subtotal * TAX_RATE;
  // Tip base = full PRE-DISCOUNT price of SERVICES only (no retail, no package purchases).
  // Try appointment.catalogPrice first (Zenoti catalog sale_price). Fall back to invoice items'
  // pre-discount price — backend stores (sales + discount) on each item (server.js:10644), which
  // survives 100%-off discounts and catalog-cache misses. If both sources are 0 we have no
  // reliable original price; the tip UI must hide percentages in that case.
  const tipBaseFromAppointment = appointment
    ? appointment.services.reduce((s, x) => s + (x.catalogPrice || x.price || 0), 0)
    : 0;
  const tipBaseFromInvoice = (invoiceData?.items || [])
    .filter(it => it.type === 'service')
    .reduce((s, it) => s + (it.price || 0), 0);
  const tipBase = isTrainingAppointment ? 0 : (tipBaseFromAppointment || tipBaseFromInvoice || 0);
  const discount = isFirstVisit ? FIRST_VISIT_DISCOUNT : UPKEEP_DISCOUNT;
  const discountCode = isFirstVisit ? 'WAXLOVE' : 'UPKEEP';
  // Robust "discount already applied" check — replaces the brittle `invoiceData.discount > 0`,
  // which only fires when Zenoti rolls line-item discounts up to invoice.total.discount (not
  // guaranteed on all invoice shapes). ORing per-item discounts catches the cases where the
  // rollup is missing.
  const anyDiscountApplied =
    (invoiceData?.discount || 0) > 0 ||
    ((invoiceData?.items || []).some(it => (it.discount || 0) > 0));

  // Auto-apply eligible campaign discount once per invoice. WAXLOVE > UPKEEP precedence.
  // The autoApplyAttemptedFor flag is keyed on invoiceId so re-checking in for a different
  // visit retries fresh; same invoice never retries even if state mutates.
  useEffect(() => {
    if (step !== 'invoice') return;
    if (!invoiceId || !invoiceData) return;
    if (autoApplyAttemptedFor === invoiceId) return;
    if (isTrainingAppointment) return;
    if (invoiceBalance === 0) return;            // membership / package zeroed the balance
    if (anyDiscountApplied) return;              // codes don't stack; respect existing discount
    setAutoApplyAttemptedFor(invoiceId);          // mark before await so concurrent renders don't double-fire

    let code = null;
    if (totalVisits === 1) {
      code = 'WAXLOVE';
    } else if (someUpkeepEligibleNow()) {
      code = 'UPKEEP';
    }
    if (!code) return;

    // Fire and forget — failures log server-side, no customer-facing error
    applyCouponInternal(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, invoiceId, invoiceData, invoiceBalance, totalVisits, autoApplyAttemptedFor]);
  const providerName = appointment?.provider?.name || 'your therapist';
  // Unique provider names across today's services — for tip screen heading & split explainer
  const tipProviderNames = useMemo(() => {
    const names = (appointment?.services || [])
      .map(s => s.provider)
      .filter(n => n && n !== 'Provider');
    return [...new Set(names)];
  }, [appointment]);
  const tipProviderHeading =
    tipProviderNames.length === 0 ? providerName :
    tipProviderNames.length === 1 ? tipProviderNames[0] :
    tipProviderNames.length === 2 ? `${tipProviderNames[0]} & ${tipProviderNames[1]}` :
    `${tipProviderNames.slice(0, -1).join(', ')} & ${tipProviderNames[tipProviderNames.length - 1]}`;

  // Determine active step list based on flow type
  const activeSteps = isMissedRebook ? MISSED_REBOOK_STEPS :
                      isTrainingAppointment ? TRAINING_CHECKOUT_STEPS :
                      CHECKOUT_STEPS;

  // Demo mode — bypass the phone + name screens entirely by mock-injecting a guest
  // + appointment. Two entry points:
  //   ?demo=rebook                       → land straight on the redesigned rebook screen
  //   ?demo=1  (or =true, =checkout)     → land at step='invoice' to walk the full tail
  //                                        (invoice → tip → payment → paid+rating → receipt → done)
  // Real flow is untouched — the multipleMatches "What's your first name?" disambiguation
  // (KioskCheckOut.jsx:2344+) still runs against the real backend for real guests. Demo
  // just never calls the backend so it can't trigger the prompt. Personalized strings
  // ("You're all set, Demo!", "Vlada recommends 5 weeks") render off the mock data.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demoParam = params.get('demo');
    const isRebookDemo = demoParam === 'rebook';
    const isFullDemo = demoParam === '1' || demoParam === 'true' || demoParam === 'checkout';
    if (isRebookDemo || isFullDemo) {
      if (params.get('first') === 'true') setIsFirstVisit(true);
      setGuest({ guestId: 'demo', firstName: 'Demo', lastName: 'Guest', phone: '0000000000', email: 'demo@example.com' });
      setAppointment({
        id: 'demo-appt',
        appointmentGroupId: 'demo-appt',
        time: '10:30 AM',
        provider: { id: '2aaebaac-5c48-406f-bfa0-92e1bb0d53bb', name: 'Vlada', image: null },
        services: [
          { id: 'svc1', name: 'Brazilian', price: 65, duration: 25, provider: 'Vlada', categoryName: 'BODY WAXING', categoryId: 'cat1' },
          { id: 'svc2', name: 'Underarms', price: 20, duration: 10, provider: 'Vlada', categoryName: 'BODY WAXING', categoryId: 'cat1' },
        ]
      });
      setSelectedServices([
        { id: 'svc1', name: 'Brazilian', price: 65, duration: 25, provider: 'Vlada', categoryName: 'BODY WAXING', selected: true, preSelected: true },
        { id: 'svc2', name: 'Underarms', price: 20, duration: 10, provider: 'Vlada', categoryName: 'BODY WAXING', selected: true, preSelected: true },
      ]);
    }
    if (isRebookDemo) {
      setStep('rebook');
    } else if (isFullDemo) {
      // Mock invoice so the invoice screen has items + totals to render.
      setInvoiceId('demo-invoice');
      setInvoiceBalance(85);
      setInvoiceData({
        invoiceId: 'demo-invoice',
        items: [
          { id: 'svc1', invoiceItemId: 'item1', name: 'Brazilian', type: 'service', price: 65, quantity: 1, therapist_name: 'Vlada', duration: 25, discount: 0 },
          { id: 'svc2', invoiceItemId: 'item2', name: 'Underarms', type: 'service', price: 20, quantity: 1, therapist_name: 'Vlada', duration: 10, discount: 0 },
        ],
        subtotal: 85,
        tax: 0,
        total: 85,
        balanceDue: 85,
        discount: 0,
      });
      setRecentServices([]);   // no past visits → no UPKEEP auto-apply on today's invoice
      setTotalVisits(2);        // returning guest profile (not first-visit auto-apply)
      setStep('invoice');
    }
    // Demo: test Google review opportunity with a real guest
    // Usage: ?demo=review&phone=6041234567&centerId=xxx
    if (params.get('demo') === 'review') {
      const testPhone = params.get('phone');
      const testCenter = params.get('centerId') || centerId;
      if (testPhone) {
        (async () => {
          try {
            const res = await fetch(`${API_URL}/api/kiosk/guest-lookup?phone=${testPhone}&centerId=${testCenter}`);
            const data = await res.json();
            if (data.found) {
              setGuest(data.guest);
              setAppointment({
                id: 'demo-appt', time: '10:30 AM',
                provider: { id: 'demo-provider', name: 'Vlada' },
                services: [{ id: 'svc1', name: 'Brazilian', price: 65, duration: 25, provider: 'Vlada' }]
              });
              setStep('rating');
            }
          } catch (e) { console.log('Demo review lookup failed:', e); }
        })();
      }
    }
    if (params.get('demo') === 'payment') {
      setGuest({ guestId: 'demo', firstName: 'Demo', lastName: 'Guest', phone: '0000000000' });
      setAppointment({
        id: 'demo-appt',
        time: '10:30 AM',
        provider: { id: 'demo-provider', name: 'Vlada' },
        services: [
          { id: 'svc1', name: 'Brazilian', price: 65, duration: 25, provider: 'Vlada' },
          { id: 'svc2', name: 'Underarms', price: 20, duration: 10, provider: 'Vlada' },
        ]
      });
      setInvoiceBalance(85);
      setInvoiceId('demo-invoice');
      setTipSelected(0.20);
      setGuestGiftCards([
        { id: 'gc1', number: '****1234', balance: 100, originalValue: 100, saleDate: '2025-12-25', occasion: 'Birthday', notes: 'Happy Birthday!', giftedBy: null, giftedByName: null },
        { id: 'gc2', number: '****5678', balance: 25, originalValue: 50, saleDate: '2026-01-15', occasion: null, notes: 'For your next wax!', giftedBy: 'demo-other', giftedByName: 'Sarah M.' },
      ]);
      setStep('payment');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Missed appointment / walk-in another day rebook: pre-fill and skip week picker
  useEffect(() => {
    if (!initialRebook) return;
    setGuest(initialRebook.guest);
    if (initialRebook.appointment) {
      setAppointment(initialRebook.appointment);
      setSelectedServices(
        (initialRebook.appointment?.services || []).map(s => ({ ...s, selected: true, preSelected: true }))
      );
    } else if (initialRebook.services) {
      setSelectedServices(initialRebook.services);
    }
    // Skip week picker — go straight to service selection, starting from this week
    setRebookWeek('0');
    setStep('rebook_services');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getTipAmount() {
    // customTip is a digits-only string of CENTS (driven by KioskKeypad). "525" → $5.25.
    // Storing as cents avoids the decimal-point key on the in-app keypad and matches the
    // Square/Stripe POS convention guests are used to.
    if (tipSelected === 'custom') return (parseInt(customTip || '0', 10) || 0) / 100;
    if (typeof tipSelected === 'number') return tipBase * tipSelected;
    return 0;
  }

  // ============================================================
  // MARK APPOINTMENTS COMPLETED (extracted for group support)
  // ============================================================
  async function markAppointmentsCompleted(appointmentIds) {
    // Use logged-in employee ID, fall back to appointment's therapist ID
    const updatedById = employee?.id || appointment?.provider?.id;
    console.log('Completing appointments:', appointmentIds, 'updatedById:', updatedById);
    const res = await fetch(`${API_URL}/api/kiosk/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appointmentIds, employeeId: updatedById })
    });
    const data = await res.json();
    console.log('Mark completed result:', JSON.stringify(data));
    if (!data.success) {
      throw new Error(data.results?.map(r => r.warning).filter(Boolean).join(', ') || 'Could not complete appointment');
    }
    return data;
  }

  // Close invoice in Zenoti (non-blocking — will succeed if balance is $0 after benefits)
  async function closeInvoice(appointmentId, invId) {
    try {
      const res = await fetch(`${API_URL}/api/kiosk/close-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId,
          invoiceId: invId || invoiceId,
          therapistId: appointment?.provider?.id || employee?.id
        })
      });
      const data = await res.json();
      console.log('Close invoice result:', data);
    } catch (e) { console.log('Close invoice error (non-blocking):', e); }
  }

  // Fetch real Zenoti invoice to get balance and line items (after benefits/discounts are applied)
  async function fetchInvoice(appointmentId) {
    try {
      const idRes = await fetch(`${API_URL}/api/kiosk/invoice-by-appointment/${appointmentId}`);
      const idData = await idRes.json();
      if (!idData.invoiceId) return;
      setInvoiceId(idData.invoiceId);

      const invRes = await fetch(`${API_URL}/api/kiosk/invoice/${idData.invoiceId}`);
      const invData = await invRes.json();
      setInvoiceBalance(invData.balanceDue ?? invData.total ?? null);
      setInvoiceData(invData);
      console.log(`[KIOSK] Invoice ${idData.invoiceId}: balance=$${invData.balanceDue}, discount=$${invData.discount}, subtotal=$${invData.subtotal}, total=$${invData.total}`, invData);
    } catch (e) {
      console.log('Fetch invoice error (non-blocking):', e);
    }
  }

  // ============================================================
  // CHANGE-REQUEST KIOSK HOLD (Task 14)
  // Before the guest enters the checkout tail we ask the backend whether the
  // desk still has an OPEN change request for this appointment. hold:true →
  // friendly pause screen + 5 s DB-only polls; the desk card escalates to
  // "GUEST AT KIOSK". If it resolves, checkout continues on the FRESH invoice.
  // If waitSeconds run out, the session ends with "see the front desk" — the
  // kiosk NEVER proceeds on the old invoice after a timeout (spec amendment).
  // Every fetch failure fails OPEN (continue checkout) — a network blip must
  // never strand a guest; the request stays on the desk panel regardless.
  // Feature is dark unless the backend says hold (server-side flag).
  // ============================================================
  const crHoldTimersRef = useRef({ poll: null, countdown: null });
  const crHoldActiveRef = useRef(false); // guards against a late poll response racing the countdown

  function clearCrHoldTimers() {
    crHoldActiveRef.current = false;
    if (crHoldTimersRef.current.poll) { clearInterval(crHoldTimersRef.current.poll); crHoldTimersRef.current.poll = null; }
    if (crHoldTimersRef.current.countdown) { clearTimeout(crHoldTimersRef.current.countdown); crHoldTimersRef.current.countdown = null; }
  }
  useEffect(() => () => clearCrHoldTimers(), []); // no interval leaks on unmount

  // Returns true → continue checkout now; false → hold screen took over.
  async function changeRequestGate(appt) {
    try {
      const res = await fetch(`${API_URL}/api/kiosk/change-request-hold?zenotiAppointmentId=${encodeURIComponent(appt.id)}`);
      const data = await res.json();
      // hold:false covers flag-off, no open request, AND resolved:true (the
      // verification read confirmed the invoice already has the new lines).
      // On resolved:true the invoice snapshot from checkout-init is seconds
      // older than that verification read — an accepted seconds-wide window,
      // not a guaranteed-fresh total.
      if (!data.hold) return true;
      beginChangeRequestHold(appt, (Number.isFinite(data.waitSeconds) && data.waitSeconds > 0) ? data.waitSeconds : 90);
      return false;
    } catch {
      return true; // fail-open — never strand a guest on a network blip
    }
  }

  function beginChangeRequestHold(appt, waitSeconds) {
    clearCrHoldTimers();
    crHoldActiveRef.current = true;
    setStep('cr_hold');
    const url = `${API_URL}/api/kiosk/change-request-hold?zenotiAppointmentId=${encodeURIComponent(appt.id)}`;

    const release = async () => {
      if (!crHoldActiveRef.current) return;
      clearCrHoldTimers();
      // The desk keyed the change DURING the hold — the invoice fetched at
      // checkout-init is stale. Refresh so the guest reviews/pays the NEW total.
      await fetchInvoice(appt.id);
      setStep('invoice');
    };

    crHoldTimersRef.current.countdown = setTimeout(() => {
      if (!crHoldActiveRef.current) return;
      clearCrHoldTimers();
      // Annotate the desk card (fire-and-forget, DB-only server-side) and end
      // the session at the desk — never proceed on the old invoice.
      fetch(`${url}&timeout=1`).catch(() => {});
      kev('cr_hold', 'change_request_hold_timeout');
      setStep('cr_desk');
    }, waitSeconds * 1000);

    crHoldTimersRef.current.poll = setInterval(async () => {
      try {
        const res = await fetch(`${url}&poll=1`);
        const data = await res.json();
        if (!crHoldActiveRef.current) return; // countdown fired while this poll was in flight
        if (!data.hold) release();
      } catch {
        release(); // fail-open on a blip mid-hold
      }
    }, 5000);
  }

  // Remove a product from the invoice
  const [removingItemId, setRemovingItemId] = useState(null);
  async function removeInvoiceItem(itemId) {
    if (!invoiceId || !itemId || removingItemId) return;
    setRemovingItemId(itemId);
    try {
      const res = await fetch(`${API_URL}/api/kiosk/invoice/${invoiceId}/item/${itemId}`, { method: 'DELETE' });
      if (res.ok) {
        // Re-fetch invoice to get updated totals
        const invRes = await fetch(`${API_URL}/api/kiosk/invoice/${invoiceId}`);
        const invData = await invRes.json();
        setInvoiceBalance(invData.balanceDue ?? invData.total ?? null);
        setInvoiceData(invData);
      }
    } catch (e) {
      console.log('Remove invoice item error:', e);
    }
    setRemovingItemId(null);
  }

  // Sibling to hasUpkeepEligibleServices() — that one reads the REBOOK cart, this reads the CURRENT visit.
  // UPKEEP applies to a service only if the same service was completed within the 6-week window.
  // recentServices comes from the backend (DB-backed) and contains {id, name, lastVisit}.
  function someUpkeepEligibleNow() {
    const services = appointment?.services || [];
    if (services.length === 0 || recentServices.length === 0) return false;
    const recentIds = new Set(recentServices.map(r => r.id).filter(Boolean));
    const recentNames = new Set(recentServices.map(r => (r.name || '').toLowerCase().trim()).filter(Boolean));
    return services.some(s => {
      if (!isUpkeepEligible(s)) return false;
      if (s.id && recentIds.has(s.id)) return true;
      const name = (s.name || '').toLowerCase().trim();
      return name && recentNames.has(name);
    });
  }

  // Apply a coupon code (manual entry path). Auto-apply uses the same underlying endpoint
  // via a separate wrapper so the two flows don't share UI state (loading spinner, error text)
  // that's specific to the manual input.
  async function applyCoupon(rawCode) {
    const code = (rawCode || '').trim().toUpperCase();
    if (!code || couponLoading) return;

    // Client-side guard for typed UPKEEP on a non-eligible visit — short-circuits before the backend call
    if (code === 'UPKEEP' && !someUpkeepEligibleNow()) {
      setCouponMessage({ type: 'error', text: 'Not eligible for UPKEEP this visit.' });
      return;
    }

    setCouponLoading(true);
    setCouponMessage(null);
    try {
      const result = await applyCouponInternal(code);
      if (result.success) {
        setCouponMessage({ type: 'success', text: 'Discount applied!' });
        setCouponInput('');
        setShowCouponInput(false);
        setTimeout(() => setCouponMessage(null), 5000);
      } else if (result.networkError) {
        setCouponMessage({ type: 'error', text: "Couldn't apply — try again." });
      } else {
        setCouponMessage({ type: 'error', text: "We're sorry, the promotional code you entered is not valid." });
      }
    } finally {
      setCouponLoading(false);
    }
  }

  // Shared apply-coupon impl used by both manual entry and auto-apply. Returns
  // { success, savings, networkError } so callers can decide how to surface results.
  // The backend response carries the FULL invoice payload (items + balanceDue + totals),
  // so we don't need a separate GET /api/kiosk/invoice/:id — saves ~900ms on this path.
  async function applyCouponInternal(code) {
    const codeMeta = CAMPAIGN_META[code] || { code, percent: null };
    try {
      const res = await fetch(`${API_URL}/api/invoice/apply-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId, couponCode: code, centerId }),
      });
      const data = await res.json();
      if (!data.success) {
        console.log(`[discount] ${code} rejected by backend:`, data.error);
        return { success: false };
      }
      // Apply-coupon response now includes items + balanceDue + total — use it directly
      setInvoiceBalance(data.balanceDue ?? data.total ?? null);
      setInvoiceData({
        items: data.items || [],
        transactions: data.transactions || [],
        subtotal: data.subtotal || 0,
        discount: data.discount || 0,
        tax: data.tax || 0,
        total: data.total || 0,
        totalPaid: data.totalPaid || 0,
        balanceDue: data.balanceDue || 0,
      });
      const savings = data.discount || 0;
      if (savings > 0) {
        setAppliedCampaign(codeMeta);
        setJustApplied({ code, savings });
        setTimeout(() => setJustApplied(null), 4500);
      } else {
        console.log(`[discount] ${code} applied with $0 savings — banner suppressed`);
      }
      return { success: true, savings };
    } catch (e) {
      console.log(`[discount] ${code} network error:`, e.message);
      return { success: false, networkError: true };
    }
  }

  // Add a featured product to the visit's invoice via PUT /v1/invoices/{id}/products (apikey auth).
  // Optimistic UX: bump the displayed quantity immediately on tap, run the API in the background,
  // refetch on success, rollback the optimistic bump on failure.
  // Dedup: PUT is additive (no native quantity merge), so for second+ taps of the same product we
  // DELETE the prior line then PUT at quantity+1. Customer sees a single line going 1→2→3.
  async function addProductToVisit(product) {
    if (!product || !invoiceId || addingProductId) return;
    setAddingProductId(product.id);
    setProductError(null);

    // Existing line for this product on the invoice (if any) — frontend dedup key
    const existing = (invoiceData?.items || []).find(it =>
      it.type === 'product' && (it.id === product.id || it.name === product.name)
    );
    const currentQty = (existing?.quantity || 0) + (optimisticQty[product.id] || 0);
    const newQty = currentQty + 1;

    // Optimistic bump — render reads (existing.quantity + optimisticQty[id]) as the displayed qty
    setOptimisticQty(prev => ({ ...prev, [product.id]: (prev[product.id] || 0) + 1 }));

    try {
      // If there's an existing line, DELETE it first so the post-PUT state shows a single qty:N line.
      if (existing) {
        const existingItemId = existing.invoiceItemId || existing.id;
        await fetch(`${API_URL}/api/kiosk/invoice/${invoiceId}/item/${existingItemId}`, { method: 'DELETE' });
      }
      const res = await fetch(`${API_URL}/api/kiosk/invoice/${invoiceId}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: product.id, quantity: newQty, centerId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not add');

      // Refetch invoice → real line item replaces the optimistic overlay
      const invRes = await fetch(`${API_URL}/api/kiosk/invoice/${invoiceId}`);
      const invData = await invRes.json();
      setInvoiceBalance(invData.balanceDue ?? invData.total ?? null);
      setInvoiceData(invData);
      setOptimisticQty(prev => { const n = { ...prev }; delete n[product.id]; return n; });
      kev('invoice', 'product_added_carousel', { productId: product.id, quantity: newQty });
    } catch (e) {
      console.log('Add product error:', e.message);
      // Rollback the optimistic bump
      setOptimisticQty(prev => { const n = { ...prev }; delete n[product.id]; return n; });
      setProductError({ productId: product.id, text: "Couldn't add — try again." });
      // Also refetch in case the DELETE succeeded but the PUT failed — invoice state is mid-flight
      try {
        const invRes = await fetch(`${API_URL}/api/kiosk/invoice/${invoiceId}`);
        const invData = await invRes.json();
        setInvoiceData(invData);
        setInvoiceBalance(invData.balanceDue ?? invData.total ?? null);
      } catch { /* leave stale state */ }
    } finally {
      setAddingProductId(null);
    }
  }

  // ============================================================
  // GUEST LOOKUP
  // ============================================================
  async function lookupGuest(overrideFirstName) {
    setLoading(true); setPageLoading(true); setError(null);
    try {
      // Single combined request: guest lookup + appointments + invoice + wallet/cards/history/review
      const params = new URLSearchParams({ phone, centerId });
      if (overrideFirstName) params.set('firstName', overrideFirstName);
      const res = await fetch(`${API_URL}/api/kiosk/checkout-init?${params}`);
      const data = await res.json();

      if (data.multipleMatches && !overrideFirstName) {
        kev('phone', 'guest_multiple_matches');
        setMultipleGuests(true);
        setStep('name');
        return;
      }
      if (!data.found) { kev('phone', 'guest_not_found'); throw new Error(data.error || 'Guest not found'); }
      setGuest(data.guest);
      setKioskContext({ guestId: data.guest?.guestId });
      kev('phone', 'guest_found');

      if (data.allCheckedOut) {
        throw new Error('You\'re all checked out! Have a great day 💕');
      }
      if (!data.openAppointment) {
        throw new Error(data.error || 'No appointment found for today.');
      }

      const appt = data.openAppointment;
      setAppointment(appt);

      // Detect training appointments by category name
      const isTraining = appt.services?.some(s =>
        s.categoryName?.toLowerCase().includes(TRAINING_CATEGORY_NAME)
      ) || false;
      setIsTrainingAppointment(isTraining);

      // Pre-populate selected services from today's visit
      setSelectedServices(appt.services.map(s => ({ ...s, selected: true, preSelected: true })));

      // Invoice data
      if (data.invoice) {
        setInvoiceId(data.invoice.invoiceId);
        setInvoiceBalance(data.invoice.balanceDue ?? data.invoice.total ?? null);
        setInvoiceData(data.invoice);
      } else {
        await fetchInvoice(appt.id);
      }

      // Guest data from combined response
      if (data.history) {
        setIsFirstVisit(data.history.isFirstVisit);
        setRecentServices(data.history.recentServices || []);
        if (typeof data.history.totalVisits === 'number') setTotalVisits(data.history.totalVisits);
      }
      if (data.wallet) setGuestGiftCards(data.wallet.giftCards || []);
      if (data.reviewStatus?.hasReviewed) setHasLeftGoogleReview(true);

      // Change-request hold gate (Task 14): the "about to pay" seam — pause
      // here if the desk still has an open change request for this visit.
      // true → continue to the invoice screen; false → hold screen took over
      // (it advances to 'invoice' itself, or ends the session at the desk).
      const proceed = await changeRequestGate(appt);
      if (proceed) setStep('invoice');
      // Preload catalog in background while user reviews invoice (speeds up rebook)
      loadFullCatalog();
      // Fetch featured-on-kiosk products for the invoice-screen carousel.
      // Read sessionStorage first for instant render on repeat checkouts in the same kiosk
      // session, then refetch in background to update the cache. TTL = session (staff can refresh
      // the kiosk if they just changed featured products in ops).
      const cacheKey = `kiosk:featured:${centerId}`;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) setFeaturedProducts(parsed);
        }
      } catch { /* sessionStorage unavailable or bad JSON — fall through to network */ }
      fetch(`${API_URL}/api/products/featured?centerId=${centerId}`)
        .then(r => r.json())
        .then(d => {
          if (d.success) {
            const products = d.products || [];
            setFeaturedProducts(products);
            try { sessionStorage.setItem(cacheKey, JSON.stringify(products)); } catch { /* ignore */ }
          }
        })
        .catch(e => console.log('Featured products fetch error (non-blocking):', e.message));
    } catch (err) { setError(err.message || 'Could not find your visit. Please ask the front desk for help.'); }
    finally { setLoading(false); setPageLoading(false); }
  }

  // ============================================================
  // LOAD FULL CATALOG FROM CACHE (one request, zero live API calls)
  // ============================================================
  const [fullCatalog, setFullCatalog] = useState(null);
  const catalogRef = useRef(null); // Ref to avoid stale state reads
  const catalogPromiseRef = useRef(null); // Prevent duplicate fetches

  async function loadFullCatalog() {
    // Return cached data if already loaded
    if (catalogRef.current) return catalogRef.current;
    // If a fetch is already in flight, wait for it
    if (catalogPromiseRef.current) return catalogPromiseRef.current;

    setLoadingServices(true);
    const promise = (async () => {
      try {
        const res = await fetch(`${API_URL}/api/kiosk/centers/${centerId}/full-catalog`);
        const data = await res.json();
        catalogRef.current = data;
        setFullCatalog(data);
        setCategories(data.categories || []);
        const todayCategoryId = appointment?.services?.[0]?.categoryId;
        if (todayCategoryId) setSelectedCategory(todayCategoryId);
        return data;
      } catch (err) {
        console.error('Failed to load catalog', err);
        return null;
      } finally {
        setLoadingServices(false);
        catalogPromiseRef.current = null;
      }
    })();
    catalogPromiseRef.current = promise;
    return promise;
  }

  // Keep old function names for compatibility
  async function loadCategories() {
    await loadFullCatalog();
  }

  async function loadServicesForCategory(categoryId) {
    const catalog = catalogRef.current || await loadFullCatalog();
    if (!catalog) return;
    const raw = catalog.services[categoryId] || [];
    // Convert to simple format, filter bookable
    const services = raw
      .filter(s => {
        if (s.add_ons_info?.is_add_on) return false;
        if (s.duration === 0 && (s.price_info?.sale_price || 0) === 0) return false;
        if (s.catalog_info?.show_in_catalog === false) return false;
        if (s.catalog_info?.can_book === false) return false;
        return true;
      })
      .map(s => ({
        id: s.id,
        name: s.display_name || s.name,
        price: s.price_info?.sale_price || 0,
        duration: s.duration || 0,
        subCategory: s.sub_category_name || ''
      }));
    setCategoryServices(services);
  }

  useEffect(() => {
    if (selectedCategory) loadServicesForCategory(selectedCategory);
  }, [selectedCategory, fullCatalog]);

  // ============================================================
  // LOAD ADD-ONS FOR SELECTED SERVICES (from cache)
  // ============================================================
  async function loadAddOnsForServices() {
    const servicesToCheck = selectedServices.filter(s => s.selected);
    if (servicesToCheck.length === 0) return { addOns: {}, mandatory: [] };
    setLoadingAddOns(true);

    try {
      const catalog = catalogRef.current || await loadFullCatalog();
      const filterInternal = (list) => (list || []).filter(a => !/^Z\s/i.test(a.name || ''));
      const newAddOns = {};
      for (const svc of servicesToCheck) {
        const resolvedId = resolveService(svc, selectedDayIso).id;
        const raw = catalog?.addOns?.[resolvedId] || catalog?.addOns?.[svc.id] || { optional: [], mandatory: [] };
        newAddOns[svc.id] = { optional: filterInternal(raw.optional), mandatory: filterInternal(raw.mandatory) };
      }
      setServiceAddOns(newAddOns);

      const withMandatory = servicesToCheck.filter(svc => (newAddOns[svc.id]?.mandatory || []).length > 0);
      setMandatoryServices(withMandatory);
      return { addOns: newAddOns, mandatory: withMandatory };
    } catch (err) { console.error('Failed to load add-ons', err); return { addOns: {}, mandatory: [] }; }
    finally { setLoadingAddOns(false); }
  }

  // ============================================================
  // LOAD THERAPISTS (from cache)
  // ============================================================
  async function loadTherapists(catalogOverride) {
    const catalog = catalogOverride || catalogRef.current || await loadFullCatalog();
    if (!catalog) return;

    // Filter therapists to only those who can perform the selected services
    const selectedSvcs = getSelectedServicesList();
    let filteredTherapists = null;

    if (selectedSvcs.length > 0) {
      try {
        // Fetch therapists for each selected service and intersect
        const therapistSets = await Promise.all(
          selectedSvcs.map(async svc => {
            const res = await fetch(`${API_URL}/api/kiosk/centers/${centerId}/therapists?serviceId=${svc.id}`);
            const data = await res.json();
            return new Set((data.therapists || []).map(t => t.Id));
          })
        );
        // Intersect: only therapists who can do ALL selected services
        const intersection = therapistSets.reduce((acc, set) =>
          new Set([...acc].filter(id => set.has(id)))
        );
        filteredTherapists = intersection;
      } catch (e) {
        console.log('Per-service therapist fetch failed, showing all:', e);
      }
    }

    const allTherapists = catalog.allTherapists || [];
    const therapists = filteredTherapists
      ? allTherapists.filter(t => filteredTherapists.has(t.Id))
      : allTherapists;

    // Compute total price from selected services + add-ons (same for all therapists);
    // price each service at its date-resolved variant so this matches the rebook screen
    const selectedSvcsList = getSelectedServicesList();
    const servicesTotal = selectedSvcsList.reduce((sum, s) => sum + (resolveService(s, selectedDayIso).price || 0), 0);
    const addOnsTotal = [...selectedOptionalAddOns, ...selectedMandatoryAddOns].reduce((sum, a) => sum + (a.price || 0), 0);
    const totalPrice = servicesTotal + addOnsTotal || null;

    const hasImage = (t) => typeof t.ImagePaths === 'object' && t.ImagePaths !== null && Object.values(t.ImagePaths).some(v => v);
    setRebookTherapists(therapists.map(t => ({
      id: t.Id,
      name: t.DisplayName,
      image: hasImage(t) ? `${API_URL}/api/therapist-image/${t.Id}` : null,
      description: t.Description || '',
      jobName: t.JobName || '',
      price: totalPrice,
    })));

    // Pre-select their current therapist
    if (appointment?.provider) {
      setRebookSelectedTherapist({ id: appointment.provider.id, name: appointment.provider.name });
    }
  }

  // ============================================================
  // LOAD TIME SLOTS
  // ============================================================
  function getBookingServices(therapist, forDateIso) {
    // Resolve each cart service to the correct Brazilian variant for the date
    // being booked/queried, then dedupe: if the guest added e.g. "Brazilian
    // Upkeep" from the catalog while their seeded "Brazilian" also resolves to
    // it, book it once.
    const dateIso = forDateIso || selectedDayIso;
    const seen = new Set();
    const services = [];
    // Original cart id → booked id, so add-ons picked for "Brazilian" stay
    // attached when it books as "Brazilian Upkeep" (the backend matches
    // add-ons by parentServiceId === a booked primary's serviceId and
    // silently drops orphans). Dedupe-dropped duplicates map to the
    // surviving id for the same reason.
    const bookedIdByOriginal = {};
    for (const s of getSelectedServicesList()) {
      const r = resolveService(s, dateIso);
      bookedIdByOriginal[s.id] = r.id;
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      services.push({
        serviceId: r.id, serviceName: r.name, duration: r.duration,
        therapistId: therapist?.id !== 'any' ? therapist?.id : null
      });
    }
    const remapParent = (id) => bookedIdByOriginal[id] || id;
    return [
      ...services,
      ...selectedOptionalAddOns.map(a => ({
        serviceId: a.id, serviceName: a.name, duration: a.duration,
        isAddOn: true, parentServiceId: remapParent(a.parentServiceId)
      })),
      ...selectedMandatoryAddOns.map(a => ({
        serviceId: a.id, serviceName: a.name, duration: a.duration,
        isAddOn: true, parentServiceId: remapParent(a.parentServiceId), isMandatory: true
      }))
    ];
  }

  function formatSlotTime(zenotiTime) {
    // Handle both "2026-03-26T10:00:00" (ISO) and "2026-03-26 10:00:00" (space) formats
    const timePart = zenotiTime.includes('T')
      ? zenotiTime.split('T')[1]
      : zenotiTime.split(' ')[1];
    const [h, m] = timePart.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  }

  function buildCenterTime(isoDate, displayTime) {
    const match = displayTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return `${isoDate} 00:00:00`;
    let h = parseInt(match[1]);
    const m = match[2];
    const ampm = match[3].toUpperCase();
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return `${isoDate} ${h.toString().padStart(2, '0')}:${m}:00`;
  }

  // Upkeep cutoff: 6 weeks from today (any day on or before this is upkeep-eligible)
  const upkeepCutoffDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + UPKEEP_MAX_WEEKS * 7);
    d.setHours(23, 59, 59);
    return d;
  })();

  // Brazilian variant swap: flag + per-center variant IDs. null = feature
  // off / endpoint unreachable → resolveService is a no-op and the kiosk
  // books the exact original IDs (pre-feature behavior).
  const [variantSwap, setVariantSwap] = useState(null);
  useEffect(() => {
    if (!centerId) return;
    fetch(`${API_URL}/api/kiosk/rebook-variants?centerId=${centerId}`)
      .then(r => r.json())
      .then(d => { if (d?.enabled && Array.isArray(d.families) && d.families.length > 0) setVariantSwap(d); })
      .catch(() => {}); // fail-safe: no swap
  }, [centerId]);

  // Window check for a concrete day; before any day is picked, fall back to
  // the chosen week (rebookWeek), matching how the discount copy reasons.
  function isWithinUpkeepForDate(isoDate) {
    if (isoDate) return isWithinUpkeepWindow(isoDate, new Date(), UPKEEP_MAX_WEEKS);
    if (rebookWeek) return parseInt(rebookWeek) <= UPKEEP_MAX_WEEKS;
    return true;
  }

  // The service to display/price/book for a cart entry, given the date context.
  function resolveService(svc, isoDate) {
    return resolveRebookService(svc, isWithinUpkeepForDate(isoDate), variantSwap?.families || []);
  }

  // Generate 7 days centered on the target day (3 before, target, 3 after)
  function getWeekDays(weekOffset) {
    const center = new Date();
    center.setDate(center.getDate() + weekOffset * 7);
    const days = [];
    for (let i = -3; i <= 3; i++) {
      const d = new Date(center);
      d.setDate(d.getDate() + i);
      days.push({
        iso: d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'),
        dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: d.getDate(),
        month: d.toLocaleDateString('en-US', { month: 'short' }),
        label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        dateObj: d,
        isUpkeep: d <= upkeepCutoffDate,
        isCenter: i === 0,
      });
    }
    return days;
  }

  // Week offset state for the day-scroller (set when user picks initial week)
  const [weekOffset, setWeekOffset] = useState(null);
  const [selectedDayIso, setSelectedDayIso] = useState(null);
  const [daySlots, setDaySlots] = useState({}); // { 'YYYY-MM-DD': { times: [...], loaded: bool } }

  const weekDays = weekOffset !== null ? getWeekDays(weekOffset) : [];
  const weekLabel = weekDays.length > 0
    ? `${weekDays[0].month} ${weekDays[0].dayNum} – ${weekDays[weekDays.length - 1].month} ${weekDays[weekDays.length - 1].dayNum}`
    : '';
  const selectedDayData = selectedDayIso ? daySlots[selectedDayIso] : null;

  // Load time slots for a single day
  async function loadDaySlots(isoDate, therapist) {
    const isDemo = new URLSearchParams(window.location.search).get('demo') === 'rebook';
    if (isDemo) {
      const mockTimes = ['10:00 AM','10:30 AM','11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM','2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM','5:00 PM'];
      const dayIdx = new Date(isoDate + 'T12:00:00').getDay();
      const dayTimes = mockTimes.filter((_, j) => (j + dayIdx) % 3 !== 0);
      const timeMap = {};
      const dayLabel = new Date(isoDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      dayTimes.forEach(t => { timeMap[`${dayLabel}|${t}`] = `${isoDate}T${t.replace(/ /g, '')}`; });
      setCenterTimeMap(prev => ({ ...prev, ...timeMap }));
      setDaySlots(prev => ({ ...prev, [isoDate]: { times: dayTimes, loaded: true } }));
      return;
    }

    const services = getBookingServices(therapist, isoDate);
    try {
      const res = await fetch(`${API_URL}/api/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ centerId, date: isoDate, services, checkFutureDays: false })
      });
      const data = await res.json();
      const openSlots = data.openSlots || [];
      const dayLabel = new Date(isoDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const timeMap = {};
      const times = openSlots.map(slot => {
        const displayTime = formatSlotTime(slot.Time);
        timeMap[`${dayLabel}|${displayTime}`] = slot.Time;
        return displayTime;
      });
      setCenterTimeMap(prev => ({ ...prev, ...timeMap }));
      setDaySlots(prev => ({ ...prev, [isoDate]: { times, loaded: true } }));
    } catch (err) {
      console.error('Failed to load slots for', isoDate, err);
      setDaySlots(prev => ({ ...prev, [isoDate]: { times: [], loaded: true } }));
    }
  }

  // Load slots for a full week (all 6 days in parallel)
  async function loadWeekSlots(wOffset, therapist) {
    setLoadingSlots(true);
    const days = getWeekDays(wOffset);
    await Promise.all(days.map(d =>
      daySlots[d.iso]?.loaded ? Promise.resolve() : loadDaySlots(d.iso, therapist)
    ));
    setLoadingSlots(false);
  }

  // Select a day tab — load its slots if not cached
  async function selectDay(isoDate) {
    setSelectedDayIso(isoDate);
    setRebookDate(null); setRebookTime(null);
    if (!daySlots[isoDate]?.loaded) {
      setLoadingSlots(true);
      await loadDaySlots(isoDate, rebookSelectedTherapist);
      setLoadingSlots(false);
    }
  }

  // Smooth swipe to navigate one day at a time on touch screens
  const swipeStartX = useRef(null);
  const swipeDragX = useRef(0);
  const dayTabsRef = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeTransition, setSwipeTransition] = useState(false);

  function handleSwipeStart(e) {
    swipeStartX.current = e.touches[0].clientX;
    swipeDragX.current = 0;
    setSwipeTransition(false);
    setSwipeOffset(0);
  }
  function handleSwipeMove(e) {
    if (swipeStartX.current === null) return;
    const diff = swipeStartX.current - e.touches[0].clientX;
    swipeDragX.current = diff;
    // Dampen the drag (move at 40% of finger speed for a controlled feel)
    setSwipeOffset(-diff * 0.4);
  }
  function handleSwipeEnd(e) {
    if (swipeStartX.current === null) return;
    const diff = swipeDragX.current;
    swipeStartX.current = null;
    // Animate snap back
    setSwipeTransition(true);
    setSwipeOffset(0);
    // Need enough drag to count as a swipe
    if (Math.abs(diff) < 40) return;
    const currentIdx = weekDays.findIndex(d => d.iso === selectedDayIso);
    if (diff > 0) {
      // Swipe left → next day
      if (currentIdx < weekDays.length - 1) selectDay(weekDays[currentIdx + 1].iso);
      else navigateWeek(1, 'first');
    } else {
      // Swipe right → previous day
      if (currentIdx > 0) selectDay(weekDays[currentIdx - 1].iso);
      else navigateWeek(-1, 'last');
    }
  }

  // Navigate to previous/next week. selectEdge: 'first'|'last' to pick edge day (for swipe)
  async function navigateWeek(direction, selectEdge) {
    const newOffset = weekOffset + direction;
    const minWeek = isMissedRebook ? 0 : 1;
    if (newOffset < minWeek || newOffset > 12) return;
    setWeekOffset(newOffset);
    const days = getWeekDays(newOffset);
    const targetDay = selectEdge === 'first' ? days[0]
      : selectEdge === 'last' ? days[days.length - 1]
      : days.find(d => d.isCenter) || days[3];
    setSelectedDayIso(targetDay.iso);
    setRebookDate(null); setRebookTime(null);
    setLoadingSlots(true);
    await Promise.all(days.map(d =>
      daySlots[d.iso]?.loaded ? Promise.resolve() : loadDaySlots(d.iso, rebookSelectedTherapist)
    ));
    setLoadingSlots(false);
    if (isMissedRebook) autoSelectFirstAvailableDay(days);
  }

  // Legacy loadTimeSlots — now initializes the week view
  async function loadTimeSlots(therapist, weekOverride) {
    const weeksOut = parseInt(weekOverride || rebookWeek);
    setWeekOffset(weeksOut);
    setDaySlots({});
    const days = getWeekDays(weeksOut);
    const targetDay = days.find(d => d.isCenter) || days[3];
    setSelectedDayIso(targetDay.iso);
    setRebookDate(null); setRebookTime(null);
    setLoadingSlots(true);
    await Promise.all(days.map(d => loadDaySlots(d.iso, therapist)));
    setLoadingSlots(false);
    // Only auto-jump to first available day for missed rebook (checkout keeps center day selected)
    if (isMissedRebook) autoSelectFirstAvailableDay(days);
  }

  // After loading a week, jump to the first day that has time slots (missed rebook only)
  function autoSelectFirstAvailableDay(days) {
    setTimeout(() => {
      setDaySlots(current => {
        const today = new Date().toLocaleDateString('en-CA');
        const firstAvail = days.find(d => d.iso >= today && current[d.iso]?.loaded && current[d.iso].times.length > 0);
        if (firstAvail) setSelectedDayIso(firstAvail.iso);
        return current;
      });
    }, 50);
  }

  // ============================================================
  // REBOOK FLOW HANDLERS
  // ============================================================
  async function handleWeekSelect(week) {
    setRebookWeek(week);
    setSameTimeSlot(computeSameTimeSlot(week));
    setRebookShowMoreDays(false); setExpandedDays({});
    setRebookDate(null); setRebookTime(null);
    // Demo mode: skip service selection but show therapist selection
    const isDemo = new URLSearchParams(window.location.search).get('demo') === 'rebook';
    if (isDemo) {
      await loadTherapists();
      setStep('rebook_therapist');
      return;
    }
    setStep('rebook_services');
  }

  function handleCategorySelect(catId) {
    setSelectedCategory(catId);
    setStep('rebook_services');
  }

  function toggleService(svcId) {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === svcId);
      if (exists) {
        if (!exists.selected) trackAddToCart(exists.name, exists.price, 'Rebook');
        return prev.map(s => s.id === svcId ? { ...s, selected: !s.selected } : s);
      }
      // New service from catalog — add it
      const catalogSvc = categoryServices.find(s => s.id === svcId);
      if (catalogSvc) {
        trackAddToCart(catalogSvc.name, catalogSvc.price, 'Rebook');
        return [...prev, { ...catalogSvc, selected: true, preSelected: false }];
      }
      return prev;
    });
  }

  function getSelectedServicesList() {
    return selectedServices.filter(s => s.selected);
  }

  // Compute nail and wax service IDs for guided add-on flow
  const nailServiceIds = useMemo(() => {
    if (!fullCatalog || !categories.length) return new Set();
    const nailCatIds = categories.filter(c => (c.name || '').toLowerCase().includes('nail')).map(c => c.id);
    const ids = new Set();
    for (const catId of nailCatIds) {
      for (const svc of (fullCatalog.services?.[catId] || [])) {
        ids.add(svc.id);
      }
    }
    return ids;
  }, [fullCatalog, categories]);

  const waxServiceIds = useMemo(() => {
    if (!fullCatalog || !categories.length) return new Set();
    const waxCatIds = categories.filter(c => (c.name || '').toLowerCase().includes('wax')).map(c => c.id);
    const ids = new Set();
    for (const catId of waxCatIds) {
      for (const svc of (fullCatalog.services?.[catId] || [])) {
        ids.add(svc.id);
      }
    }
    return ids;
  }, [fullCatalog, categories]);

  // Check if any selected services are eligible for upkeep discount
  function hasUpkeepEligibleServices() {
    return getSelectedServicesList().some(s => isUpkeepEligible(s));
  }

  // Compute recommended rebook timeline from service codes
  // Uses the longest timeline among selected services, or default 5 weeks
  function getRecommendedRebook() {
    const selected = getSelectedServicesList();
    let maxDays = 0;
    let hasCode = false;
    for (const svc of selected) {
      const timeline = parseRebookTimeline(svc.code);
      if (timeline) {
        hasCode = true;
        const days = timeline.days || (timeline.weeks * 7);
        if (days > maxDays) maxDays = days;
      }
    }
    if (!hasCode) return { weeks: DEFAULT_REBOOK_WEEKS, label: `${DEFAULT_REBOOK_WEEKS} Weeks` };
    const weeks = Math.round(maxDays / 7);
    return { weeks, days: maxDays, label: weeks === 1 ? '1 Week' : `${weeks} Weeks` };
  }

  // Whether the selected week is past the upkeep discount window
  const isPastUpkeepWindow = rebookWeek && parseInt(rebookWeek) > UPKEEP_MAX_WEEKS && hasUpkeepEligibleServices();

  async function proceedFromServices() {
    setPageLoading(true);
    try {
      const { addOns: freshAddOns, mandatory: freshMandatory } = await loadAddOnsForServices();
      // Use returned values (not stale state) to decide next step
      const hasOptional = getSelectedServicesList().some(svc => {
        const a = freshAddOns[svc.id];
        return a && a.optional && a.optional.length > 0;
      });
      if (hasOptional) {
        setStep('rebook_addons');
      } else if (freshMandatory.length > 0) {
        setCurrentMandatoryIdx(0);
        setStep('rebook_mandatory');
      } else {
        await loadTherapists();
        setStep('rebook_therapist');
      }
    } finally { setPageLoading(false); }
  }

  function toggleOptionalAddOn(addOn, parentServiceId) {
    setSelectedOptionalAddOns(prev => {
      const exists = prev.find(a => a.id === addOn.id && a.parentServiceId === parentServiceId);
      if (exists) return prev.filter(a => !(a.id === addOn.id && a.parentServiceId === parentServiceId));
      trackAddToCart(addOn.name, addOn.price, 'Rebook Add-On');
      return [...prev, { ...addOn, parentServiceId }];
    });
  }


  async function proceedFromOptionalAddOns() {
    setPageLoading(true);
    try {
      if (mandatoryServices.length > 0) {
        setCurrentMandatoryIdx(0);
        setStep('rebook_mandatory');
      } else {
        await loadTherapists();
        setStep('rebook_therapist');
      }
    } finally { setPageLoading(false); }
  }

  function handleRemovalAnswer(serviceId, answer) {
    setNeedsRemoval(prev => ({ ...prev, [serviceId]: answer }));
    if (answer && serviceAddOns[serviceId]?.mandatory?.length > 0) {
      setStep('rebook_mandatory_select');
    } else {
      // Move to next mandatory service or proceed
      advanceMandatory();
    }
  }

  function selectMandatoryAddOn(addOn, parentServiceId) {
    setSelectedMandatoryAddOns(prev => {
      // Replace any existing mandatory for this parent
      const filtered = prev.filter(a => a.parentServiceId !== parentServiceId);
      return [...filtered, { ...addOn, parentServiceId, isMandatory: true }];
    });
  }

  async function advanceMandatory() {
    setPageLoading(true);
    try {
      const nextIdx = currentMandatoryIdx + 1;
      if (nextIdx < mandatoryServices.length) {
        setCurrentMandatoryIdx(nextIdx);
        setStep('rebook_mandatory');
      } else {
        await loadTherapists();
        setStep('rebook_therapist');
      }
    } finally { setPageLoading(false); }
  }

  function selectTherapist(therapist) {
    setRebookSelectedTherapist(therapist);
  }

  async function proceedFromTherapist() {
    if (!rebookSelectedTherapist) return;
    setPageLoading(true);
    try {
      setRebookDate(null); setRebookTime(null);
      setRebookShowMoreDays(false); setExpandedDays({});
      await loadTimeSlots(rebookSelectedTherapist);
      setStep('rebook_time');
    } finally { setPageLoading(false); }
  }

  function computeSameTimeSlot(week) {
    if (!appointment) return null;
    const today = new Date();
    const weeksOut = parseInt(week);
    const sameDate = new Date(today);
    sameDate.setDate(sameDate.getDate() + (weeksOut * 7));
    const monthDay = sameDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const isoDate = sameDate.getFullYear() + '-' + String(sameDate.getMonth() + 1).padStart(2, '0') + '-' + String(sameDate.getDate()).padStart(2, '0');
    return {
      date: monthDay, time: appointment.time, isoDate,
      label: sameDate.toLocaleDateString('en-US', { weekday: 'short' }) + ' at ' + appointment.time,
      sublabel: weeksOut + ' weeks from today'
    };
  }

  function getVisibleDays() {
    return rebookShowMoreDays ? rebookSlots : rebookSlots.slice(0, INITIAL_DAYS_SHOWN);
  }

  function hasMoreDays() { return !rebookShowMoreDays && rebookSlots.length > INITIAL_DAYS_SHOWN; }
  function getHiddenDayCount() { return rebookSlots.length - INITIAL_DAYS_SHOWN; }

  function getVisibleTimes(day) {
    return expandedDays[day.date] ? day.times : day.times.slice(0, INITIAL_TIMES_PER_DAY);
  }

  function hasMoreTimes(day) {
    return !expandedDays[day.date] && day.times.length > INITIAL_TIMES_PER_DAY;
  }

  function toggleDayExpand(dateKey) {
    setExpandedDays(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  }

  async function confirmRebook(overrideCenterTime) {
    setPageLoading(true);
    // Demo mode: skip API, just show confirmed screen
    const isDemo = new URLSearchParams(window.location.search).get('demo') === 'rebook';
    if (isDemo) {
      const selectedDate = selectedDayIso ? new Date(selectedDayIso + 'T12:00:00') : null;
      const isWithinUpkeep = selectedDate ? selectedDate <= upkeepCutoffDate : true;
      setRebookDiscountApplied(isWithinUpkeep);
      setRebookConfirmed(true);
      setPageLoading(false);
      setTimeout(() => setStep(isMissedRebook ? 'done' : 'tip'), 1800);
      return;
    }

    try {
      const centerTime = overrideCenterTime || centerTimeMap[`${rebookDate}|${rebookTime}`];
      if (!centerTime) {
        console.error('No centerTime found for', rebookDate, rebookTime);
        setPageLoading(false);
        setError('Could not reserve that time slot. Please pick another time.');
        return;
      }

      const services = getBookingServices(rebookSelectedTherapist);

      // Use V1 kiosk rebook endpoint (admin-level, no guest auth needed)
      const rebookRes = await fetch(`${API_URL}/api/kiosk/rebook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId,
          guestId: guest?.guestId,
          services,
          slotTime: centerTime
        })
      });
      const rebookData = await rebookRes.json();
      if (rebookData.error) throw new Error(rebookData.error);

      // Apply discount (WAXLOVE or UPKEEP) — only if within the upkeep window AND services are eligible
      const selectedDate = selectedDayIso ? new Date(selectedDayIso + 'T12:00:00') : null;
      const isWithinUpkeep = selectedDate ? selectedDate <= upkeepCutoffDate : true;
      const eligibleForDiscount = hasUpkeepEligibleServices();
      if (rebookData.appointmentGroupId && isWithinUpkeep && eligibleForDiscount) {
        try {
          await fetch(`${API_URL}/api/kiosk/apply-discount`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invoiceId: rebookData.invoiceId, appointmentId: rebookData.appointmentGroupId, couponCode: discountCode, centerId })
          });
          setRebookDiscountApplied(true);
        } catch (discountErr) {
          console.log('Discount apply error (non-blocking):', discountErr);
          setRebookDiscountApplied(false);
        }
      } else {
        setRebookDiscountApplied(false);
        if (rebookData.appointmentGroupId && !isWithinUpkeep) {
          console.log(`[KIOSK] Skipping ${discountCode} discount — selected date ${selectedDayIso} is past ${UPKEEP_MAX_WEEKS}-week upkeep window`);
        } else if (rebookData.appointmentGroupId && !eligibleForDiscount) {
          console.log(`[KIOSK] Skipping discount — services not eligible for upkeep discount`);
        }
      }
    } catch (err) {
      console.error('Rebook error:', err);
      setPageLoading(false);
      setError(err.message || 'Booking failed. Please try again or ask the front desk.');
      return;
    }

    setPageLoading(false);
    const selectedSvcs = getSelectedServicesList().map(s => resolveService(s, selectedDayIso));
    trackRebook(
      selectedSvcs,
      `${rebookDate} ${rebookTime}`,
      rebookSelectedTherapist?.name
    );
    const daysOut = selectedDayIso ? Math.round((new Date(selectedDayIso) - new Date()) / 86400000) : null;
    const rebookListTotal = [...selectedSvcs, ...selectedOptionalAddOns, ...selectedMandatoryAddOns]
      .reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
    const rebookTotal = rebookDiscountApplied ? rebookListTotal * 0.9 : rebookListTotal; // upkeep/first-visit = 10% off
    kev('rebook_confirm', 'rebook_confirmed', {
      services: selectedSvcs.map(s => s.name),
      serviceCount: selectedSvcs.length,
      therapist: rebookSelectedTherapist?.name,
      therapistPreference: rebookSelectedTherapist?.id === 'any' ? 'any' : 'specific',
      daysOut,
      discountApplied: rebookDiscountApplied,
      total: Math.round(rebookTotal * 100) / 100,
    });
    setRebookConfirmed(true);
    setTimeout(() => setStep(isMissedRebook ? 'done' : 'tip'), 1800);
  }

  function selectSameTime() {
    if (sameTimeSlot) {
      const ct = buildCenterTime(sameTimeSlot.isoDate, sameTimeSlot.time);
      setRebookDate(sameTimeSlot.date); setRebookTime(sameTimeSlot.time);
      confirmRebook(ct);
    }
  }

  // Trigger terminal payment via Zenoti's online_payments API
  // Order: complete appointment (done at "Looks Good") → trigger terminal (with tip) → poll for result → close invoice (at receipt)
  const paymentPollRef = useRef(null);
  // Package invoice ids bundled into the main terminal charge this session —
  // when set, the legacy post-payment shopCheckout must NOT re-sell them.
  const bundledPackagesRef = useRef(false);
  // Bridge charge dispatched to the terminal this attempt — { pollInvoiceId,
  // totalAmount }. Set right after a successful charge POST (the card MAY be
  // charged from that moment on), cleared when the terminal itself resolves
  // the attempt (declined/cancelled) or a new payment attempt / new checkout
  // starts. A ref (not state) because the poll callbacks close over the render
  // where startPayment ran — state set in that same event isn't visible there.
  // Drives the timeout no-blind-retry guard + accurate paid-amount analytics.
  const bridgeDispatchedRef = useRef(null);

  function stopPaymentPolling() {
    if (paymentPollRef.current) { clearInterval(paymentPollRef.current); paymentPollRef.current = null; }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopPaymentPolling(), []);

  // Poll the helcim-status endpoint every 1s for the webhook result. Shared by
  // the service/bundled charge and the bridge package charge — the webhook keys
  // helcimResults off whatever invoice number the terminal charge was filed
  // under (synthetic pkgsale-* ids work the same as real invoice ids). The
  // webhook flips status to "approved" BEFORE its async Zenoti reconciliation,
  // so approval lands within ~1s of the Helcim webhook (~2-4s from card tap).
  // onApproved runs once on approval; declined/cancelled keep the existing
  // guest-facing copy. onTimeout (optional) replaces ONLY the timeout copy —
  // the bridge call site uses it because a timed-out bridge charge may still
  // land (webhook just slow) and must never be blindly retried; timeout on the
  // service charge keeps the existing behavior (pre-existing, out of scope).
  function beginHelcimStatusPolling(pollId, onApproved, onTimeout) {
    let pollCount = 0;
    const MAX_POLLS = 120; // ~2 minutes at 1s interval
    paymentPollRef.current = setInterval(async () => {
      pollCount++;
      try {
        const statusRes = await fetch(`${API_URL}/api/kiosk/helcim-status/${pollId}`);
        const statusData = await statusRes.json();
        console.log(`[helcim poll ${pollCount}] complete=${statusData.complete}, status=${statusData.status}`);
        if (statusData.complete) {
          stopPaymentPolling();
          if (statusData.status === 'approved') {
            onApproved();
          } else {
            // Terminal resolved the attempt (declined/cancelled) — nothing was
            // charged, so a dispatched bridge charge is dead: clear the marker
            // and let Try Again safely re-dispatch. (No-op on the service flow.)
            bridgeDispatchedRef.current = null;
            setPaymentProcessing(false);
            setPaymentError(statusData.status === 'cancelled'
              ? 'The payment was cancelled at the terminal. Ready when you are — just try again.'
              : 'Your card was declined. Try again or use a different card.');
          }
        }
      } catch (e) { console.log('Helcim poll error (non-blocking):', e.message); }
      if (pollCount >= MAX_POLLS) {
        stopPaymentPolling();
        setPaymentProcessing(false);
        if (onTimeout) {
          onTimeout();
        } else {
          setPaymentError('Payment timed out. The terminal may not have received the request. Please try again or ask the front desk for help.');
        }
      }
    }, 1000);
  }

  async function startPayment() {
    setPaymentProcessing(true);
    setPaymentError(null);
    setBridgeCharge(null); // stale server totals from a declined attempt must not drive display
    bridgeDispatchedRef.current = null; // fresh attempt — prior dispatch was resolved or its package was dropped from the cart

    stopPaymentPolling();
    // Demo mode: simulate the full Helcim-webhook approval path without any real API
    // calls. Hold on "Please pay at the terminal" for 2s (feels like a real card tap),
    // then jump straight to the combined paid+rating screen — no separate "Payment
    // complete!" interstitial (it's the screen header now).
    const isDemoPayment = new URLSearchParams(window.location.search).get('demo');
    if (isDemoPayment) {
      setTimeout(() => { setPaymentProcessing(false); setPaymentComplete(true); setStep('rating'); }, 2000);
      return;
    }
    const tipAmt = getTipAmount();

    // Family-checkout prefetch: while the guest taps the terminal, ask the
    // backend whether anyone linked to this payer (shared phone/email) has an
    // unpaid visit today. Toggle-gated server-side; any error → no prompt.
    if (!familyGuest && guest?.guestId && familyCandidatesRef.current === null) {
      familyCandidatesRef.current = false; // in-flight marker
      fetch(`${API_URL}/api/kiosk/family-open-invoices?zenotiGuestId=${encodeURIComponent(guest.guestId)}&centerId=${encodeURIComponent(centerId)}`)
        .then(r => r.json())
        .then(d => { familyCandidatesRef.current = (d && d.enabled && Array.isArray(d.candidates)) ? d.candidates : []; })
        .catch(() => { familyCandidatesRef.current = []; });
    }

    if (terminal?.type === 'helcim') {
      // ── HELCIM SMART TERMINAL FLOW ──
      try {
        // ── BRIDGE PACKAGE (manual-entry pilot) ──
        // One standalone Helcim charge covers the package AND, on uncovered
        // days, today's service balance — the SERVER computes coverage and the
        // real amount from the live Zenoti invoice; the kiosk only initiates
        // and displays. This branch RETURNS before the legacy create-invoice
        // package flow and the service helcim-payment call below, so the old
        // path can never charge this cart a second time.
        const bridgePkg = shopCart.find(c => c.type === 'bridge-package');
        if (bridgePkg) {
          const chargeRes = await fetch(`${API_URL}/api/kiosk/package-sale/charge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              centerId,
              guestId: guest?.guestId || null,
              guestName: [guest?.firstName, guest?.lastName].filter(Boolean).join(' ') || null,
              guestPhone: phone || guest?.phone || null,
              guestEmail: guest?.email || null,
              packageCatalogId: bridgePkg.id,
              invoiceId: invoiceId || null,
              appointmentId: appointment?.appointmentId || appointment?.id || null,
              soldById: appointment?.provider?.id || null, // provider gets the sale credit
              soldByName: appointment?.provider?.name || null,
              // collected_by for the uncovered-day service payment — same
              // fallback chain as the legacy helcim-payment branch below
              // (provider, else the kiosk-login employee). Seller credit
              // stays soldById (provider-only).
              collectorId: appointment?.provider?.id || employee?.id || null,
              tipAmount: tipAmt > 0 ? tipAmt : 0,
            }),
          });
          let chargeData = null;
          try { chargeData = await chargeRes.json(); } catch (_) { /* non-JSON error body */ }
          if (chargeRes.status === 502) {
            // Terminal offline/unresponsive — the card was never charged.
            throw new Error("The payment terminal isn't responding — please see staff.");
          }
          if (!chargeRes.ok || !chargeData?.success) {
            // 403 = bridge flipped off mid-checkout, 400 = validation. Show the
            // server's message (normal payment-error UX, NO legacy fallback) —
            // the guest can still pay for the service normally and staff can
            // text-link the package.
            throw new Error(chargeData?.error || 'Package payment failed — please see staff.');
          }
          // Charge is on the terminal; the webhook owns the sale from here.
          // Legacy post-payment shopCheckout must never re-sell this package.
          bundledPackagesRef.current = true;
          bridgeDispatchedRef.current = { pollInvoiceId: chargeData.pollInvoiceId, totalAmount: chargeData.totalAmount };
          setBridgeCharge({
            totalAmount: chargeData.totalAmount,
            coveredToday: !!chargeData.coveredToday,
            packageName: bridgePkg.name,
            packagePrice: bridgePkg.price || 0,
          });
          kev('payment', 'package_purchased', { count: 1, bridged: true, covered: !!chargeData.coveredToday });
          beginHelcimStatusPolling(chargeData.pollInvoiceId, () => confirmPaymentComplete(), () => {
            // TIMEOUT with a live dispatched bridge charge: unlike declined/
            // cancelled, we can't know whether the guest's tap went through —
            // the webhook may just be slow, and the server mints a fresh
            // idempotency key per POST, so re-running this branch would be a
            // real second terminal charge. Drop the package from the cart so
            // Try Again can only re-attempt the service payment, and send the
            // guest to staff (who can see the sale in Kiosk Analytics).
            setShopCart(prev => prev.filter(c => c.type !== 'bridge-package'));
            setPaymentError("We couldn't confirm your payment — please see the front desk before trying again.");
          });
          return;
        }

        // Series packages in the cart ride the SAME terminal tap: create each
        // package's own Zenoti invoice FIRST (packages can't share a service
        // invoice), then send ONE bundled charge — the webhook records a
        // custom payment per invoice. Card is never charged if invoice
        // creation fails.
        const cartPackages = shopCart.filter(c => c.type === 'package');
        // Free consult / $0-balance service (e.g. a laser consultation) — the service
        // invoice can't carry a terminal charge (Helcim rejects a $0 TransactionAmount),
        // so packages must be created and charged regardless of the service balance.
        // <= 0 (not === 0): an overpaid/credited service invoice also can't carry the
        // charge, and routing it here keeps the tip on the package primary. Must be a
        // real number — a null/undefined balance (fetch failed/in-flight) stays on the
        // paid path, where the backend recomputes and clamps (safe fail direction).
        const serviceBalanceIsZero = typeof invoiceBalance === 'number' && invoiceBalance <= 0;
        let packageInvoiceIds = [];
        if (cartPackages.length > 0) {
          for (const pkg of cartPackages) {
            const invRes = await fetch(`${API_URL}/api/packages/create-invoice`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                guestId: guest.guestId, packageId: pkg.id, centerId,
                soldById: appointment?.provider?.id || null, // provider gets the sale credit
              }),
            });
            const invData = await invRes.json();
            if (!invData.invoiceId) {
              throw new Error(`Couldn't set up your ${pkg.name || 'package'} purchase — please see the front desk. Your service payment has NOT been charged.`);
            }
            packageInvoiceIds.push(invData.invoiceId);
          }
        }

        const useBundle = packageInvoiceIds.length > 0;

        // $0 service + packages: the service invoice has nothing to charge, so close it
        // on its own (no payment needed — it's free) and charge the package(s) alone as
        // the bundle. Only runs after package-invoice creation succeeded above, so a free
        // consult never closes if the package purchase failed (card also never charged).
        if (serviceBalanceIsZero && useBundle) {
          await closeInvoice(appointment?.appointmentGroupId || appointment?.id);
        }
        // Bundle "primary" is whichever invoice actually needs the terminal charge:
        // the service invoice normally, or the first package invoice when the service
        // was free and already closed above.
        const bundlePrimaryId = serviceBalanceIsZero ? packageInvoiceIds[0] : invoiceId;
        const bundleExtraIds = serviceBalanceIsZero ? packageInvoiceIds.slice(1) : packageInvoiceIds;
        // Poll target must match whatever invoice number the terminal charge was actually
        // filed under (webhook keys helcimResults off it) — not always the service invoiceId.
        const pollInvoiceId = useBundle ? bundlePrimaryId : invoiceId;

        const helcimRes = await fetch(`${API_URL}/api/kiosk/${useBundle ? 'trigger-bundled-terminal-payment' : 'helcim-payment'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(useBundle ? {
            primaryInvoiceId: bundlePrimaryId,
            extraInvoiceIds: bundleExtraIds,
            appointmentId: appointment?.appointmentId || appointment?.id || null,
            tipAmount: tipAmt > 0 ? tipAmt : 0,
            centerId,
            guestId: guest?.guestId || null,
            guestName: [guest?.firstName, guest?.lastName].filter(Boolean).join(' ') || null,
            guestEmail: guest?.email || null,
            guestPhone: phone || null,
            // collected_by: whoever opened the kiosk this morning (Lindsay's call)
            therapistId: employee?.id || appointment?.provider?.id || null,
          } : {
            invoiceId,
            // Lets the webhook re-resolve the current invoice if Zenoti rotated it
            // (group-owner individual payment) and recover the payment automatically
            appointmentId: appointment?.appointmentId || appointment?.id || null,
            centerId,
            tipAmount: tipAmt > 0 ? tipAmt : 0,
            amount: invoiceBalance || 0,
            guestId: guest?.guestId || null,
            guestName: [guest?.firstName, guest?.lastName].filter(Boolean).join(' ') || null,
            guestEmail: guest?.email || null,
            guestPhone: phone || null,
            therapistId: appointment?.provider?.id || employee?.id || null,
            invoiceItems: invoiceData?.items || appointment?.services?.map(s => ({
              name: s.name, price: s.price, therapist_name: appointment?.provider?.name
            })) || [],
            invoiceSubtotal: invoiceData?.subtotal || 0,
            invoiceTax: invoiceData?.tax || 0,
            invoiceDiscount: invoiceData?.discount || 0,
          })
        });
        const helcimData = await helcimRes.json();
        console.log('Helcim payment trigger result:', JSON.stringify(helcimData));
        if (!helcimData.success) {
          throw new Error(helcimData.error || 'Could not send payment to Helcim terminal');
        }

        // Poll for the webhook result (shared helper — see beginHelcimStatusPolling).
        beginHelcimStatusPolling(pollInvoiceId, () => {
          if (useBundle) {
            // Packages were paid inside this tap — legacy post-payment
            // shopCheckout must not charge them again.
            bundledPackagesRef.current = true;
            kev('payment', 'package_purchased', { count: packageInvoiceIds.length, bundled: true });
          }
          confirmPaymentComplete();
        });
      } catch (err) {
        console.error('Helcim payment error:', err);
        setPaymentProcessing(false);
        setPaymentError(err.message || 'Something went wrong. Please ask the front desk.');
      }
    } else {
      // ── VERIFONE TERMINAL FLOW (existing) ──
      try {
        // Appointment completion already done at "Looks Good" button — don't repeat here to avoid 429 rate limits

        // Trigger terminal payment via Zenoti — this sends the charge to the Verifone terminal
        // Tip is included in the terminal charge (no separate tip recording needed)
        if (invoiceId) {
          const terminalRes = await fetch(`${API_URL}/api/kiosk/trigger-terminal-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invoiceId,
              centerId,
              tipAmount: tipAmt > 0 ? tipAmt : undefined,
              guestId: guest?.guestId,
              employeeToken: employee?.token,
              employeeId: employee?.id,
              terminalId: terminal?.id
            })
          });
          const terminalData = await terminalRes.json();
          console.log('Terminal payment trigger result:', JSON.stringify(terminalData));
          if (!terminalData.success) {
            throw new Error(terminalData.error || 'Could not send payment to terminal');
          }
          // API call triggers the terminal — hosted_payment_uri is a web fallback, ignore it
          if (terminalData.data?.hosted_payment_uri) {
            console.log('[KIOSK] Got hosted_payment_uri (ignoring — terminal should activate from API call)');
          }
        }

        // Poll invoice balance every 3s — auto-detect when terminal payment goes through
        let pollCount = 0;
        const MAX_POLLS = 40; // ~2 minutes
        const balanceBefore = invoiceBalance; // remember balance before terminal trigger
        paymentPollRef.current = setInterval(async () => {
          pollCount++;
          try {
            const invRes = await fetch(`${API_URL}/api/kiosk/invoice/${invoiceId}`);
            const invData = await invRes.json();
            console.log(`[poll ${pollCount}] balance=$${invData.balanceDue}, totalPaid=$${invData.totalPaid}, tip=$${invData.tipAmount || 0}, before=$${balanceBefore}`);
            // Payment went through: balance dropped to 0 AND (there's a recorded payment OR tip was paid)
            if (invData.balanceDue <= 0 && (invData.totalPaid > 0 || (tipAmt > 0 && balanceBefore === 0 && invData.tipAmount > 0))) {
              stopPaymentPolling();
              confirmPaymentComplete();
            }
          } catch (e) { console.log('Poll error (non-blocking):', e.message); }
          if (pollCount >= MAX_POLLS) {
            stopPaymentPolling();
            setPaymentProcessing(false);
            setPaymentError('Payment timed out. The terminal may not have received the request. Please try again or ask the front desk for help.');
          }
        }, 3000);
      } catch (err) {
        console.error('Payment error:', err);
        setPaymentProcessing(false);
        setPaymentError(err.message || 'Something went wrong. Please ask the front desk.');
      }
    }
  }

  // Pay with a saved card on file (verified by last 4 digits)
  async function payWithCardOnFile() {
    const input = cardOnFileInput.trim();
    if (input.length !== 4) {
      setCardOnFileError('Please enter the last 4 digits of your card.');
      return;
    }
    const matchedCard = savedCards.find(c => c.lastFour === input);
    if (!matchedCard) {
      setCardOnFileError('No card on file matches those digits. Please try again or pay at the terminal.');
      return;
    }
    setCardOnFileProcessing(true);
    setCardOnFileError('');
    try {
      const tipAmt = getTipAmount();
      // Record tip first if needed (pay-saved-card handles tip separately)
      const res = await fetch(`${API_URL}/api/kiosk/pay-saved-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId,
          accountId: matchedCard.accountId,
          centerId,
          tipAmount: tipAmt > 0 ? tipAmt : 0
        })
      });
      const data = await res.json();
      if (data.success) {
        confirmPaymentComplete();
      } else if (data.requires3DS) {
        setCardOnFileError('This card requires additional verification. Please pay at the terminal instead.');
      } else {
        setCardOnFileError(data.error || 'Payment failed. Please try at the terminal instead.');
      }
    } catch (err) {
      console.error('Card on file payment error:', err);
      setCardOnFileError('Something went wrong. Please try at the terminal instead.');
    }
    setCardOnFileProcessing(false);
  }

  // Auto-called when polling detects balance = 0, or manually by staff
  async function confirmPaymentComplete() {
    stopPaymentPolling();

    // Bridge sale: the server's totalAmount is what the card was actually
    // charged (read via ref — this runs from a poll callback whose render
    // predates the bridgeCharge state update).
    const paidAmount = bridgeDispatchedRef.current
      ? (bridgeDispatchedRef.current.totalAmount || 0)
      : (invoiceBalance || 0);
    trackPurchase(
      `kiosk_${Date.now()}`,
      paidAmount,
      appointment.services.map(s => ({ name: s.name, price: s.price }))
    );
    kev('payment', 'payment_success', { method: terminal?.type || 'card', amount: paidAmount, tipAmount: getTipAmount() });

    // Process cart packages after appointment payment
    if (shopCart.length > 0 && !shopPurchased) {
      setPaymentError(null);
      try {
        await shopCheckout();
      } catch (err) {
        console.error('Cart checkout after payment failed:', err);
        // Don't block — appointment payment succeeded, cart is secondary
      }
    }

    setPaymentProcessing(false);
    setPaymentComplete(true);
    // Combined paid+rating screen now owns the paid-moment header — no 1.2s
    // "Payment complete!" interstitial. The pink check + chime on screen entry
    // is the loud cue.
    const familyCandidates = familyCandidatesRef.current;
    if (!familyGuest && Array.isArray(familyCandidates) && familyCandidates.length > 0) {
      setStep('family_prompt');
    } else {
      setStep('rating');
    }
  }

  // FAMILY CHECKOUT — trimmed second flow for a linked guest's open invoice.
  // Straight to gratuity → payment (Lindsay 2026-08-07: no rebook, no upsell).
  // Reuses the whole tip/payment machinery by loading the candidate as the
  // active checkout; familyGuest gates the prompt from ever re-showing.
  function startFamilyCheckout(candidate) {
    kev('family_prompt', 'family_checkout_started', { balance: candidate.balance });
    setTipSelected(null); setCustomTip(''); setShowCustomTip(false);
    setPaymentComplete(false); setPaymentError(null); setPaymentProcessing(false);
    setShopCart([]); setShopPurchased(false); setGiftCardApplied(null);
    setBridgeCharge(null); // bridge package (if any) belonged to the first payer's checkout
    bridgeDispatchedRef.current = null;
    bundledPackagesRef.current = false;
    const nameParts = (candidate.name || '').trim().split(/\s+/);
    setGuest({
      guestId: candidate.zenotiGuestId,
      firstName: nameParts[0] || candidate.name,
      lastName: nameParts.slice(1).join(' '),
    });
    setInvoiceId(candidate.appointment.invoiceId);
    setInvoiceBalance(candidate.balance);
    setInvoiceData(null); // payment payload falls back to appointment.services
    setAppointment({
      appointmentId: candidate.appointment.zenotiAppointmentId,
      id: candidate.appointment.zenotiAppointmentId,
      provider: { id: candidate.therapistId || null, name: candidate.therapistName || null },
      services: [{
        name: candidate.serviceName || 'Service',
        price: candidate.balance,
        catalogPrice: candidate.balance,
        provider: candidate.therapistName || null,
      }],
    });
    setFamilyGuest(candidate);
    setStep('tip');
  }

  // Complete checkout for zero-balance invoices (package/membership covered everything)
  async function completeZeroBalanceCheckout() {
    setLoading(true);
    // Demo mode: skip API calls; advance straight to the combined paid+rating screen.
    if (new URLSearchParams(window.location.search).get('demo')) {
      setPaymentComplete(true); setLoading(false);
      setStep('rating');
      return;
    }
    const apptId = appointment?.appointmentGroupId || appointment?.id;
    const tipAmt = getTipAmount();

    try {
      // If there's a tip on a zero-balance invoice, record it via the tips endpoint
      // so it's properly attributed to the therapist (not as a custom payment)
      if (tipAmt > 0 && invoiceId) {
        await fetch(`${API_URL}/api/kiosk/add-tip`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            invoiceId,
            tipAmount: tipAmt
          })
        });
      }

      await closeInvoice(apptId);

      trackPurchase(
        `kiosk_zero_${Date.now()}`,
        0,
        appointment.services.map(s => ({ name: s.name, price: s.price }))
      );
      kev('payment', 'payment_success', { method: 'zero_balance', amount: 0, tipAmount: getTipAmount() });

      // Process cart packages if any
      if (shopCart.length > 0 && !shopPurchased) {
        try { await shopCheckout(); } catch (err) { console.error('Cart checkout error:', err); }
      }

      setPaymentComplete(true);
      setStep('rating');
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Could not complete checkout.');
    } finally {
      setLoading(false);
    }
  }

  async function applyGiftCard(card) {
    setGiftCardError(null);
    setPaymentProcessing(true);
    // Gift card covers services only — tips must be paid by card at the terminal
    const serviceDue = invoiceBalance || 0;
    const redeemAmount = Math.min(card.balance, serviceDue);
    const tipAmt = getTipAmount();

    // Demo mode: simulate redemption
    const isDemo = new URLSearchParams(window.location.search).get('demo');
    if (isDemo) {
      setTimeout(() => {
        setGiftCardApplied({ number: card.number, amount: redeemAmount });
        const serviceRemaining = Math.round((serviceDue - redeemAmount) * 100) / 100;
        setRemainingAfterGiftCard(serviceRemaining + tipAmt);
        setPaymentProcessing(false);
        // Only skip terminal if services fully covered AND no tip
        if (serviceRemaining <= 0 && tipAmt <= 0) {
          setPaymentComplete(true);
          setStep('rating');
        }
      }, 1200);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/kiosk/redeem-giftcard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId, giftCardNumber: card.number, amount: redeemAmount })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      const actualRedeemed = data.redeemedAmount || redeemAmount;
      kev('payment', 'giftcard_redeemed', { amount: actualRedeemed });
      setGiftCardApplied({ number: card.number, amount: actualRedeemed });
      const serviceRemaining = Math.round((serviceDue - actualRedeemed) * 100) / 100;
      setRemainingAfterGiftCard(serviceRemaining + tipAmt);
      setPaymentProcessing(false);

      // Only skip terminal if services fully covered AND no tip
      if (serviceRemaining <= 0 && tipAmt <= 0) {
        const apptId = appointment?.appointmentGroupId || appointment?.id;
        await closeInvoice(apptId);
        trackPurchase(`kiosk_gc_${Date.now()}`, actualRedeemed, appointment.services.map(s => ({ name: s.name, price: s.price })));
        setPaymentComplete(true);
        setStep('rating');
      }
    } catch (err) {
      console.error('Gift card error:', err);
      setPaymentProcessing(false);
      // Map Zenoti's domain error messages to guest-facing language. Backend forwards
      // error.message from Zenoti's response — covers both HTTP-level failures (flat
      // { code, message }) and is_applied=false domain failures (nested error.message).
      // Known Zenoti error texts we want to translate:
      //   "Insufficient Balance"  → guest needs to know the balance is too low
      //   "Invalid Gift Card" / "Card not found" → wrong number / typo
      //   "unable to add payment" / "Invoice does not exists" → invoice-side issue, ask staff
      const msg = err.message || '';
      const lower = msg.toLowerCase();
      let friendly;
      if (lower.includes('insufficient')) {
        friendly = "This gift card doesn't have enough balance to cover this visit.";
      } else if (lower.includes('invalid gift card') || lower.includes('card not found') || lower.includes('not found')) {
        friendly = "We couldn't find that gift card. Double-check the number and try again.";
      } else if (lower.includes('unable to add payment') || lower.includes('invoice does not exist') || lower.includes('invoice not found')) {
        friendly = "We couldn't apply that gift card to your invoice. Please ask the front desk for help.";
      } else if (msg) {
        // Pass through any other Zenoti message verbatim — they tend to be readable already.
        friendly = msg;
      } else {
        friendly = 'Could not apply gift card. Please try another payment method.';
      }
      setGiftCardError(friendly);
    }
  }

  // Manual gift card entry: physical card / code not linked to this guest's account.
  // 1) GET /api/kiosk/giftcard-balance/{code} → 404=not found, 200+balance=0=empty, else ok.
  // 2) Hand the resulting { number, balance } to applyGiftCard so the redeem POST,
  //    state updates, and tip-still-owed branching all go through the same path
  //    on-file cards use. Zenoti redeems by card number — no need to link first
  //    (see server.js:11220 — POST /invoices/{id}/giftcard takes no guest_id).
  async function lookupAndApplyManualGiftCard() {
    // Normalize: the input's textTransform:uppercase shows uppercase but the keypad
    // emits whatever the guest typed, so without this the displayed code and the
    // code sent to Zenoti could diverge by case. Trim too — keypad has a space key.
    const code = (manualGcCode || '').trim().toUpperCase();
    if (!code) return;
    setManualGcError(null);
    setManualGcLookup(true);

    // Demo mode: simulate a $50 balance lookup so flow can be exercised without Zenoti.
    if (new URLSearchParams(window.location.search).get('demo')) {
      setTimeout(() => {
        setManualGcLookup(false);
        setManualGcOpen(false);
        setManualGcCode('');
        applyGiftCard({ number: code, balance: 50 });
      }, 600);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/kiosk/giftcard-balance/${encodeURIComponent(code)}`);
      if (res.status === 404) {
        setManualGcLookup(false);
        setManualGcError("We couldn't find that gift card. Double-check the number and try again.");
        return;
      }
      if (!res.ok) {
        setManualGcLookup(false);
        setManualGcError("We couldn't reach the gift card service. Please try again or pay at the terminal.");
        return;
      }
      const data = await res.json();
      const balance = Number(data?.balance) || 0;
      if (balance <= 0) {
        setManualGcLookup(false);
        setManualGcError(`This gift card has no balance remaining (${formatPrice(0)}).`);
        return;
      }
      // Found a valid card with balance → hand off to the on-file apply path.
      kev('payment', 'giftcard_manual_lookup_ok', { balance });
      setManualGcLookup(false);
      setManualGcOpen(false);
      setManualGcCode('');
      applyGiftCard({ number: code, balance });
    } catch (err) {
      console.error('Manual gift card lookup error:', err);
      setManualGcLookup(false);
      setManualGcError("Something went wrong looking up that card. Please try again or pay at the terminal.");
    }
  }

  async function submitRating() {
    // Demo mode: skip API call
    const isDemoRating = new URLSearchParams(window.location.search).get('demo');
    if (!isDemoRating) {
      // Insert feedback into Zenoti via our backend
      try {
        await fetch(`${API_URL}/api/kiosk/rating`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            guestId: guest?.guestId,
            appointmentGroupId: appointment?.appointmentGroupId || appointment?.id,
            appointmentId: appointment?.id,
            rating, comment,
            therapistId: appointment?.provider?.id,
            therapistName: providerName,
            guestName: guest?.firstName,
            guestLastName: guest?.lastName || '',
            centerId
          })
        });
      } catch (err) {
        console.log('Rating submit error (non-blocking):', err);
      }
    }
    setRatingSubmitted(true);
    kev('rating', 'rating_submitted', { rating, hasComment: !!comment });
    if (rating === 5 && !isDemoRating) {
      // Fire Zenoti opportunity in background (triggers review link text) — no screen shown.
      // Demo mode skips this so test taps don't generate real review-request SMS messages.
      // Collect all unique provider names from services (for multi-service appointments)
      const allProviderNames = [...new Set(
        (appointment?.services || []).map(s => s.provider).filter(n => n && n !== 'Provider')
      )];
      fetch(`${API_URL}/api/kiosk/google-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: guest?.guestId,
          guestName: guest?.firstName || 'Guest',
          guestLastName: guest?.lastName || '',
          therapistId: appointment?.provider?.id,
          therapistName: providerName,
          therapistNames: allProviderNames.length > 0 ? allProviderNames : [providerName],
          centerId,
          appointmentGroupId: appointment?.appointmentGroupId || appointment?.id,
          guestPhone: phone || guest?.phone || ''
        })
      }).catch(err => console.log('Google review request error (non-blocking):', err));
    }
    // Advance straight to the receipt screen — the redesigned tail dropped the
    // "Thank you!"/"You made our day!" interstitial. Continue/Skip on the combined
    // paid+rating screen goes directly to "Want your receipt?".
    setStep('receipt');
  }

  // ============================================================
  // FINISH CHECKOUT — complete appointment + close invoice
  // Called once when guest is done (receipt sent or "no thanks")
  // ============================================================
  async function finishCheckout() {
    // Demo mode: skip the defensive closeInvoice call so test runs don't touch any real
    // Zenoti invoice. Real flow still runs the close (idempotent — Helcim webhook may
    // have already closed it during the backgrounded reconciliation).
    if (new URLSearchParams(window.location.search).get('demo')) return;
    const apptId = appointment?.appointmentGroupId || appointment?.id;
    // Appointment completion already done at "Looks Good" button — don't repeat here to avoid 429 rate limits
    // Close the invoice at end of checkout (in case it wasn't closed after payment)
    if (apptId || invoiceId) await closeInvoice(apptId);
  }

  // ============================================================
  // SEND RECEIPT
  // ============================================================
  async function sendReceipt(method) {
    // Demo mode: skip the real receipt send (no emails/SMS go out) but keep the
    // exact same UX timing — receiptSent flips, "Receipt sent!" interstitial shows
    // for 1.5s, then advances to done. Match production pacing on the test device.
    const isDemoReceipt = new URLSearchParams(window.location.search).get('demo');
    if (!isDemoReceipt) {
      try {
        const tipAmt = getTipAmount();
        // Bridge package rides the receipt — it was charged in the same terminal
        // tap but lives outside the Zenoti service invoice until staff enter it.
        // bridgeCharge is only set once the charge was actually initiated (and
        // this screen is only reachable after approval), so gating on it can't
        // add an unsold package. The receipt must ALWAYS reconcile with the card:
        //   uncovered day — card = service balance + package + tip → base invoice
        //     totals + package price (append).
        //   covered day — card = package + tip ONLY (the service is redeemed from
        //     the new package, invoice untouched) → services listed at $0 as
        //     covered, package priced from the SERVER's charge (totalAmount − tip),
        //     no tax/discount lines, so total + tip === bridgeCharge.totalAmount.
        const bridgeSold = bridgeCharge ? shopCart.find(c => c.type === 'bridge-package') : null;
        const baseItems = invoiceData?.items || appointment?.services?.map(s => ({
          name: s.name, price: s.price, therapist_name: appointment?.provider?.name
        })) || [];
        let receiptItems, receiptSubtotal, receiptDiscount, receiptTax, receiptTotal;
        if (bridgeSold && bridgeCharge.coveredToday) {
          const packageCharged = Math.max(0, (bridgeCharge.totalAmount || 0) - tipAmt);
          receiptItems = [
            ...baseItems.map(it => ({
              name: `${it.name} (covered by your new package)`,
              price: 0,
              therapist_name: it.therapist_name || null,
            })),
            { name: bridgeSold.name, price: packageCharged },
          ];
          receiptSubtotal = packageCharged;
          receiptDiscount = 0;
          receiptTax = 0;
          receiptTotal = packageCharged; // + tip below = exactly the card charge
        } else {
          receiptItems = [
            ...baseItems,
            ...(bridgeSold ? [{ name: bridgeSold.name, price: bridgeSold.price || 0 }] : []),
          ];
          receiptSubtotal = (invoiceData?.subtotal || 0) + (bridgeSold?.price || 0);
          receiptDiscount = invoiceData?.discount || 0;
          receiptTax = invoiceData?.tax || 0;
          receiptTotal = (invoiceData?.total || 0) + (bridgeSold?.price || 0);
        }
        await fetch(`${API_URL}/api/kiosk/send-receipt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            method: method || 'email',
            email: method === 'sms' ? null : guest?.email,
            phone: method === 'sms' ? phone : null,
            invoiceId,
            guestName: guest?.firstName || 'Guest',
            centerId,
            items: receiptItems,
            subtotal: receiptSubtotal,
            discount: receiptDiscount,
            tax: receiptTax,
            total: receiptTotal,
            tipAmount: tipAmt
          })
        });
      } catch (err) {
        console.log('Receipt send error (non-blocking):', err);
      }
    }
    setReceiptMethod(method);
    setReceiptSent(true);
    kev('receipt', 'receipt_sent', { method });
    // Complete appointment + close invoice (no-op in demo — see finishCheckout)
    await finishCheckout();
    kev('done', 'flow_complete');
    // No more "Receipt sent!" interstitial — the redesigned done screen surfaces
    // a small "Receipt texted/emailed to you" line when receiptMethod is set.
    setStep('done');
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="kiosk-container">
      <KioskHeader />

      {/* Full-screen loading overlay for page transitions */}
      {pageLoading && (
        <div className="kiosk-page-loading-overlay">
          <div className="kiosk-page-spinner" />
        </div>
      )}

      <div className="kiosk-main">

        {/* PHONE */}
        {step === 'phone' && (
          <div className="kiosk-screen kiosk-phone-screen kiosk-fade-in" style={{ flex: 'none' }}>
            <div className="kiosk-screen-title">Enter your phone number</div>
            <div className="kiosk-screen-sub">We'll pull up your visit details</div>
            <input className="kiosk-phone-input" type="tel" placeholder="(604) 555-1234"
              value={formatPhone(phone)} readOnly />
            <KioskKeypad mode="numeric" value={phone} onChange={v => setPhone(v.slice(0, 10))} maxLength={10} />
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button className="kiosk-btn-primary" disabled={phone.length < 10 || loading} onClick={() => lookupGuest()}>
              {loading ? 'Looking up...' : 'Find Me'}
            </button>
            <button className="kiosk-nav-back" onClick={onBack}>← Back</button>
          </div>
        )}

        {/* FIRST NAME — multiple accounts on this phone */}
        {step === 'name' && (
          <div className="kiosk-screen kiosk-phone-screen kiosk-fade-in">
            <div className="kiosk-screen-title">What's your first name?</div>
            <div className="kiosk-screen-sub">We found more than one account with this number</div>
            <input
              className="kiosk-phone-input"
              type="text"
              placeholder="First name"
              value={nameInput}
              readOnly
            />
            <KioskKeypad mode="alpha" value={nameInput} onChange={setNameInput} maxLength={30} />
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button
              className="kiosk-btn-primary"
              disabled={!nameInput.trim() || loading}
              onClick={() => lookupGuest(nameInput.trim())}
            >
              {loading ? 'Looking up...' : 'Continue'}
            </button>
            <button className="kiosk-nav-back" onClick={() => { setError(null); setNameInput(''); setStep('phone'); }}>← Back</button>
          </div>
        )}

        {/* INVOICE */}
        {step === 'invoice' && appointment && (<>
          <StepDots steps={activeSteps} current="invoice" />
          <div className="kiosk-screen kiosk-invoice-screen kiosk-fade-in">
            <div className="kiosk-greeting">Hi {guest.firstName}! {isTrainingAppointment ? 'Here\'s your training session today.' : 'Here\'s your visit today.'}</div>
            {justApplied && (
              <div className="kiosk-savings-banner" key={justApplied.code + justApplied.savings}>
                ✨ You saved {formatPrice(justApplied.savings)} with {justApplied.code}!
              </div>
            )}
            <div className="kiosk-invoice-card kiosk-slide-up kiosk-delay-1">
              {invoiceData?.items?.length > 0 ? (
                /* SOLO INVOICE — from Zenoti (real prices with discounts) */
                invoiceData.items.map((item, i) => (
                  <React.Fragment key={item.id || i}>
                    <div className="kiosk-invoice-row">
                      <div>
                        <div className="kiosk-svc-name">{item.name}</div>
                        <div className="kiosk-svc-detail">
                          {item.therapist_name ? `with ${item.therapist_name}` : ''}
                          {item.duration > 0 ? ` · ${item.duration} min` : ''}
                          {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                        </div>
                        {item.type === 'service' && item.price === 0 && (
                          <div className="kiosk-svc-detail" style={{ color: '#4a9', fontWeight: 500 }}>
                            {appointment?.isMember ? 'Membership benefit' : 'Package benefit'}
                          </div>
                        )}
                        {(item.discount || 0) > 0 && (
                          <div className="kiosk-campaign-label">
                            {appliedCampaign
                              ? `${appliedCampaign.code} ${appliedCampaign.percent}% off`
                              : 'Discount'}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="kiosk-svc-price">
                          {item.discount > 0 ? (<>
                            <span style={{ textDecoration: 'line-through', opacity: 0.5, marginRight: 6 }}>{formatPrice(item.price)}</span>
                            {formatPrice(item.final_price)}
                          </>) : formatPrice(item.price)}
                        </div>
                        {(item.type === 'product' || item.type === 'package') && (
                          <button
                            onClick={() => removeInvoiceItem(item.invoiceItemId || item.id)}
                            disabled={removingItemId === (item.invoiceItemId || item.id)}
                            style={{
                              background: 'none', border: '1px solid #ccc', borderRadius: 6,
                              padding: '4px 10px', fontSize: 13, color: '#999', cursor: 'pointer',
                              opacity: removingItemId ? 0.5 : 1
                            }}
                          >{removingItemId === (item.invoiceItemId || item.id) ? 'Removing...' : 'Remove'}</button>
                        )}
                      </div>
                    </div>
                    {i < invoiceData.items.length - 1 && <div className="kiosk-divider" />}
                  </React.Fragment>
                ))
              ) : (
                /* SOLO INVOICE — fallback to appointment data */
                appointment.services.map((svc, i) => (
                  <React.Fragment key={i}>
                    <div className="kiosk-invoice-row">
                      <div><div className="kiosk-svc-name">{svc.name}</div><div className="kiosk-svc-detail">with {svc.provider} · {svc.duration} min</div></div>
                      <div className="kiosk-svc-price">{isTrainingAppointment ? 'Free' : formatPrice(svc.price)}</div>
                    </div>
                    {i < appointment.services.length - 1 && <div className="kiosk-divider" />}
                  </React.Fragment>
                ))
              )}
              {/* Inline promo code entry — small "+ Have a promo code?" link → expand to input+Apply.
                  Lives inside the invoice card between line items and the totals area. */}
              {!isTrainingAppointment && invoiceBalance !== 0 && !anyDiscountApplied && invoiceId && (
                <div style={{ padding: '10px 0 6px', textAlign: 'center' }}>
                  {!showCouponInput ? (
                    <button
                      onClick={() => { setShowCouponInput(true); setCouponMessage(null); }}
                      style={{
                        background: 'none', border: 'none', padding: '6px 10px',
                        color: '#c27a7a', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        fontFamily: 'Lato, sans-serif', textDecoration: 'underline',
                      }}
                    >+ Have a promo code?</button>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, maxWidth: 320, margin: '0 auto' }}>
                      <input
                        type="text"
                        value={couponInput}
                        onChange={e => { setCouponInput(e.target.value.toUpperCase()); if (couponMessage) setCouponMessage(null); }}
                        onKeyDown={e => { if (e.key === 'Enter') applyCoupon(couponInput); }}
                        placeholder="ENTER CODE"
                        autoFocus
                        style={{
                          flex: 1, padding: '10px 12px', border: '1px solid #d8c5c0', borderRadius: 8,
                          fontSize: 14, fontFamily: 'Lato, sans-serif', textTransform: 'uppercase',
                          letterSpacing: 1, background: '#fff',
                        }}
                      />
                      <button
                        onClick={() => applyCoupon(couponInput)}
                        disabled={couponLoading || !couponInput.trim()}
                        style={{
                          padding: '10px 14px', background: '#c27a7a', color: '#fff',
                          border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                          fontFamily: 'Lato, sans-serif',
                          cursor: (couponLoading || !couponInput.trim()) ? 'default' : 'pointer',
                          opacity: (couponLoading || !couponInput.trim()) ? 0.5 : 1,
                        }}
                      >{couponLoading ? '…' : 'Apply'}</button>
                    </div>
                  )}
                </div>
              )}
              <div className="kiosk-divider" />
              {isTrainingAppointment ? (
                <div className="kiosk-invoice-row"><div className="kiosk-total-label">Total</div><div className="kiosk-total-price">$0.00</div></div>
              ) : invoiceData ? (<>
                {/* Membership/package benefit line — when balance is 0, no transactions, and services have a price */}
                {invoiceData.balanceDue === 0 && invoiceData.transactions.length === 0 && subtotal > 0 && (
                  <div className="kiosk-invoice-row">
                    <div className="kiosk-tax-label">{appointment?.isMember ? 'Membership Benefit' : 'Package Benefit'}</div>
                    <div className="kiosk-tax-label" style={{ color: '#4a9' }}>-{formatPrice(subtotal)}</div>
                  </div>
                )}
                {invoiceData.discount > 0 && (
                  <div className="kiosk-invoice-row"><div className="kiosk-tax-label">Discount</div><div className="kiosk-tax-label" style={{ color: '#4a9' }}>-{formatPrice(invoiceData.discount)}</div></div>
                )}
                <div className="kiosk-invoice-row"><div className="kiosk-tax-label">Tax</div><div className="kiosk-tax-label">{formatPrice(invoiceData.tax)}</div></div>
                <div className={`kiosk-invoice-row ${justApplied ? 'kiosk-just-discounted' : ''}`}><div className="kiosk-total-label">Total</div><div className="kiosk-total-price" style={{ color: invoiceData.balanceDue === 0 ? '#4a9' : undefined }}>{invoiceData.balanceDue === 0 ? 'Covered' : formatPrice(invoiceData.total)}</div></div>
              </>) : (<>
                <div className="kiosk-invoice-row"><div className="kiosk-tax-label">GST + PST (12%)</div><div className="kiosk-tax-label">{formatPrice(tax)}</div></div>
                <div className="kiosk-invoice-row"><div className="kiosk-total-label">Total</div><div className="kiosk-total-price">{formatPrice(subtotal + tax)}</div></div>
              </>)}
            </div>
            {isTrainingAppointment ? (
              <div className="kiosk-auto-note">Training session — no charge. Please arrive 5 minutes early.</div>
            ) : anyDiscountApplied ? (
              <div className="kiosk-auto-note">Discounts applied automatically</div>
            ) : invoiceBalance !== null && invoiceBalance === 0 ? (
              <div className="kiosk-auto-note">{appointment?.isMember ? 'Your membership covers this visit' : 'Your package covers this visit'}</div>
            ) : (
              <div className="kiosk-auto-note">Packages & memberships applied automatically</div>
            )}
            {/* Coupon entry status line — survives even after the input itself collapses on success */}
            {couponMessage && (
              <div style={{
                margin: '10px auto 0', maxWidth: 420, textAlign: 'center', fontSize: 14, fontWeight: 600,
                color: couponMessage.type === 'success' ? '#4a9' : '#c44'
              }}>
                {couponMessage.type === 'success' ? '✓ ' : ''}{couponMessage.text}
              </div>
            )}
            {/* Skeleton placeholders while featured products are loading — instant render so the
                section doesn't pop in 600ms after the invoice card. Replaced by real cards once
                the fetch resolves. featuredProducts === null = loading; [] = loaded but empty (hide). */}
            {!isTrainingAppointment && featuredProducts === null && (
              <div style={{ margin: '14px 0 4px' }}>
                <div style={{ padding: '0 18px 8px', fontSize: 14, fontWeight: 700, color: '#3a3a3a', letterSpacing: 0.2 }}>
                  Add to your visit
                </div>
                <div
                  style={{
                    display: 'flex', gap: 12, overflowX: 'hidden', padding: '4px 18px 12px',
                  }}
                >
                  {[0, 1, 2, 3].map(i => (
                    <div
                      key={i}
                      style={{
                        flex: '0 0 160px', background: '#fff', border: '1px solid #f0dfd9',
                        borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column',
                      }}
                    >
                      <div style={{ width: '100%', height: 130, background: '#fdf6f3', borderRadius: 10 }} className="kiosk-shimmer" />
                      <div style={{ marginTop: 8, height: 14, width: '80%', background: '#fdf6f3', borderRadius: 4 }} className="kiosk-shimmer" />
                      <div style={{ marginTop: 6, height: 12, width: '40%', background: '#fdf6f3', borderRadius: 4 }} className="kiosk-shimmer" />
                      <div style={{ marginTop: 10, height: 30, background: '#fdf6f3', borderRadius: 8 }} className="kiosk-shimmer" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Featured products carousel — replaces the old "Shop Products" card on this screen.
                Hidden when no products are featured for this center (Lindsay curates via ops). */}
            {!isTrainingAppointment && featuredProducts && featuredProducts.length > 0 && (
              <div style={{ margin: '14px 0 4px' }}>
                {/* Header row: section title left, "Browse all →" link right (catches non-scrollers) */}
                <div style={{
                  padding: '0 18px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#3a3a3a', letterSpacing: 0.2 }}>
                    Add to your visit
                  </div>
                  <button
                    onClick={() => openShop('products')}
                    style={{
                      background: 'none', border: 'none', padding: '4px 6px',
                      color: '#c27a7a', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'Lato, sans-serif',
                    }}
                  >Browse all →</button>
                </div>
                <div
                  style={{
                    display: 'flex', gap: 12, overflowX: 'auto', padding: '4px 18px 12px',
                    scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                  }}
                  className="kiosk-featured-carousel"
                >
                  {featuredProducts.map(product => {
                    // Detect "already added" by scanning the service invoice line items
                    // (PUT /v1/invoices/{id}/products adds them directly there). Quantity reflects
                    // the real line plus any in-flight optimistic bump.
                    const onInvoice = (invoiceData?.items || []).find(it =>
                      it.type === 'product' && (it.id === product.id || it.name === product.name)
                    );
                    const inCart = onInvoice ? { quantity: (onInvoice.quantity || 1) + (optimisticQty[product.id] || 0) } : (optimisticQty[product.id] ? { quantity: optimisticQty[product.id] } : null);
                    const adding = addingProductId === product.id;
                    // Image paths from /api/products are relative — backend serves them at API_URL.
                    // Mirrors KioskShop.jsx getImageUrl() which the Shop overlay already uses correctly.
                    const imgUrl = product.image
                      ? (product.image.startsWith('http') ? product.image : `${API_URL}${product.image}`)
                      : null;
                    return (
                      <div
                        key={product.id}
                        style={{
                          flex: '0 0 160px', scrollSnapAlign: 'start',
                          background: '#fff', border: '1px solid #f0dfd9', borderRadius: 14,
                          padding: 12, display: 'flex', flexDirection: 'column',
                        }}
                      >
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={product.name}
                            onError={e => { e.target.style.visibility = 'hidden'; }}
                            style={{ width: '100%', height: 130, objectFit: 'cover', borderRadius: 10, background: '#fdf6f3' }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: 130, background: '#fdf6f3', borderRadius: 10 }} />
                        )}
                        <div style={{
                          marginTop: 8, fontSize: 13, fontWeight: 600, color: '#1a1a1a',
                          lineHeight: 1.25, minHeight: 32,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>{product.name}</div>
                        <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
                          ${(product.price || 0).toFixed(2)}{inCart && inCart.quantity > 1 ? ` · in bag (${inCart.quantity})` : inCart ? ' · in bag' : ''}
                        </div>
                        <button
                          onClick={() => addProductToVisit(product)}
                          disabled={adding}
                          style={{
                            marginTop: 8, padding: '8px 10px', borderRadius: 8,
                            border: 'none', cursor: adding ? 'default' : 'pointer',
                            background: inCart ? '#fff' : '#E8A0B4',
                            color: inCart ? '#c27a7a' : '#fff',
                            border: inCart ? '1px solid #E8A0B4' : 'none',
                            fontSize: 13, fontWeight: 600, fontFamily: 'Lato, sans-serif',
                            opacity: adding ? 0.6 : 1,
                          }}
                        >{adding ? 'Adding…' : (inCart ? 'Add another' : 'Add to my visit')}</button>
                        {productError?.productId === product.id && (
                          <div style={{ marginTop: 6, fontSize: 12, color: '#c44', textAlign: 'center' }}>
                            {productError.text}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Browse all tile — matches card dimensions, catches engaged scrollers */}
                  <div
                    onClick={() => openShop('products')}
                    style={{
                      flex: '0 0 160px', scrollSnapAlign: 'start',
                      background: '#fff', border: '1px dashed #e8a0b4', borderRadius: 14,
                      padding: 12, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 64, height: 64, borderRadius: 16, background: '#fdf6f3',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
                    }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c27a7a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                        <line x1="3" y1="6" x2="21" y2="6"/>
                        <path d="M16 10a4 4 0 01-8 0"/>
                      </svg>
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: '#c27a7a', textAlign: 'center', lineHeight: 1.3,
                    }}>Browse all<br/>products</div>
                  </div>
                </div>
              </div>
            )}
            {!isTrainingAppointment && (
              <div className="kiosk-shop-buttons kiosk-slide-up kiosk-delay-2">
                {/* Shop Packages stays. Browse All Products discovery moved into the carousel
                    header (a "Browse all →" link) + end-of-carousel tile. */}
                <div className="kiosk-shop-card" onClick={() => openShop('packages')}>
                  <div className="kiosk-shop-card-icon"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c27a7a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l1.09 3.26L16.36 6.5l-2.63 2.13L14.45 12 12 10.07 9.55 12l.72-3.37L7.64 6.5l3.27-1.24z"/><path d="M5 16l.55 1.64L3.5 18.8l1.96.15L6 20.8l.92-1.72 1.96.15-1.42-1.37.55-1.64L6 17.34z"/><path d="M19 16l-.55 1.64 2.05 1.16-1.96.15L18 20.8l-.92-1.72-1.96.15 1.42-1.37-.55-1.64L18 17.34z"/></svg></div>
                  <div className="kiosk-shop-card-title">Shop Packages</div>
                  <div className="kiosk-shop-card-sub">Save with bundles</div>
                </div>
              </div>
            )}
            {shopCart.length > 0 && (
              <div style={{ textAlign: 'center', marginTop: 6, fontSize: 13, color: '#c4879a', fontWeight: 600 }}>
                {shopCart.length} item{shopCart.length !== 1 ? 's' : ''} in bag — {`$${shopCart.reduce((s, c) => s + (c.price || 0), 0).toFixed(2)}`}
              </div>
            )}
          </div>
          <div className="kiosk-bottom-bar">
            <button className="kiosk-nav-back" onClick={onBack}>
              Cancel
            </button>
            <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 280 }}
              disabled={loading}
              onClick={async () => {
                // Complete the appointment (open → started → completed) — must succeed before payment
                const idsToTry = (appointment?.serviceAppointmentIds?.length > 0 ? appointment.serviceAppointmentIds : [appointment?.appointmentId]).filter(Boolean);
                if (idsToTry.length > 0) {
                  setLoading(true);
                  setError(null);
                  try {
                    await markAppointmentsCompleted(idsToTry);
                  } catch (e) {
                    console.error('Appointment completion failed:', e);
                    // Log error but proceed — completion can be retried at payment step
                    console.log('Proceeding despite completion error — will retry at payment');
                  }
                  setLoading(false);
                }
                if (isTrainingAppointment) {
                  closeInvoice(appointment?.appointmentGroupId || appointment?.id);
                }
                trackBeginCheckout(
                  appointment.services.map(s => ({ name: s.name, price: s.price })),
                  invoiceData?.total ?? (subtotal + tax)
                );
                const nextStep = isTrainingAppointment ? 'rating' : (rebookConfirmed ? 'tip' : 'rebook');
                if (nextStep === 'rebook') { kev('invoice', 'rebook_started'); loadFullCatalog(); } // Preload catalog while user picks a week
                // Check for staff recommendations before proceeding
                if (!isTrainingAppointment) {
                  checkRecommendations(nextStep);
                } else {
                  setStep(nextStep);
                }
              }}>
              {loading ? 'Completing...' : (isTrainingAppointment ? 'Continue' : 'Looks Good — Continue')}
            </button>
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center', marginTop: 8, width: '100%' }}>{error}</div>}
          </div>
        </>)}

        {/* SHOP (between invoice and rebook — tabbed: For You / Packages / Services) */}
        {step === 'recommendations' && (
          <div className="kiosk-screen kiosk-fade-in" style={{ padding: '0 0 120px', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 80px)' }}>
            {shopPurchased ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
                <div className="kiosk-screen-title" style={{ fontSize: 22 }}>Purchase Complete!</div>
                <div className="kiosk-screen-sub" style={{ marginBottom: 24, color: '#888' }}>
                  {shopRebookServices.length > 0 ? `${shopRebookServices.length} service${shopRebookServices.length > 1 ? 's' : ''} added to your rebook.` : ''}
                </div>
                <button className="kiosk-nav-forward" onClick={proceedFromShop} style={{ maxWidth: 280 }}>Continue</button>
              </div>
            ) : (
              <>
                {/* Tab bar */}
                <div style={{ display: 'flex', borderBottom: '2px solid #f0e0e6', background: '#fff', position: 'sticky', top: 0, zIndex: 5 }}>
                  {recommendations && (
                    <button onClick={() => setShopTab('foryou')} style={{
                      flex: 1, padding: '14px 8px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600,
                      color: shopTab === 'foryou' ? '#E8A0B4' : '#999', borderBottom: shopTab === 'foryou' ? '3px solid #E8A0B4' : '3px solid transparent',
                      cursor: 'pointer', fontFamily: 'Lato, sans-serif'
                    }}>For You</button>
                  )}
                  <button onClick={() => setShopTab('packages')} style={{
                    flex: 1, padding: '14px 8px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600,
                    color: shopTab === 'packages' ? '#E8A0B4' : '#999', borderBottom: shopTab === 'packages' ? '3px solid #E8A0B4' : '3px solid transparent',
                    cursor: 'pointer', fontFamily: 'Lato, sans-serif'
                  }}>Packages</button>
                  <button onClick={() => setShopTab('services')} style={{
                    flex: 1, padding: '14px 8px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600,
                    color: shopTab === 'services' ? '#E8A0B4' : '#999', borderBottom: shopTab === 'services' ? '3px solid #E8A0B4' : '3px solid transparent',
                    cursor: 'pointer', fontFamily: 'Lato, sans-serif'
                  }}>Services</button>
                </div>

                {/* Tab content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

                  {/* FOR YOU — tech recommendations */}
                  {shopTab === 'foryou' && recommendations && (
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
                        {recommendations.techId && (
                          <img src={`${API_URL}/api/therapist-image/${recommendations.techId}?size=px200`}
                            alt={recommendations.techName?.split(' ')[0] || ''} onError={e => { e.target.style.display = 'none'; }}
                            style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '3px solid #E8A0B4', marginBottom: 12 }} />
                        )}
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#3a3a3a', textAlign: 'center' }}>
                          {recommendations.techName ? `${recommendations.techName.split(' ')[0]}'s Recommendations` : 'Recommended for you'}
                        </div>
                        <div style={{ fontSize: 14, color: '#888', textAlign: 'center', marginTop: 4 }}>Based on today's visit</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {recommendations.packages.map(pkg => {
                          const isService = pkg.type === 'service';
                          const isPackage = !isService && pkg.type !== 'product';
                          const pkgStatus = isPackage ? packageLinkStatus[pkg.id] : null;
                          const pkgSent = pkgStatus === 'sent';
                          const inRebook = isService && shopRebookServices.some(s => s.id === pkg.id);
                          // Products route through addProductToVisit → live on invoiceData.items.
                          // Packages: bridge-enabled centers sell them right here (same Add/
                          // Remove toggle as the Packages tab — identical behavior for the
                          // same package in both tabs); otherwise the kiosk can't sell them
                          // and we text the guest a webstore link instead.
                          const inBridgeCart = isPackage && bridgeEnabled &&
                            shopCart.some(c => c.type === 'bridge-package' && c.id === pkg.id);
                          const inCart = pkg.type === 'product' &&
                            (invoiceData?.items || []).some(it => it.type === 'product' && (it.id === pkg.id || it.name === pkg.name));
                          const isActive = inCart || inRebook || inBridgeCart || (isPackage && !bridgeEnabled && pkgSent);
                          return (
                            <div key={pkg.id} style={{
                              background: isActive ? '#fef0f4' : '#fff', borderRadius: 12, padding: '16px 18px',
                              border: isActive ? '2px solid #E8A0B4' : '1px solid #f0e0e6',
                              display: 'flex', alignItems: 'center', gap: 12
                            }}>
                              {pkg.image ? (
                                <img src={pkg.image.startsWith('http') ? pkg.image : `${API_URL}${pkg.image}`}
                                  alt="" onError={e => { e.target.style.display = 'none'; }}
                                  style={{ width: 180, height: 180, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
                              ) : (
                                <div style={{ width: 180, height: 180, borderRadius: 12, flexShrink: 0,
                                  background: 'linear-gradient(135deg, #f7e8ee, #edd5de)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  textAlign: 'center', padding: 16
                                }}>
                                  <div style={{ fontSize: 16, fontWeight: 700, color: '#c4879a', lineHeight: 1.3 }}>{pkg.name}</div>
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 15, fontWeight: 600, color: '#3a3a3a' }}>{pkg.name}</div>
                                {pkg.description && <div style={{ fontSize: 13, color: '#999', marginTop: 3 }}>{pkg.description}</div>}
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#c4879a', marginTop: 4 }}>${pkg.price?.toFixed(2)}</div>
                                {isPackage && !bridgeEnabled && pkgStatus === 'error' && (
                                  <div style={{ fontSize: 12, color: '#c44', marginTop: 4 }}>Couldn't send — try again</div>
                                )}
                              </div>
                              <button onClick={() => {
                                if (isService) {
                                  inRebook ? removeServiceFromRebook(pkg.id) : addServiceToRebook({ id: pkg.id, name: pkg.name, price: pkg.price, duration: pkg.duration || 0 });
                                } else if (isPackage) {
                                  if (bridgeEnabled) {
                                    // Bridge center: sell it here — addToCart converts to
                                    // 'bridge-package' (one per checkout, replaces existing).
                                    inBridgeCart
                                      ? removeFromCart(pkg.id)
                                      : addToCart({ id: pkg.id, name: pkg.name, price: pkg.priceFinal || pkg.price, type: 'package' });
                                  } else {
                                    // Kiosk can't sell packages — text the guest a webstore link instead.
                                    textPackageLink(pkg);
                                  }
                                } else if (inCart) {
                                  // Product is on the invoice (added via addProductToVisit) — remove the
                                  // matching line item instead of touching shopCart (which it isn't in).
                                  const line = (invoiceData?.items || []).find(it =>
                                    it.type === 'product' && (it.id === pkg.id || it.name === pkg.name)
                                  );
                                  if (line) removeInvoiceItem(line.invoiceItemId || line.id);
                                } else {
                                  addToCart({ ...pkg, type: pkg.type || 'product' });
                                }
                              }}
                                disabled={isPackage && !bridgeEnabled && (pkgStatus === 'sending' || pkgSent)}
                                style={{
                                  padding: '10px 16px', background: isActive ? '#fff' : '#E8A0B4', color: isActive ? '#c4879a' : '#fff',
                                  border: isActive ? '1px solid #e8c8d4' : 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
                                  cursor: (isPackage && !bridgeEnabled && (pkgStatus === 'sending' || pkgSent)) ? 'default' : 'pointer',
                                  flexShrink: 0, fontFamily: 'Lato, sans-serif',
                                  minWidth: isPackage ? 120 : undefined, textAlign: 'center'
                                }}>
                                {isService
                                  ? (inRebook ? 'Remove' : 'Add to Rebook')
                                  : isPackage
                                    ? (bridgeEnabled
                                        ? (inBridgeCart ? 'Remove' : 'Add to Cart')
                                        : (pkgSent ? '✓ Sent!' : pkgStatus === 'sending' ? 'Sending…' : 'Text me the link 📲'))
                                    : (inCart ? 'Remove' : 'Add to Cart')}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* PACKAGES — all available packages */}
                  {shopTab === 'packages' && (
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#3a3a3a', marginBottom: 14 }}>Available Packages</div>
                      {shopPackages.length === 0 ? (
                        <div style={{ color: '#999', textAlign: 'center', padding: 40 }}>No packages available</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {shopPackages.map(pkg => {
                            const status = packageLinkStatus[pkg.id];
                            const sent = status === 'sent';
                            // Bridge-enabled centers sell the package right here (added to
                            // the cart, charged in the same terminal tap); everywhere else
                            // keeps the legacy text-me-the-webstore-link flow untouched.
                            const inCart = bridgeEnabled && shopCart.some(c => c.type === 'bridge-package' && c.id === pkg.id);
                            const active = bridgeEnabled ? inCart : sent;
                            return (
                              <div key={pkg.id} style={{
                                background: active ? '#fef0f4' : '#fff', borderRadius: 12, padding: '16px 18px',
                                border: active ? '2px solid #E8A0B4' : '1px solid #f0e0e6',
                                display: 'flex', alignItems: 'center', gap: 12
                              }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 15, fontWeight: 600, color: '#3a3a3a' }}>{pkg.name}</div>
                                  {pkg.services?.length > 0 && (
                                    <div style={{ fontSize: 13, color: '#999', marginTop: 3 }}>
                                      {pkg.services.map(s => `${s.name}${s.quantity > 1 ? ` x${s.quantity}` : ''}`).join(', ')}
                                    </div>
                                  )}
                                  <div style={{ fontSize: 15, fontWeight: 700, color: '#c4879a', marginTop: 4 }}>${(pkg.priceFinal || pkg.price)?.toFixed(2)}</div>
                                  {!bridgeEnabled && status === 'error' && (
                                    <div style={{ fontSize: 12, color: '#c44', marginTop: 4 }}>Couldn't send — try again</div>
                                  )}
                                </div>
                                {bridgeEnabled ? (
                                  <button
                                    onClick={() => inCart
                                      ? removeFromCart(pkg.id)
                                      : addToCart({ id: pkg.id, name: pkg.name, price: pkg.priceFinal || pkg.price, type: 'package' })}
                                    style={{
                                      padding: '10px 16px', background: inCart ? '#fff' : '#E8A0B4', color: inCart ? '#c4879a' : '#fff',
                                      border: inCart ? '1px solid #e8c8d4' : 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
                                      cursor: 'pointer', flexShrink: 0, fontFamily: 'Lato, sans-serif',
                                      minWidth: 120, textAlign: 'center'
                                    }}>
                                    {inCart ? 'Remove' : 'Add to Cart'}
                                  </button>
                                ) : (
                                  <button onClick={() => textPackageLink(pkg)} disabled={status === 'sending' || sent}
                                    style={{
                                      padding: '10px 16px', background: sent ? '#fff' : '#E8A0B4', color: sent ? '#c4879a' : '#fff',
                                      border: sent ? '1px solid #e8c8d4' : 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
                                      cursor: (status === 'sending' || sent) ? 'default' : 'pointer', flexShrink: 0, fontFamily: 'Lato, sans-serif',
                                      minWidth: 120, textAlign: 'center'
                                    }}>
                                    {sent ? '✓ Sent!' : status === 'sending' ? 'Sending…' : 'Text me the link 📲'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* SERVICES — browse categories → add to rebook */}
                  {shopTab === 'services' && (
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#3a3a3a', marginBottom: 4 }}>Add Services to Your Next Visit</div>
                      <div style={{ fontSize: 14, color: '#888', marginBottom: 14 }}>These will be pre-selected when you rebook</div>
                      {!shopServiceCat ? (
                        /* Category list */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {(categories.length > 0 ? categories : []).map(cat => (
                            <button key={cat.id} onClick={() => loadShopServices(cat.id)}
                              style={{
                                padding: '16px 18px', background: '#fff', border: '1px solid #f0e0e6',
                                borderRadius: 12, textAlign: 'left', cursor: 'pointer', fontFamily: 'Lato, sans-serif',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                              }}>
                              <span style={{ fontSize: 15, fontWeight: 600, color: '#3a3a3a' }}>{cat.name}</span>
                              <span style={{ color: '#ccc', fontSize: 18 }}>&rsaquo;</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        /* Service list within category */
                        <div>
                          <button onClick={() => { setShopServiceCat(null); setShopServices([]); }}
                            style={{ background: 'none', border: 'none', color: '#c4879a', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 12, fontFamily: 'Lato, sans-serif' }}>
                            &larr; Categories
                          </button>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {shopServices.map(svc => {
                              const added = shopRebookServices.some(s => s.id === svc.id);
                              return (
                                <div key={svc.id} style={{
                                  background: added ? '#fef0f4' : '#fff', borderRadius: 12, padding: '14px 16px',
                                  border: added ? '2px solid #E8A0B4' : '1px solid #f0e0e6',
                                  display: 'flex', alignItems: 'center', gap: 12
                                }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#3a3a3a' }}>{svc.name}</div>
                                    <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                                      {svc.duration} min · ${svc.price?.toFixed(2)}
                                    </div>
                                  </div>
                                  <button onClick={() => added ? removeServiceFromRebook(svc.id) : addServiceToRebook(svc)}
                                    style={{
                                      padding: '8px 14px', background: added ? '#fff' : '#E8A0B4', color: added ? '#c4879a' : '#fff',
                                      border: added ? '1px solid #e8c8d4' : 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                      cursor: 'pointer', flexShrink: 0, fontFamily: 'Lato, sans-serif'
                                    }}>
                                    {added ? 'Remove' : 'Add to Rebook'}
                                  </button>
                                </div>
                              );
                            })}
                            {shopServices.length === 0 && <div style={{ color: '#999', textAlign: 'center', padding: 30 }}>No services in this category</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Bottom bar — cart + continue */}
                <div style={{
                  position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff',
                  borderTop: '1px solid #f0e0e6', padding: '12px 20px',
                  boxShadow: '0 -2px 10px rgba(0,0,0,0.06)', zIndex: 10
                }}>
                  {shopCart.length > 0 && (
                    <div style={{ maxWidth: 480, margin: '0 auto 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, color: '#3a3a3a' }}>
                      <span style={{ fontWeight: 600 }}>Cart: {shopCart.length} item{shopCart.length > 1 ? 's' : ''}</span>
                      <span style={{ fontWeight: 700, color: '#c4879a' }}>${shopCart.reduce((s, c) => s + (c.price || 0), 0).toFixed(2)}</span>
                    </div>
                  )}
                  {shopRebookServices.length > 0 && (
                    <div style={{ maxWidth: 480, margin: '0 auto 8px', fontSize: 13, color: '#888' }}>
                      Rebook: {shopRebookServices.map(s => s.name).join(', ')}
                    </div>
                  )}
                  <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', gap: 10 }}>
                    <button onClick={() => setStep('invoice')}
                      style={{
                        padding: '14px 20px', background: 'transparent',
                        color: '#c4879a', border: '1px solid #e8c8d4',
                        borderRadius: 10, fontSize: 15, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'Lato, sans-serif'
                      }}>
                      ← Back
                    </button>
                    {(() => {
                      // Products added via the recommendations card route through
                      // addProductToVisit → live on invoiceData.items, not shopCart.
                      // Count those too so the button reflects the customer's true cart state.
                      const hasInvoiceProducts = (invoiceData?.items || []).some(it => it.type === 'product');
                      const hasItems = shopCart.length > 0 || shopRebookServices.length > 0 || hasInvoiceProducts;
                      return (
                        <button onClick={proceedFromShop}
                          style={{
                            flex: 1, padding: '14px', background: hasItems ? '#E8A0B4' : 'transparent',
                            color: hasItems ? '#fff' : '#c4879a',
                            border: hasItems ? 'none' : '1px solid #e8c8d4',
                            borderRadius: 10, fontSize: 15, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'Lato, sans-serif'
                          }}>
                          {hasItems ? 'Next' : 'Skip'}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* REBOOK — WEEK SELECTION + BANNER */}
        {step === 'rebook' && (<>
          <StepDots steps={activeSteps} current="rebook" />
          <div className="kiosk-screen kiosk-rebook-screen kiosk-fade-in" style={{ position: 'relative' }}>
            {isMissedRebook ? (
              /* Missed appointment rebook — near-term week options */
              <>
                <div className="kiosk-rebook-banner kiosk-slide-up kiosk-delay-1">
                  <div className="kiosk-rebook-title">When would you like to come in?</div>
                  <div className="kiosk-rebook-savings">Let's get you rebooked, {guest?.firstName}!</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, width: '100%' }} className="kiosk-slide-up kiosk-delay-2">
                  <button className="kiosk-week-btn" onClick={() => handleWeekSelect('0')}><div className="kiosk-week-btn-label">This Week</div><div className="kiosk-week-btn-sub">&nbsp;</div></button>
                  <button className="kiosk-week-btn" onClick={() => handleWeekSelect('1')}><div className="kiosk-week-btn-label">Next Week</div><div className="kiosk-week-btn-sub">&nbsp;</div></button>
                  <button className="kiosk-week-btn" onClick={() => handleWeekSelect('2')}><div className="kiosk-week-btn-label">2 Weeks</div><div className="kiosk-week-btn-sub">&nbsp;</div></button>
                </div>
                <button className="kiosk-nav-back" onClick={onBack}>← Back</button>
              </>
            ) : (
              /* Normal checkout rebook — redesigned 2026-05-31. Two-choice screen with
                 warm copy. Express card + 4/5/6 week buttons + "or" divider were
                 replaced by Save my spot → existing picker (pre-set to recommended
                 week) / Skip & pay → straight to tip. Back is top-left, distinct. */
              (() => {
                const recommended = getRecommendedRebook();
                const recWeeks = recommended.weeks;
                const banner = pickRebookBanner({
                  services: appointment?.services || [],
                  isFirstVisit,
                });
                const providerFirst = ((providerName || 'Your therapist').split(' ')[0] || 'Your therapist').trim();
                const providerImg = appointment?.provider?.image || null;
                const trackPayload = {
                  bannerState: banner.state,
                  category: banner.category,
                  recommendedWeeks: recWeeks,
                };
                return <>
                  {/* Composed group — headline, subhead, card, reassurance, buttons,
                      and footer in a single centered column. Parent screen has
                      flex gap: 20, so we sit as ONE flex child and control internal
                      spacing precisely. */}
                  <div className="kiosk-slide-up kiosk-delay-1" style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    width: '100%', maxWidth: 480, padding: '20px 20px 88px',
                    margin: '0 auto', textAlign: 'center',
                  }}>
                    {/* 1. Headline */}
                    <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.25, color: '#374151' }}>
                      We should do this again some time 💕
                    </div>

                    {/* 2. Subhead */}
                    <div style={{ marginTop: 10, fontSize: 15, color: '#6B7280', lineHeight: 1.45, padding: '0 8px' }}>
                      {banner.subhead}
                    </div>

                    {/* 3. CARD — soft pink gradient, subtle pink border */}
                    <div style={{
                      marginTop: 20,
                      width: '100%',
                      padding: '22px 22px 24px',
                      background: 'linear-gradient(165deg, #FFF4F7 0%, #FFE0EA 100%)',
                      border: '1px solid #F8C7D4',
                      borderRadius: 18,
                      boxShadow: '0 4px 14px rgba(247,141,167,0.10)',
                    }}>
                      {/* Provider avatar centered at top */}
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {providerImg ? (
                          <img
                            src={providerImg}
                            alt={providerFirst}
                            style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', border: '3px solid #fff' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex'); }}
                          />
                        ) : null}
                        <div
                          style={{
                            display: providerImg ? 'none' : 'flex',
                            width: 64, height: 64, borderRadius: '50%',
                            background: '#F78DA7', color: '#fff',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: 26, fontWeight: 700, lineHeight: 1,
                            boxShadow: '0 2px 10px rgba(247,141,167,0.30)',
                            border: '3px solid #fff',
                          }}
                        >
                          {(providerFirst.charAt(0) || '?').toUpperCase()}
                        </div>
                      </div>

                      {/* Recommendation lead-in */}
                      <div style={{ marginTop: 14, fontSize: 15.5, color: '#4B5563', lineHeight: 1.35 }}>
                        <strong>{providerFirst}</strong> recommends you come back in
                      </div>

                      {/* {N} weeks — large pink with decorative ticks */}
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
                        <span aria-hidden="true" style={{ width: 22, height: 2, background: '#F8C7D4', borderRadius: 1 }} />
                        <div style={{ fontSize: 40, fontWeight: 800, color: '#F78DA7', lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                          {recWeeks} {recWeeks === 1 ? 'week' : 'weeks'}
                        </div>
                        <span aria-hidden="true" style={{ width: 22, height: 2, background: '#F8C7D4', borderRadius: 1 }} />
                      </div>

                      {/* Discount-eligible only — heart divider + line + badge */}
                      {banner.badge && (
                        <>
                          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span aria-hidden="true" style={{ flex: 1, height: 1, background: '#F8C7D4' }} />
                            <span aria-hidden="true" style={{ fontSize: 13, color: '#F78DA7', lineHeight: 1 }}>♡</span>
                            <span aria-hidden="true" style={{ flex: 1, height: 1, background: '#F8C7D4' }} />
                          </div>
                          <div style={{ marginTop: 12, fontSize: 14.5, color: '#4B5563', lineHeight: 1.4 }}>
                            {banner.discountText}
                          </div>
                          <div style={{ marginTop: 10 }}>
                            <span className="kiosk-discount-badge" style={{ marginTop: 0 }}>
                              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                <path d="M7.5 1.5h-5a1 1 0 0 0-1 1v5a1 1 0 0 0 .293.707l6.5 6.5a1 1 0 0 0 1.414 0l5-5a1 1 0 0 0 0-1.414l-6.5-6.5A1 1 0 0 0 7.5 1.5Zm-3 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
                              </svg>
                              {banner.badge}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* 4. Reassurance block */}
                    <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: '#FFE0EA',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flex: '0 0 36px',
                      }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F78DA7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="3" y="4" width="18" height="18" rx="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 14.5, fontWeight: 700, color: '#374151', lineHeight: 1.3 }}>
                          Plans change — no worries.
                        </div>
                        <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.3 }}>
                          Reschedule or cancel anytime, even same-day.
                        </div>
                      </div>
                    </div>

                    {/* 5. Primary "Save my spot" — every state, button isn't about the deal */}
                    <button
                      className="kiosk-btn-primary"
                      style={{ padding: '18px 28px', width: '100%', marginTop: 22, fontSize: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
                      onClick={() => {
                        kev('rebook', 'rebook_saved', trackPayload);
                        handleWeekSelect(String(recWeeks));
                      }}
                    >
                      Save my spot <span aria-hidden="true" style={{ fontSize: 18 }}>→</span>
                    </button>

                    {/* 6. Skip & pay ghost */}
                    <button
                      className="kiosk-btn-ghost"
                      style={{ marginTop: 8, padding: '10px 24px', fontSize: 14.5 }}
                      onClick={() => {
                        kev('rebook', 'rebook_skipped', trackPayload);
                        setStep('tip');
                      }}
                    >
                      Skip &amp; pay
                    </button>

                    {/* 7. Small footer */}
                    <div style={{ marginTop: 16, fontSize: 11.5, color: '#9CA3AF', letterSpacing: 0.3 }}>
                      Secure checkout · Your info is safe with us
                    </div>
                  </div>

                  {/* 8. Back in bottom-left (matches other steps) */}
                  <button
                    className="kiosk-nav-back"
                    style={{ position: 'absolute', bottom: 20, left: 20, padding: '10px 14px', zIndex: 2 }}
                    onClick={() => {
                      kev('rebook', 'rebook_back', trackPayload);
                      setStep(recommendations ? 'recommendations' : 'invoice');
                    }}
                  >← Back</button>
                </>;
              })()
            )}
          </div>
        </>)}

        {/* REBOOK — YOUR SERVICES (today's visit, pre-checked) */}
        {step === 'rebook_services' && (<>
          <StepDots steps={activeSteps} current="rebook" />
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>What would you like to rebook?</div>
            <div className="kiosk-screen-sub">Uncheck any services you don't need next time</div>
            <div style={{ width: '100%' }} className="kiosk-slide-up kiosk-delay-1">
              {selectedServices.map((svc, i) => {
                const shown = resolveService(svc, selectedDayIso);
                return (
                  <div key={svc.id || i} className={`kiosk-addon-card ${svc.selected ? 'selected' : ''}`}
                    onClick={() => toggleService(svc.id)}>
                    <div className="kiosk-addon-info">
                      <div className="kiosk-addon-name">{shown.name}</div>
                      <div className="kiosk-addon-meta">with {svc.provider} · {shown.duration} min</div>
                    </div>
                    <div className="kiosk-addon-price">{formatPrice(shown.price)}</div>
                    <div className="kiosk-addon-check">{svc.selected ? '✓' : ''}</div>
                  </div>
                );
              })}
            </div>

            {/* Add More Services button */}
            <button className="kiosk-btn-ghost" style={{ width: '100%', marginTop: 8, borderStyle: 'dashed', borderColor: '#F78DA7', color: '#F78DA7' }}
              onClick={() => { loadCategories(); setStep('rebook_catalog'); }}>
              + Add More Services
            </button>

            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={() => { if (isMissedRebook) { onBack(); } else { setRebookWeek(null); setStep('rebook'); } }}>← Back</button>
              <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }}
                disabled={getSelectedServicesList().length === 0}
                onClick={proceedFromServices}>
                {getSelectedServicesList().length === selectedServices.length
                  ? 'Continue — Same Visit'
                  : `Continue with ${getSelectedServicesList().length} Service${getSelectedServicesList().length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </>)}

        {/* REBOOK — FULL SERVICE CATALOG (categories + services) */}
        {step === 'rebook_catalog' && (<>
          <StepDots steps={activeSteps} current="rebook" />
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Add more services</div>
            <div className="kiosk-screen-sub">Browse our full menu</div>

            {/* Category tabs */}
            <div className="kiosk-category-tabs">
              {categories.map(cat => (
                <button key={cat.id}
                  className={`kiosk-category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}>
                  {cat.name}
                </button>
              ))}
            </div>

            {loadingServices ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>Loading...</div>
            ) : (
              <div style={{ width: '100%' }} className="kiosk-slide-up kiosk-delay-1">
                {categoryServices.map(svc => {
                  const isSelected = selectedServices.some(s => s.id === svc.id && s.selected);
                  const isPreSelected = selectedServices.some(s => s.id === svc.id && s.preSelected);
                  return (
                    <div key={svc.id} className={`kiosk-addon-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleService(svc.id)}>
                      <div className="kiosk-addon-info">
                        <div className="kiosk-addon-name">{svc.name}</div>
                        <div className="kiosk-addon-meta">
                          {svc.duration} min
                          {isPreSelected && ' · today\'s service'}
                        </div>
                      </div>
                      <div className="kiosk-addon-price">{formatPrice(svc.price)}</div>
                      <div className="kiosk-addon-check">{isSelected ? '✓' : ''}</div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={() => setStep('rebook_services')}>← Back</button>
              <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }}
                disabled={getSelectedServicesList().length === 0}
                onClick={() => setStep('rebook_services')}>
                Done — {getSelectedServicesList().length} Service{getSelectedServicesList().length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </>)}

        {/* REBOOK — OPTIONAL ADD-ONS (Guided Flow) */}
        {step === 'rebook_addons' && (<>
          <StepDots steps={activeSteps} current="rebook" />
          {loadingAddOns ? (
            <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>Loading add-ons...</div>
            </div>
          ) : (
            <GuidedAddOnsFlow
              selectedServices={getSelectedServicesList()}
              serviceAddOns={serviceAddOns}
              nailServiceIds={nailServiceIds}
              waxServiceIds={waxServiceIds}
              selectedOptionalAddOns={selectedOptionalAddOns}
              onToggleAddOn={toggleOptionalAddOn}
              onComplete={proceedFromOptionalAddOns}
              onBack={() => setStep('rebook_services')}
            />
          )}
        </>)}

        {/* REBOOK — MANDATORY ADD-ONS: REMOVAL QUESTION */}
        {step === 'rebook_mandatory' && mandatoryServices.length > 0 && (<>
          <StepDots steps={activeSteps} current="rebook" />
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Do you need removal?</div>
            <div className="kiosk-screen-sub">For your {mandatoryServices[currentMandatoryIdx]?.name}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, width: '100%', maxWidth: 400 }} className="kiosk-slide-up kiosk-delay-1">
              <button className="kiosk-week-btn"
                onClick={() => handleRemovalAnswer(mandatoryServices[currentMandatoryIdx].id, true)}>
                <div className="kiosk-week-btn-label">Yes</div>
                <div className="kiosk-week-btn-sub">I need removal</div>
              </button>
              <button className="kiosk-week-btn"
                onClick={() => handleRemovalAnswer(mandatoryServices[currentMandatoryIdx].id, false)}>
                <div className="kiosk-week-btn-label">No</div>
                <div className="kiosk-week-btn-sub">No removal needed</div>
              </button>
            </div>
            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={() => setStep('rebook_addons')}>← Back</button>
            </div>
          </div>
        </>)}

        {/* REBOOK — MANDATORY ADD-ON SELECTION */}
        {step === 'rebook_mandatory_select' && mandatoryServices.length > 0 && (<>
          <StepDots steps={activeSteps} current="rebook" />
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Choose removal type</div>
            <div className="kiosk-screen-sub">For your {mandatoryServices[currentMandatoryIdx]?.name}</div>
            <div style={{ width: '100%' }} className="kiosk-slide-up kiosk-delay-1">
              {(serviceAddOns[mandatoryServices[currentMandatoryIdx]?.id]?.mandatory || []).map(addon => {
                const parentId = mandatoryServices[currentMandatoryIdx]?.id;
                const isSelected = selectedMandatoryAddOns.some(a => a.id === addon.id && a.parentServiceId === parentId);
                return (
                  <div key={addon.id} className={`kiosk-addon-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => selectMandatoryAddOn(addon, parentId)}>
                    <div className="kiosk-addon-info">
                      <div className="kiosk-addon-name">{addon.name}</div>
                      {addon.duration > 0 && <div className="kiosk-addon-meta">{addon.duration} min</div>}
                    </div>
                    <div className="kiosk-addon-price">{formatPrice(addon.price)}</div>
                    <div className="kiosk-addon-check">{isSelected ? '✓' : ''}</div>
                  </div>
                );
              })}
            </div>
            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={() => setStep('rebook_mandatory')}>← Back</button>
              <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }}
                disabled={!selectedMandatoryAddOns.some(a => a.parentServiceId === mandatoryServices[currentMandatoryIdx]?.id)}
                onClick={advanceMandatory}>
                Continue
              </button>
            </div>
          </div>
        </>)}

        {/* REBOOK — THERAPIST SELECTION */}
        {step === 'rebook_therapist' && (<>
          <StepDots steps={activeSteps} current="rebook" />
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Choose your therapist</div>
            <div className="kiosk-screen-sub">{isMissedRebook ? 'for your rebook' : `for your visit in ${rebookWeek} week${rebookWeek !== '1' ? 's' : ''}`}</div>

            {loadingTherapists ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>Loading therapists...</div>
            ) : (<>
              {appointment?.provider && (() => {
                const enriched = rebookTherapists.find(t => t.id === appointment.provider.id);
                const img = enriched?.image || appointment.provider.image || null;
                const desc = enriched?.description || appointment.provider.description || '';
                const job = enriched?.jobName || '';
                const price = enriched?.price || null;
                return (
                  <div className={`kiosk-therapist-card-primary kiosk-slide-up kiosk-delay-1 ${rebookSelectedTherapist?.id === appointment.provider.id ? 'selected' : ''}`}
                    onClick={() => selectTherapist({ id: appointment.provider.id, name: appointment.provider.name })}>
                    <div className="kiosk-therapist-avatar">
                      {img
                        ? <img src={img} alt={appointment.provider.name} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                        : null}
                      <span style={img ? { display: 'none' } : {}}>{appointment.provider.name.charAt(0)}</span>
                    </div>
                    <div className="kiosk-therapist-info">
                      <div className="kiosk-therapist-label">REBOOK WITH YOUR THERAPIST</div>
                      <div className="kiosk-therapist-name">{appointment.provider.name}</div>
                      {job && <div className="kiosk-therapist-job">{job}</div>}
                      {desc && <div className="kiosk-therapist-desc">{desc}</div>}
                    </div>
                    {price > 0 && <div className="kiosk-therapist-price">{formatPrice(price)}</div>}
                  </div>
                );
              })()}
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: '#999', marginTop: 8, alignSelf: 'flex-start' }}>
                Or choose another provider
              </div>
              <div className={`kiosk-therapist-card kiosk-slide-up kiosk-delay-2 ${rebookSelectedTherapist?.id === 'any' ? 'selected' : ''}`}
                onClick={() => selectTherapist({ id: 'any', name: 'Any Provider' })}>
                <div className="kiosk-therapist-avatar"><span>?</span></div>
                <div className="kiosk-therapist-info"><div className="kiosk-therapist-name">Any Provider</div><div className="kiosk-therapist-desc">First available · more times</div></div>
              </div>
              {rebookTherapists.filter(t => t.id !== appointment?.provider?.id).map(t => (
                <div key={t.id} className={`kiosk-therapist-card kiosk-slide-up kiosk-delay-2 ${rebookSelectedTherapist?.id === t.id ? 'selected' : ''}`}
                  onClick={() => selectTherapist(t)}>
                  <div className="kiosk-therapist-avatar">
                    {t.image ? <img src={t.image} alt={t.name} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} /> : null}
                    <span style={t.image ? { display: 'none' } : {}}>{t.name.charAt(0)}</span>
                  </div>
                  <div className="kiosk-therapist-info">
                    <div className="kiosk-therapist-name">{t.name}</div>
                    {t.jobName && <div className="kiosk-therapist-job">{t.jobName}</div>}
                    {t.description && <div className="kiosk-therapist-desc">{t.description}</div>}
                  </div>
                  {t.price > 0 && <div className="kiosk-therapist-price">{formatPrice(t.price)}</div>}
                </div>
              ))}
            </>)}
          </div>
          <div className="kiosk-bottom-bar">
            <button className="kiosk-nav-back" onClick={() => {
              if (mandatoryServices.length > 0) setStep('rebook_mandatory');
              else if (Object.values(serviceAddOns).some(a => a.optional?.length > 0)) setStep('rebook_addons');
              else setStep('rebook_services');
            }}>← Back</button>
            <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }}
              disabled={!rebookSelectedTherapist || loadingSlots}
              onClick={proceedFromTherapist}>
              {loadingSlots ? 'Loading times...' : rebookSelectedTherapist ? `Continue with ${rebookSelectedTherapist.name}` : 'Select a therapist'}
            </button>
          </div>
        </>)}

        {/* REBOOK — TIME SELECTION (day-scroller) */}
        {step === 'rebook_time' && !rebookConfirmed && (<>
          <StepDots steps={activeSteps} current="rebook" />
          <div className="kiosk-screen kiosk-rebook-screen kiosk-fade-in">

            {/* Week navigator: ← Week of Mar 9 → */}
            <div className="kiosk-week-nav">
              <button className="kiosk-week-arrow" onClick={() => navigateWeek(-1)} disabled={weekOffset <= (isMissedRebook ? 0 : 1)}>‹</button>
              <div className="kiosk-week-label">{weekLabel}</div>
              <button className="kiosk-week-arrow" onClick={() => navigateWeek(1)} disabled={weekOffset >= 12}>›</button>
            </div>

            {/* Day tabs: Mon 9 · Tue 10 · Wed 11 ... (swipeable) */}
            <div className="kiosk-day-tabs" ref={dayTabsRef}
              onTouchStart={handleSwipeStart} onTouchMove={handleSwipeMove} onTouchEnd={handleSwipeEnd}
              style={{ transform: swipeOffset ? `translateX(${swipeOffset}px)` : undefined, transition: swipeTransition ? 'transform 0.25s ease-out' : 'none' }}>
              {weekDays.map(day => {
                // Calculate how many weeks out this day is from today
                const daysOut = Math.round((day.dateObj - new Date().setHours(0,0,0,0)) / (1000*60*60*24));
                const weeksOut = Math.round(daysOut / 7);
                return (
                  <button key={day.iso}
                    className={`kiosk-day-tab ${selectedDayIso === day.iso ? 'active' : ''}`}
                    onClick={() => selectDay(day.iso)}>
                    <div className="kiosk-day-tab-name">{day.dayName}</div>
                    <div className="kiosk-day-tab-num">{day.dayNum}</div>
                    {day.isCenter && (
                      <div className="kiosk-weeks-out-label">{weeksOut}wk</div>
                    )}
                    {day.isUpkeep && hasUpkeepEligibleServices() && (
                      <div className="kiosk-upkeep-pill">Upkeep</div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Time slots for selected day */}
            {loadingSlots ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>Loading available times...</div>
            ) : selectedDayData?.loaded && selectedDayData.times.length > 0 ? (
              <div className="kiosk-day-times">
                {(() => {
                  const dayLabel = weekDays.find(d => d.iso === selectedDayIso)?.label || '';
                  return selectedDayData.times.map(t => (
                    <button key={t}
                      className={`kiosk-slot-time ${rebookDate === dayLabel && rebookTime === t ? 'active' : ''}`}
                      onClick={() => { setRebookDate(dayLabel); setRebookTime(t); }}>
                      {t}
                    </button>
                  ));
                })()}
              </div>
            ) : selectedDayData?.loaded ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>No available times this day</div>
            ) : null}

            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center', padding: '8px 0' }}>{error}</div>}

            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={() => setStep('rebook_therapist')}>← Back</button>
              {!isMissedRebook && <button className="kiosk-nav-back" onClick={() => { kev('rebook_time', 'rebook_skipped'); setStep('tip'); }}>Skip</button>}
              {rebookTime
                ? <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }} onClick={() => { setError(null); confirmRebook(); }}>Book {rebookDate} at {rebookTime}</button>
                : <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }} disabled>Select a time</button>}
            </div>
          </div>
        </>)}

        {/* REBOOK — CONFIRMED */}
        {step === 'rebook_time' && rebookConfirmed && (<>
          <StepDots steps={activeSteps} current="rebook" />
          <div className="kiosk-screen kiosk-rebook-screen kiosk-fade-in">
            {/* Fireworks for first-visit WAXLOVE */}
            {rebookConfirmed && (
              <div className="kiosk-fireworks">
                {/* One giant burst from center */}
                {[...Array(60)].map((_, i) => {
                  const angle = (i / 60) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
                  const dist = 120 + Math.random() * 300;
                  const isHeart = i % 3 === 0;
                  return (
                    <div key={i} className={`kiosk-firework-particle${isHeart ? ' heart' : ''}`} style={{
                      left: '50%',
                      top: '45%',
                      '--x': `${Math.cos(angle) * dist}px`,
                      '--y': `${Math.sin(angle) * dist}px`,
                      '--color': ['#FFB6C1','#F9A8D4','#DDA0DD','#B5EAD7','#FFDAC1','#FF69B4','#E8A0BF','#C3B1E1'][i % 8],
                      '--delay': `${Math.random() * 0.4}s`,
                      '--size': `${isHeart ? Math.random() * 10 + 8 : Math.random() * 7 + 5}px`,
                      '--spin': `${Math.random() > 0.5 ? 1 : -1}`,
                    }} />
                  );
                })}
              </div>
            )}
            <div className="kiosk-rebook-success">
              <div className="kiosk-rebook-success-icon">{isFirstVisit && rebookDiscountApplied ? '🎉' : '✅'}</div>
              <div className="kiosk-rebook-success-title">{isFirstVisit && rebookDiscountApplied ? 'Welcome to the family!' : rebookDiscountApplied ? 'Upkeep booked!' : 'You\'re booked!'}</div>
              <div className="kiosk-rebook-success-detail">
                {rebookDate} at {rebookTime}
                {rebookSelectedTherapist && rebookSelectedTherapist.id !== 'any' && (' with ' + rebookSelectedTherapist.name)}
              </div>
              {rebookDiscountApplied ? (
                <div className="kiosk-discount-badge">
                  {isFirstVisit
                    ? '25% off applied · WaxLove'
                    : '10% off applied · Upkeep'}
                </div>
              ) : hasUpkeepEligibleServices() ? (
                <div className="kiosk-discount-badge" style={{ background: '#f5f5f5', color: '#999' }}>
                  Booked past {UPKEEP_MAX_WEEKS} weeks — no upkeep discount
                </div>
              ) : null}
            </div>
          </div>
        </>)}

        {/* TIP */}
        {step === 'tip' && (<>
          <StepDots steps={activeSteps} current="tip" />
          <div className="kiosk-screen kiosk-tip-screen kiosk-fade-in">
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>
              Add a tip for {tipProviderHeading}?
            </div>
            {tipBase > 0 && (
              <div className="kiosk-tip-grid">
                {TIPS.map(t => (
                  <button key={t.value} className={`kiosk-tip-btn ${tipSelected === t.value ? 'active' : ''}`}
                    onClick={() => { setTipSelected(t.value); setShowCustomTip(false); setCustomTip(''); }}>
                    <div className="kiosk-tip-percent">{t.label}</div>
                    <div className="kiosk-tip-amount">{formatPrice(tipBase * t.value)}</div>
                  </button>
                ))}
              </div>
            )}
            {/* Custom-amount entry is driven by the in-app KioskKeypad so the OS soft keyboard
                never appears on the touchscreen (which would cover the fixed bottom bar / Continue
                button). The display is a readOnly + inputMode="none" field — belt + suspenders for
                keyboard suppression. customTip stores digit-only cents ("525" → $5.25); the keypad
                appends digits, the display formats them.
                When tipBase === 0 (no recoverable pre-discount price) the percentage grid is hidden
                and the keypad is the only path to a tip — guest enters a dollar amount or taps No Tip. */}
            {(tipBase === 0 || showCustomTip) ? (
              <>
                <div className="kiosk-custom-tip-wrap">
                  <span>$</span>
                  <input
                    className="kiosk-custom-tip-input"
                    type="text"
                    inputMode="none"
                    readOnly
                    placeholder="0.00"
                    value={customTip ? ((parseInt(customTip, 10) || 0) / 100).toFixed(2) : ''}
                  />
                </div>
                <KioskKeypad
                  mode="numeric"
                  value={customTip}
                  onChange={v => { setCustomTip(v); setTipSelected('custom'); }}
                  maxLength={6}
                />
              </>
            ) : (
              <button className="kiosk-btn-ghost" style={{ fontSize: 11, padding: '10px 20px' }} onClick={() => { setShowCustomTip(true); setTipSelected('custom'); }}>Custom Amount</button>
            )}
            {tipProviderNames.length > 1 && (
              <div className="kiosk-giftcard-tip-note" style={{ marginTop: 16, maxWidth: 440 }}>
                Your tip will be split between your providers automatically, based on the price of each service.
              </div>
            )}
          </div>
          {(() => {
            // Single source of truth for the "is there anything to charge" rule.
            // Skip payment iff balance + tip + cart === 0.
            const cartTotal = shopCart.reduce((s, c) => s + (c.price || 0), 0);
            const balanceForPayment = invoiceBalance != null ? invoiceBalance : (subtotal + tax);
            const payableNow = balanceForPayment + getTipAmount() + cartTotal;
            const payableAfterTipReset = balanceForPayment + cartTotal;
            return (
              <div className="kiosk-bottom-bar">
                <button className="kiosk-nav-back" onClick={() => setStep(rebookConfirmed ? 'invoice' : 'rebook')}>← Back</button>
                <button className="kiosk-nav-skip" onClick={() => { setTipSelected(0); kev('tip', 'tip_selected', { tipPercent: 0, tipAmount: 0 }); setStep(payableAfterTipReset === 0 ? 'checkout_complete' : 'payment'); }}>No Tip</button>
                <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 300 }}
                  disabled={tipSelected === null}
                  onClick={() => { kev('tip', 'tip_selected', { tipPercent: typeof tipSelected === 'number' ? Math.round(tipSelected * 100) : 0, tipAmount: getTipAmount(), isCustom: tipSelected === 'custom' }); setStep(payableNow === 0 ? 'checkout_complete' : 'payment'); }}>
                  {payableNow === 0
                    ? 'Continue'
                    : `Continue to Pay — ${formatPrice(payableNow)}`}
                </button>
              </div>
            );
          })()}
        </>)}

        {/* CHECKOUT COMPLETE — zero-balance invoices (package/membership covered it) */}
        {step === 'checkout_complete' && (
          <div className="kiosk-screen kiosk-payment-screen kiosk-fade-in">
            {!paymentComplete ? (<>
              <div className="kiosk-success-icon" style={{ fontSize: 48 }}>✓</div>
              <div className="kiosk-screen-title" style={{ fontSize: 24 }}>You're all covered!</div>
              <div className="kiosk-screen-sub">Your package or membership covers today's visit.</div>
              {getTipAmount() > 0 && (
                <div className="kiosk-screen-sub" style={{ marginTop: 4 }}>Tip: {formatPrice(getTipAmount())}</div>
              )}
              <button className="kiosk-btn-primary" style={{ marginTop: 24, padding: '18px 52px' }}
                disabled={loading}
                onClick={completeZeroBalanceCheckout}>
                {loading ? 'Completing...' : 'Complete Checkout'}
              </button>
              {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center', marginTop: 8 }}>{error}</div>}
              <button className="kiosk-nav-back" style={{ marginTop: 12 }} onClick={() => setStep('tip')}>← Back</button>
            </>) : (
              <div className="kiosk-fade-in" style={{ textAlign: 'center' }}>
                <div className="kiosk-check-circle">✓</div>
                <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Checked out!</div>
              </div>
            )}
          </div>
        )}

        {/* PAYMENT */}
        {step === 'payment' && (<>
          <StepDots steps={activeSteps} current="payment" />
          {/* PICKER — "How would you like to pay?" Lead with the question in plain language
              instead of a buried "Pay at Terminal" button. Guests don't understand "terminal";
              they understand the type of card they're holding. Two visible options + Back. */}
          {!paymentComplete && !paymentProcessing && !paymentError && !giftCardApplied && !manualGcOpen && paymentMethodChoice === null && (
            <div className="kiosk-screen kiosk-payment-screen kiosk-fade-in" style={{ justifyContent: 'flex-start', paddingTop: 40 }}>
              <div className="kiosk-screen-title" style={{ fontSize: 24 }}>How would you like to pay?</div>
              <div className="kiosk-payment-total">{formatPrice((invoiceBalance != null ? invoiceBalance : (subtotal + tax)) + getTipAmount() + shopCart.reduce((s, c) => s + (c.price || 0), 0))}</div>
              {/* Breakdown under the big number — the total jumps once a tip is added, and
                  without this line guests think they're being overcharged. */}
              {(() => {
                const services = invoiceBalance != null ? invoiceBalance : (subtotal + tax);
                const tipAmt = getTipAmount();
                const bridgePkg = shopCart.find(c => c.type === 'bridge-package');
                const products = shopCart.filter(c => c.type !== 'package' && c.type !== 'bridge-package').reduce((s, c) => s + (c.price || 0), 0);
                const packages = shopCart.filter(c => c.type === 'package').reduce((s, c) => s + (c.price || 0), 0);
                if (tipAmt <= 0 && products <= 0 && packages <= 0 && !bridgePkg) return null;
                return (
                  <div style={{ fontSize: 14, color: '#6B7280', marginTop: -6, marginBottom: 8 }}>
                    Services {formatPrice(services)}
                    {bridgePkg && <> · Package — {bridgePkg.name} {formatPrice(bridgePkg.price || 0)}</>}
                    {packages > 0 && <> · Package {formatPrice(packages)}</>}
                    {products > 0 && <> · Products {formatPrice(products)}</>}
                    {tipAmt > 0 && <> · Tip {formatPrice(tipAmt)}</>}
                  </div>
                );
              })()}
              {shopCart.length > 0 && (
                <div style={{ fontSize: 13, color: '#888', marginTop: -8, marginBottom: 12 }}>
                  Includes {shopCart.map(c => c.name).join(', ')}
                </div>
              )}

              {/* Tapping "Debit / Credit" IS the choice — kick off the Helcim terminal
                  transaction directly, no second tap. paymentProcessing flips true inside
                  startPayment(), which hides this picker and shows the "pay at terminal"
                  waiting screen. The intermediate paymentMethodChoice === 'card' screen
                  with its own "Pay at Terminal" button was redundant — guests don't need
                  to confirm the choice they just made. */}
              <button
                className="kiosk-btn-primary"
                style={{ padding: '20px 24px', width: '100%', maxWidth: 380, fontSize: 18, marginTop: 16 }}
                onClick={startPayment}
              >
                💳&nbsp;&nbsp;Debit / Credit
              </button>

              <button
                className="kiosk-btn-primary"
                style={{ padding: '20px 24px', width: '100%', maxWidth: 380, fontSize: 18, marginTop: 14 }}
                onClick={() => setPaymentMethodChoice('giftcard')}
              >
                🎁&nbsp;&nbsp;Gift Card
              </button>

              <button className="kiosk-nav-back" style={{ marginTop: 16 }} onClick={() => setStep('tip')}>← Back</button>
            </div>
          )}

          {/* (paymentMethodChoice === 'card' sub-screen was deleted — "Debit / Credit" on the
              picker now calls startPayment directly, no intermediate confirm step.
              savedCards / payWithCardOnFile UI was here too; left dormant in the codebase
              for now since this PR is scoped to the redundant-tap fix. If saved-cards-on-file
              needs to come back, surface it as a third picker option.) */}

          {/* GIFT CARD — drills down to the existing gift-card flows. Tip note moved here from
              the (former) top-level screen since it only applies on this path. All redemption
              logic (applyGiftCard, manual entry lookup, applied-state handling) unchanged. */}
          {!paymentComplete && !paymentProcessing && !paymentError && !giftCardApplied && !manualGcOpen && paymentMethodChoice === 'giftcard' && (
            <div className="kiosk-screen kiosk-payment-screen kiosk-fade-in" style={{ justifyContent: 'flex-start', paddingTop: 40 }}>
              <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Pay with a gift card</div>
              <div className="kiosk-payment-total">{formatPrice((invoiceBalance != null ? invoiceBalance : (subtotal + tax)) + getTipAmount() + shopCart.reduce((s, c) => s + (c.price || 0), 0))}</div>
              {/* Breakdown — the header total includes tip but the card buttons below only
                  show the services amount, so without this line the math looks wrong. */}
              {(() => {
                const services = invoiceBalance != null ? invoiceBalance : (subtotal + tax);
                const tipAmt = getTipAmount();
                const bridgePkg = shopCart.find(c => c.type === 'bridge-package');
                const products = shopCart.filter(c => c.type !== 'package' && c.type !== 'bridge-package').reduce((s, c) => s + (c.price || 0), 0);
                const packages = shopCart.filter(c => c.type === 'package').reduce((s, c) => s + (c.price || 0), 0);
                if (tipAmt <= 0 && products <= 0 && packages <= 0 && !bridgePkg) return null;
                return (
                  <div style={{ fontSize: 14, color: '#6B7280', marginTop: -6, marginBottom: 8 }}>
                    Services {formatPrice(services)}
                    {bridgePkg && <> · Package — {bridgePkg.name} {formatPrice(bridgePkg.price || 0)}</>}
                    {packages > 0 && <> · Package {formatPrice(packages)}</>}
                    {products > 0 && <> · Products {formatPrice(products)}</>}
                    {tipAmt > 0 && <> · Tip {formatPrice(tipAmt)}</>}
                  </div>
                );
              })()}
              {shopCart.length > 0 && (
                <div style={{ fontSize: 13, color: '#888', marginTop: -8, marginBottom: 12 }}>
                  Includes {shopCart.map(c => c.name).join(', ')}
                </div>
              )}

              {/* Tip note — gift cards cover services only; any tip continues to the
                  terminal in the giftCardApplied + remainingAfterGiftCard screen below. */}
              {getTipAmount() > 0 && (
                <div className="kiosk-giftcard-tip-note">
                  Gift cards cover your services. Your tip ({formatPrice(getTipAmount())}) will be charged separately at the terminal.
                </div>
              )}

              {/* Redeem-side errors (applyGiftCard) — visible whether the card came from
                  the on-file list or manual entry. Sized so a guest staring at the screen
                  can't miss it. */}
              {giftCardError && (
                <>
                  <div style={{
                    color: '#fff',
                    background: '#c44',
                    fontSize: 16,
                    fontWeight: 600,
                    textAlign: 'center',
                    padding: '12px 18px',
                    borderRadius: 10,
                    marginBottom: 12,
                    maxWidth: 420,
                  }}>
                    ⚠️ {giftCardError}
                  </div>
                  {/* Escape hatch — gift-card failures (esp. invoice-side errors) shouldn't
                      strand the guest on this screen with only Back. Let them switch to
                      the terminal directly. */}
                  <button
                    className="kiosk-btn-primary"
                    style={{ padding: '14px 24px', width: '100%', maxWidth: 380, fontSize: 16, marginBottom: 12 }}
                    onClick={() => { setGiftCardError(null); startPayment(); }}
                  >
                    💳&nbsp;&nbsp;Pay by card instead
                  </button>
                </>
              )}

              {guestGiftCards.length > 0 && (
                <>
                  <div className="kiosk-giftcard-section">
                    <span className="kiosk-giftcard-section-icon">🎁</span> Your Gift Cards
                  </div>
                  {guestGiftCards.map(card => (
                    <button key={card.id} className="kiosk-giftcard-btn" onClick={() => applyGiftCard(card)}>
                      <div className="kiosk-giftcard-btn-top">
                        <div>
                          <div className="kiosk-giftcard-btn-number">{card.number}</div>
                          {card.giftedByName ? (
                            <div className="kiosk-giftcard-fromto">
                              <span>From <strong>{card.giftedByName}</strong></span>
                              <span> → To <strong>{guest?.firstName || 'You'}</strong></span>
                            </div>
                          ) : null}
                          {(card.occasion || card.notes) && (
                            <div className="kiosk-giftcard-btn-detail">
                              {card.occasion && <span className="kiosk-giftcard-occasion">{card.occasion}</span>}
                              {card.notes && <span className="kiosk-giftcard-note">"{card.notes}"</span>}
                            </div>
                          )}
                          {card.saleDate && (
                            <div className="kiosk-giftcard-btn-meta">
                              {new Date(card.saleDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              {card.originalValue > 0 && card.originalValue !== card.balance ? ` · Originally ${formatPrice(card.originalValue)}` : ''}
                            </div>
                          )}
                        </div>
                        <div className="kiosk-giftcard-btn-balance">{formatPrice(card.balance)}</div>
                      </div>
                      <div className="kiosk-giftcard-btn-action">PAY {formatPrice(Math.min(card.balance, invoiceBalance || 0))} FOR SERVICES</div>
                      {/* Partial-balance cards leave a remainder; say up front where it goes
                          so guests aren't left wondering what happens to the rest. */}
                      {(() => {
                        const covered = Math.min(card.balance, invoiceBalance || 0);
                        const serviceRemaining = Math.max(0, (invoiceBalance || 0) - covered);
                        const terminalDue = serviceRemaining + getTipAmount();
                        if (terminalDue <= 0) return null;
                        return (
                          <div className="kiosk-giftcard-btn-subaction">
                            {serviceRemaining > 0
                              ? `Then pay the remaining ${formatPrice(terminalDue)} at the terminal`
                              : `Tip (${formatPrice(getTipAmount())}) charged at the terminal`}
                          </div>
                        );
                      })()}
                    </button>
                  ))}
                </>
              )}

              {/* Manual gift card entry — always shown so guests with a physical card not
                  linked to their account have a path. Opens the alpha-keypad panel below. */}
              <button
                className="kiosk-btn-ghost"
                style={{ fontSize: 14, padding: '12px 24px', marginTop: guestGiftCards.length > 0 ? 8 : 0 }}
                onClick={() => { setManualGcOpen(true); setManualGcCode(''); setManualGcError(null); }}
              >
                🎁 Enter a gift card number
              </button>

              <button className="kiosk-nav-back" style={{ marginTop: 16 }} onClick={() => setPaymentMethodChoice(null)}>← Choose a different method</button>
            </div>
          )}
          {/* MANUAL GIFT CARD ENTRY — alpha keypad pattern (same as name screen).
              Replaces the default payment UI while open. On Apply we look up the
              balance, then hand off to applyGiftCard which handles the redeem POST,
              the tip-still-owed branching, and the giftCardApplied state. */}
          {!paymentComplete && !paymentProcessing && !paymentError && !giftCardApplied && manualGcOpen && (
            <div className="kiosk-screen kiosk-payment-screen kiosk-fade-in" style={{ justifyContent: 'flex-start', paddingTop: 24 }}>
              <div className="kiosk-screen-title" style={{ fontSize: 22 }}>Enter your gift card number</div>
              <div className="kiosk-screen-sub" style={{ marginTop: 4 }}>We'll look up the balance and apply it to today's visit.</div>
              <input
                className="kiosk-phone-input"
                type="text"
                inputMode="none"
                readOnly
                placeholder="Card number"
                value={manualGcCode}
                style={{ textTransform: 'uppercase', letterSpacing: 2 }}
              />
              <KioskKeypad mode="alpha" value={manualGcCode} onChange={setManualGcCode} maxLength={40} />
              {manualGcError && (
                <div style={{ color: '#c44', fontSize: 14, textAlign: 'center', marginTop: 12, maxWidth: 380 }}>{manualGcError}</div>
              )}
              {manualGcLookup && (
                <div style={{ fontSize: 14, color: '#6B7280', textAlign: 'center', marginTop: 12 }}>Looking up your gift card…</div>
              )}
              <button
                className="kiosk-btn-primary"
                style={{ marginTop: 16, padding: '16px 40px', width: '100%', maxWidth: 380 }}
                disabled={!manualGcCode.trim() || manualGcLookup}
                onClick={lookupAndApplyManualGiftCard}
              >
                {manualGcLookup ? 'Checking…' : 'Apply Gift Card'}
              </button>
              <button
                className="kiosk-nav-back"
                style={{ marginTop: 10 }}
                onClick={() => { setManualGcOpen(false); setManualGcCode(''); setManualGcError(null); }}
              >
                ← Cancel
              </button>
            </div>
          )}
          {!paymentComplete && !paymentProcessing && !paymentError && giftCardApplied && remainingAfterGiftCard > 0 && (
            <div className="kiosk-screen kiosk-payment-screen kiosk-fade-in">
              <div className="kiosk-giftcard-applied">
                ✓ Gift card applied: {formatPrice(giftCardApplied.amount)} for services
              </div>
              {(() => {
                const serviceRemaining = Math.max(0, (invoiceBalance || 0) - giftCardApplied.amount);
                const tipAmt = getTipAmount();
                return (
                  <div className="kiosk-giftcard-remaining-breakdown">
                    {serviceRemaining > 0 && <div>Services remaining: {formatPrice(serviceRemaining)}</div>}
                    {tipAmt > 0 && <div>Tip: {formatPrice(tipAmt)}</div>}
                    <div className="kiosk-giftcard-remaining-total">
                      Pay at terminal: {formatPrice(remainingAfterGiftCard)}
                    </div>
                  </div>
                );
              })()}
              <button className="kiosk-btn-primary" style={{ marginTop: 20, padding: '18px 52px', width: '100%', maxWidth: 380 }} onClick={startPayment}>
                💳&nbsp;&nbsp;Pay {formatPrice(remainingAfterGiftCard)} at Terminal
              </button>
              <div className="kiosk-sim-note">Tap, insert, or swipe your card</div>
            </div>
          )}
          {paymentProcessing && (
            <div className="kiosk-screen kiosk-payment-screen kiosk-fade-in">
              <div className="kiosk-pulse-ring"><div className="kiosk-terminal-icon">💳</div></div>
              <div className="kiosk-screen-title" style={{ fontSize: 24 }}>
                {/* Always "the terminal" — never name the processor. Guests don't know what
                    Helcim is (or any other brand we might use), and the device label from
                    terminal.name confused them. The card icon + "tap, insert, or swipe"
                    prompt below is the only guidance they need. */}
                {shopPurchasing ? 'Processing your purchases...' : 'Please pay at the terminal'}
              </div>
              <div className="kiosk-payment-total" style={{ marginTop: 8 }}>
                {/* Bridge package sale: the server computed the real charge (package
                    + tip, plus the service balance ONLY on uncovered days) — show ITS
                    total, never the local invoiceBalance+tip estimate, or covered
                    days would display a service amount that isn't being charged. */}
                {shopPurchasing
                  ? formatPrice(shopCart.reduce((s, c) => s + (c.price || 0), 0))
                  : formatPrice(bridgeCharge ? bridgeCharge.totalAmount : (invoiceBalance != null ? invoiceBalance : (subtotal + tax)) + getTipAmount())}
              </div>
              {!shopPurchasing && bridgeCharge?.coveredToday && (
                <div className="kiosk-screen-sub" style={{ marginTop: 2, color: '#c4879a', fontWeight: 600 }}>
                  Today's session: covered by your new package 🎉
                </div>
              )}
              <div className="kiosk-screen-sub" style={{ marginTop: 4, color: '#9CA3AF' }}>
                {shopPurchasing ? 'Please tap your card again for your package purchase' : 'Tap, insert, or swipe your card on the terminal'}
              </div>
              <div className="kiosk-screen-sub" style={{ marginTop: 16, color: '#6B7280', fontSize: 13 }}>This screen will update automatically</div>
            </div>
          )}
          {paymentError && (
            <div className="kiosk-screen kiosk-payment-screen kiosk-fade-in">
              <div style={{ fontSize: 44, marginBottom: 8 }}>⚠️</div>
              <div className="kiosk-screen-title" style={{ fontSize: 22 }}>Payment didn't go through</div>
              <div className="kiosk-screen-sub" style={{ marginTop: 4, color: '#c44', maxWidth: 340 }}>
                {paymentError}
              </div>
              <button className="kiosk-btn-primary" style={{ marginTop: 20, padding: '16px 40px' }}
                onClick={() => { setPaymentError(null); startPayment(); }}>
                Try Again
              </button>
              <button className="kiosk-nav-back" style={{ marginTop: 8 }} onClick={() => { setPaymentError(null); setPaymentProcessing(false); }}>← Back</button>
              <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 16 }}>
                Still not working? Our front desk is happy to help.
              </div>
            </div>
          )}
          {/* (Standalone "Payment complete!" interstitial removed — the redesigned rating
              screen owns the paid-moment header. paymentComplete state is still set so the
              picker / gift-card UI guards keep working; it just no longer renders its own
              screen.) */}
        </>)}


        {/* COMBINED PAID + RATING — replaces both the standalone "Payment complete!"
            interstitial and the separate rating screen. The paid header is the loud cue
            (big pink check + chime); the rating section sits below a divider. Continue
            and Skip both advance to the receipt step. The post-submit "Thank you!" /
            "You made our day!" interstitial was removed — it added a forced 1.2s pause
            that fought the "snappy tail" goal. */}
        {/* FAMILY PROMPT — payer just paid; someone linked to them (shared
            phone/email, detected in OUR db) still has an open invoice today.
            Yes → trimmed flow: straight to gratuity → payment (no rebook). */}
        {step === 'family_prompt' && (<>
          <div className="kiosk-screen kiosk-fade-in" style={{ justifyContent: 'center', gap: 18 }}>
            <div style={{
              width: 96, height: 96, borderRadius: '50%',
              background: '#F78DA7', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 56, fontWeight: 700, lineHeight: 1,
            }}>✓</div>
            <div className="kiosk-screen-title" style={{ fontSize: 26, textAlign: 'center' }}>
              You're all paid!
            </div>
            {(familyCandidatesRef.current || []).slice(0, 1).map(c => (
              <div key={c.appointment.invoiceId} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, marginBottom: 18 }}>
                  <strong>{c.name}</strong> also had a service today
                  {c.serviceName ? <> — {c.serviceName}</> : null} · <strong>{formatPrice(c.balance)}</strong>
                  <div style={{ marginTop: 6 }}>Pay for {c.name.split(' ')[0]} too?</div>
                </div>
                <button className="kiosk-btn-primary" style={{ minWidth: 280 }}
                  onClick={() => startFamilyCheckout(c)}>
                  Yes — pay {formatPrice(c.balance)}
                </button>
                <div style={{ marginTop: 12 }}>
                  <button className="kiosk-btn-ghost" onClick={() => { kev('family_prompt', 'family_checkout_declined'); setStep('rating'); }}>
                    No thanks
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>)}

        {step === 'rating' && (<>
          <StepDots steps={activeSteps} current="rating" />
          <div className="kiosk-screen kiosk-rating-screen kiosk-fade-in" style={{ justifyContent: 'flex-start', paddingTop: 24 }}>
            {/* Paid moment — brand-pink check, bold greeting, amount line */}
            <div style={{
              width: 96, height: 96, borderRadius: '50%',
              background: '#F78DA7', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 56, fontWeight: 700, lineHeight: 1,
              margin: '8px auto 14px',
              boxShadow: '0 8px 24px rgba(247, 141, 167, 0.35)',
            }}>✓</div>
            <div className="kiosk-screen-title" style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
              You're all set{guest?.firstName ? `, ${guest.firstName}` : ''}!
            </div>
            {(() => {
              // Bridge sale: the server's totalAmount is what the card was actually
              // charged (covered days exclude the service balance) — the local
              // balance+tip+cart formula would overstate it.
              const paidTotal = bridgeCharge
                ? bridgeCharge.totalAmount
                : (invoiceBalance != null ? invoiceBalance : (subtotal + tax))
                  + getTipAmount()
                  + shopCart.reduce((s, c) => s + (c.price || 0), 0);
              if (paidTotal <= 0) return null;
              return (
                <div style={{ fontSize: 15, color: '#6B7280', marginTop: 6 }}>
                  Payment received <span style={{ color: '#374151', fontWeight: 600, marginLeft: 8 }}>{formatPrice(paidTotal)}</span>
                </div>
              );
            })()}
            {/* Guest-honest package confirmation — the Zenoti entry is async
                (staff finish it from the closing checklist), so don't claim
                it's on the account already. */}
            {bridgeCharge && (
              <div style={{ fontSize: 14.5, color: '#6B7280', marginTop: 10, maxWidth: 400, lineHeight: 1.45, padding: '0 16px', textAlign: 'center' }}>
                Your package is all set — our team finishes the paperwork and it'll show on your account today.
              </div>
            )}

            {/* Divider between the paid header and the feedback section */}
            <div style={{ width: '70%', maxWidth: 360, height: 1, background: '#E5E7EB', margin: '24px auto 18px' }} />

            {/* Feedback section */}
            <div style={{ fontSize: 18, color: '#374151', textAlign: 'center', maxWidth: 380, lineHeight: 1.4, padding: '0 16px' }}>
              {isTrainingAppointment
                ? 'How was your training today?'
                : `How was your visit${providerName && providerName !== 'your therapist' ? ` with ${providerName}` : ''}?`}
            </div>
            <div className="kiosk-stars" style={{ marginTop: 8 }}>
              {[1,2,3,4,5].map(n => (
                <button key={n} className={`kiosk-star ${rating >= n ? 'active' : ''} ${rating === 5 ? 'five-star' : ''}`}
                  style={{ '--star-delay': `${(n - 1) * 0.08}s` }}
                  onClick={() => setRating(n)}>
                  {rating >= n ? '★' : '☆'}
                </button>
              ))}
            </div>
            {rating === 5 && (
              <div className="kiosk-gold-confetti">
                {[...Array(40)].map((_, i) => (
                  <div key={i} className="kiosk-gold-particle" style={{
                    left: `${Math.random() * 100}%`,
                    '--start-y': `${-20 - Math.random() * 40}px`,
                    '--delay': `${Math.random() * 1}s`,
                    width: `${Math.random() * 8 + 4}px`,
                    height: `${Math.random() * 8 + 4}px`,
                    background: ['#FFD700','#FFC107','#FFB300','#FF8F00','#F9A825','#FFE082','#F78DA7','#DDA0DD'][i % 8],
                  }} />
                ))}
              </div>
            )}

            {/* Comment is always visible alongside the stars. The in-app KioskKeypad
                only appears once the guest taps the note itself — tapping a star
                must NOT pop the keypad. Per the redesign brief. */}
            <textarea
              className="kiosk-comment-box"
              placeholder="Add a note (optional)"
              value={comment}
              readOnly
              rows={2}
              onClick={() => setCommentFocused(true)}
              style={{ marginTop: 16 }}
            />
            {commentFocused && (
              <KioskKeypad mode="alpha" value={comment} onChange={setComment} maxLength={200} />
            )}

            {/* 1-4 star reassurance kept; the 5-star "we'll text you a link" line was
                removed per brief. The Google-review SMS still fires silently from
                submitRating when the guest taps Continue with 5 stars. */}
            {rating > 0 && rating <= 4 && (
              <div className="kiosk-rating-msg kiosk-fade-in">
                We're sorry it wasn't perfect. Our management team will reach out today to make things right.
                <div className="kiosk-rating-msg-small">Only management can see your feedback</div>
              </div>
            )}
          </div>
          <div className="kiosk-bottom-bar">
            <button className="kiosk-nav-skip" onClick={() => { kev('rating', 'rating_skipped'); setStep('receipt'); }}>Skip</button>
            <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 280 }}
              onClick={() => {
                if (rating > 0) {
                  // submitRating fires the rating POST + (if 5-star) the silent Google
                  // review SMS, then advances to step='receipt' itself.
                  submitRating();
                } else {
                  kev('rating', 'rating_skipped');
                  setStep('receipt');
                }
              }}>
              Continue
            </button>
          </div>
        </>)}

        {/* RECEIPT — "Want your receipt?". No more "Receipt sent!" interstitial; the
            confirmation now lives on the done screen as a small "Receipt {texted|emailed}
            to you" line (so the snappy tail isn't padded by another forced pause). */}
        {step === 'receipt' && (
          <div className="kiosk-screen kiosk-receipt-screen kiosk-fade-in">
            <div className="kiosk-receipt-icon">🧾</div>
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Want your receipt?</div>
            <div className="kiosk-screen-sub" style={{ marginTop: 0 }}>
              We'll send it to you right away
            </div>

            <div className="kiosk-receipt-options kiosk-slide-up kiosk-delay-1">
              {guest?.email && (
                <button className="kiosk-receipt-option" onClick={() => sendReceipt('email')}>
                  <div className="kiosk-receipt-option-icon">📧</div>
                  <div className="kiosk-receipt-option-label">Email Me</div>
                  <div className="kiosk-receipt-option-detail">{guest.email}</div>
                </button>
              )}
              {phone && (
                <button className="kiosk-receipt-option" onClick={() => sendReceipt('sms')}>
                  <div className="kiosk-receipt-option-icon">💬</div>
                  <div className="kiosk-receipt-option-label">Text Me</div>
                  <div className="kiosk-receipt-option-detail">{formatPhone(phone)}</div>
                </button>
              )}
              <button className="kiosk-receipt-option" onClick={async () => { kev('receipt', 'receipt_skipped'); await finishCheckout(); kev('done', 'flow_complete'); setStep('done'); }}>
                <div className="kiosk-receipt-option-icon">👋</div>
                <div className="kiosk-receipt-option-label">No Thanks</div>
                <div className="kiosk-receipt-option-detail">Skip receipt</div>
              </button>
            </div>
          </div>
        )}

        {/* DONE — small "Receipt texted/emailed to you" line replaces the removed
            standalone "Receipt sent!" interstitial. Auto-reset behavior is unchanged. */}
        {step === 'done' && (
          <div className="kiosk-screen kiosk-done-screen kiosk-sunset kiosk-fade-in">
            <div className="kiosk-done-check">✓</div>
            <div className="kiosk-done-title">See you soon, <span className="kiosk-done-name">{guest?.firstName || 'friend'}</span>!</div>
            {rebookConfirmed
              ? <div className="kiosk-done-sub">You're all set. See you on <strong>{rebookDate} at {rebookTime}</strong>!</div>
              : <div className="kiosk-done-sub">You're all set. We can't wait to see you again!</div>}
            {receiptMethod && (
              <div style={{ marginTop: 14, fontSize: 15, color: 'rgba(255,255,255,0.9)' }}>
                Receipt {receiptMethod === 'sms' ? 'texted' : 'emailed'} to you ✓
              </div>
            )}
            <div className="kiosk-done-reset">This screen resets automatically</div>
            <button className="kiosk-btn-ghost" style={{ marginTop: 16, background: 'rgba(255,255,255,0.85)' }} onClick={onBack}>Back to Home</button>
          </div>
        )}

        {/* CHANGE-REQUEST HOLD — desk is finishing an update to this visit's services */}
        {step === 'cr_hold' && (
          <div className="kiosk-screen kiosk-fade-in" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: 24 }}>
            <div className="kiosk-page-spinner" />
            <div className="kiosk-screen-title" style={{ maxWidth: 560 }}>One moment — we're just updating your services ✨</div>
            <div className="kiosk-screen-sub">This usually only takes a few seconds</div>
          </div>
        )}

        {/* CHANGE-REQUEST DESK REDIRECT — hold timed out; session ends here.
            The kiosk never proceeds on the old invoice (spec amendment) — the
            front desk keys the change and finishes checkout at the desk. */}
        {step === 'cr_desk' && (
          <div className="kiosk-screen kiosk-done-screen kiosk-sunset kiosk-fade-in">
            <div className="kiosk-done-title">Please see the front desk to finish checkout 💕</div>
            <div className="kiosk-done-sub">We're updating your services — they'll have you on your way in no time.</div>
            <button className="kiosk-btn-primary" style={{ marginTop: 28 }} onClick={onBack}>OK</button>
            <div className="kiosk-done-reset">This screen resets automatically</div>
          </div>
        )}

      </div>
      {showWarning && <IdleWarningModal countdown={countdown} onDismiss={dismissWarning} onReset={onBack} />}

      {/* Shop overlay (products or packages browsing) */}
      {shopMode && (
        <KioskShop
          mode={shopMode}
          products={shopProducts}
          packages={shopPackages}
          // Merge in product lines already on the service invoice so the overlay's isInBag(id)
          // lights up for products added via either carousel or Browse All. Packages stay in shopCart.
          cart={[
            ...shopCart,
            ...((invoiceData?.items || []).filter(it => it.type === 'product').map(it => ({
              id: it.id, name: it.name, price: it.final_price ?? it.price, type: 'product',
            }))),
          ]}
          loading={shopMode === 'products' && shopProductsLoading}
          onCartAdd={addToCart}
          onCartRemove={removeFromCart}
          // Remove a product from the service invoice. Looks up the matching invoice line
          // by product id (or name fallback) and DELETEs it via the existing helper. This
          // handles the case where addProductToVisit put the product on invoiceData.items
          // (instead of shopCart), which removeFromCart wouldn't touch.
          onProductRemove={(productId) => {
            const line = (invoiceData?.items || []).find(it =>
              it.type === 'product' && it.id === productId
            );
            if (line) removeInvoiceItem(line.invoiceItemId || line.id);
          }}
          onClose={() => setShopMode(null)}
        />
      )}
    </div>
  );
}
